import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Hls from "hls.js";
import {
    Play,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    X,
    Search,
    Folder,
    Tag,
    Tags,
    Bookmark,
    Trash2,
    Trash,
    Eye,
    MoreVertical,
    Plus,
    ExternalLink,
    Download,
    Upload,
    Copy,
    Edit,
    Edit2,
    Pen,
    Settings,
    HelpCircle,
    Save,
    LogOut,
    CloudUpload,
    Cloud,
    Menu,
    LayoutGrid,
    Columns2,
    Maximize2,
    Calendar,
    User,
    Filter,
    Clock,
    Archive,
    FolderOpen,
    Share2,
    Info,
    AlertCircle,
    CheckCircle,
    Check,
    RotateCcw,
    Inbox,
    Layers,
    Loader2,
    AlertTriangle,
    Pin,
    Globe,
    List
} from 'lucide-react';
import Dexie from 'dexie';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';

const ICON_MAP = {
    'play': Play,
    'chevron-down': ChevronDown,
    'chevron-right': ChevronRight,
    'chevron-left': ChevronLeft,
    'x': X,
    'search': Search,
    'folder': Folder,
    'tag': Tag,
    'tags': Tags,
    'bookmark': Bookmark,
    'trash-2': Trash2,
    'trash': Trash,
    'eye': Eye,
    'more-vertical': MoreVertical,
    'plus': Plus,
    'external-link': ExternalLink,
    'download': Download,
    'upload': Upload,
    'copy': Copy,
    'edit': Edit,
    'edit-2': Edit2,
    'pen': Pen,
    'settings': Settings,
    'help-circle': HelpCircle,
    'save': Save,
    'log-out': LogOut,
    'cloud-upload': CloudUpload,
    'cloud': Cloud,
    'menu': Menu,
    'grid': LayoutGrid,
    'columns': Columns2,
    'maximize-2': Maximize2,
    'calendar': Calendar,
    'user': User,
    'filter': Filter,
    'clock': Clock,
    'archive': Archive,
    'folder-open': FolderOpen,
    'share-2': Share2,
    'info': Info,
    'alert-circle': AlertCircle,
    'check-circle': CheckCircle,
    'check': Check,
    'rotate-ccw': RotateCcw,
    'inbox': Inbox,
    'layers': Layers,
    'loader': Loader2,
    'alert-triangle': AlertTriangle,
    'pin': Pin,
    'globe': Globe,
    'list': List
};

const LucideIcon = ({ name, className = '', style = {}, size, strokeWidth = 2 }) => {
    const IconComponent = ICON_MAP[name.toLowerCase()] || HelpCircle;
    return (
        <span className={`inline-flex items-center justify-center ${className}`} style={{ ...style, verticalAlign: 'middle', lineHeight: '1' }}>
            <IconComponent size={size || '1em'} strokeWidth={strokeWidth} />
        </span>
    );
};

const TAG_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const getRandomColor = () => TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
const DEFAULT_WEBSITE_VIEW_MODE = 'list';
const WEBSITE_PREVIEW_TIMEOUT_MS = 8000;
const WEBSITE_PREVIEW_API_ENABLED = true;
const websitePreviewResultCache = new Map();
const websitePreviewRequestCache = new Map();
const getWebsiteViewScopeKey = (filters) => {
    if (!Array.isArray(filters) || filters.length === 0) return 'All';
    return [...new Set(filters)].sort((a, b) => a.localeCompare(b)).join('|');
};

const DEFAULT_WEBSITE_FOLDERS = [];
const DEFAULT_WEBSITE_TAGS = [];
const DEFAULT_WEBSITE_BOOKMARKS = [];

const UNSORTED_ALIASES = new Set(['', 'general', 'genel', 'unsorted', 'gelen kutusu']);
const normalizeFolder = (folder) => {
    const raw = folder == null ? '' : String(folder).trim();
    if (!raw) return null;
    return UNSORTED_ALIASES.has(raw.toLowerCase()) ? null : raw;
};
const isUnsortedFolder = (folder) => normalizeFolder(folder) === null;
const getFolderLabel = (folder) => normalizeFolder(folder) || 'Unsorted';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const d = new Date(Number(dateStr) || dateStr);
        if (!isNaN(d.getTime())) {
            return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        }
        const parts = dateStr.split(/[-/.]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[0]}`;
            if (dateStr.includes('/')) return `${parts[1].padStart(2, '0')}.${parts[0].padStart(2, '0')}.${parts[2]}`;
            return `${parts[0].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[2]}`;
        }
        return dateStr;
    } catch { return dateStr; }
};

const sanitizeUrl = (url) => {
    if (!url) return '#';
    const trimmed = url.trim();
    return trimmed.startsWith('http') ? trimmed : 'https://' + trimmed;
};

const getDomainFromUrl = (url) => {
    try {
        return new URL(sanitizeUrl(url)).hostname.replace(/^www\./i, '');
    } catch {
        return '';
    }
};

const getWebsiteTitleFromUrl = (url) => {
    const domain = getDomainFromUrl(url);
    if (!domain) return '';
    const label = domain.split('.')[0] || domain;
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const isGeneratedWebsiteTitle = (title, url, domain) => {
    const normalizedTitle = String(title || '').trim().toLowerCase();
    if (!normalizedTitle) return true;

    const fallbackDomain = String(domain || getDomainFromUrl(url) || '').trim().toLowerCase();
    const fallbackTitle = getWebsiteTitleFromUrl(url).trim().toLowerCase();

    return normalizedTitle === fallbackDomain || normalizedTitle === fallbackTitle;
};

const getWebsiteFaviconUrl = (urlOrDomain) => {
    const domain = (String(urlOrDomain || '').includes('.') && !String(urlOrDomain || '').includes('/'))
        ? String(urlOrDomain || '').trim()
        : getDomainFromUrl(urlOrDomain);
    if (!domain) return '';
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
};

const getWebsiteThumbnailUrl = (url) => {
    const cleanUrl = sanitizeUrl(url);
    if (!cleanUrl || cleanUrl === '#') return '';
    return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(cleanUrl)}?w=1200&h=720`;
};

const isFallbackWebsiteThumbnail = (url) => String(url || '').includes('s.wordpress.com/mshots/v1/');
const WEBSITE_ASSET_PROTOCOLS = new Set(['http:', 'https:']);

const parseIpv4Octets = (address) => {
    const parts = String(address || '').split('.').map((part) => parseInt(part, 10));
    return parts.length === 4 && parts.every((part) => Number.isInteger(part) && part >= 0 && part <= 255) ? parts : null;
};

const isPrivateIpv4Address = (address) => {
    const parts = parseIpv4Octets(address);
    if (!parts) return false;
    const [a, b] = parts;
    if (a === 0 || a === 10 || a === 127) return true;
    if (a === 169 && b === 254) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    if (a === 100 && b >= 64 && b <= 127) return true;
    if (a === 198 && (b === 18 || b === 19)) return true;
    if (a >= 224) return true;
    return false;
};

const isPrivateIpv6Address = (address) => {
    const normalized = String(address || '').toLowerCase();
    if (!normalized) return false;
    if (normalized === '::1' || normalized === '::') return true;
    if (normalized.startsWith('fe80:')) return true;
    if (normalized.startsWith('fc') || normalized.startsWith('fd')) return true;
    if (normalized.startsWith('::ffff:')) return isPrivateIpv4Address(normalized.slice(7));
    return false;
};

const isDisallowedHostname = (hostname) => {
    const normalized = String(hostname || '').trim().toLowerCase().replace(/\.$/, '');
    if (!normalized) return true;
    if (normalized === 'localhost' || normalized.endsWith('.localhost')) return true;
    if (normalized.endsWith('.local') || normalized.endsWith('.localdomain')) return true;
    if (normalized.endsWith('.internal') || normalized.endsWith('.home.arpa')) return true;
    return false;
};

const isSafeWebsiteAssetUrl = (url) => {
    if (!url) return false;
    try {
        const parsed = new URL(String(url).trim(), window.location.origin);
        if (!WEBSITE_ASSET_PROTOCOLS.has(parsed.protocol)) return false;
        if (parsed.username || parsed.password) return false;
        if (parsed.port && !['80', '443'].includes(parsed.port)) return false;
        const hostname = parsed.hostname.replace(/\.$/, '');
        if (isDisallowedHostname(hostname)) return false;
        if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) && isPrivateIpv4Address(hostname)) return false;
        if (hostname.includes(':') && isPrivateIpv6Address(hostname)) return false;
        return true;
    } catch {
        return false;
    }
};

const sanitizeWebsiteAssetUrl = (url) => isSafeWebsiteAssetUrl(url) ? String(url).trim() : '';

const getWebsitePreviewApiUrl = (url) => {
    const cleanUrl = sanitizeUrl(url);
    if (!WEBSITE_PREVIEW_API_ENABLED || !cleanUrl || cleanUrl === '#') return '';
    return `/api/website-preview?url=${encodeURIComponent(cleanUrl)}`;
};

const requestWebsitePreviewFromExtension = (url) => new Promise((resolve) => {
    if (typeof window === 'undefined') {
        resolve({ imageUrl: '', title: '' });
        return;
    }

    const requestId = `website-preview-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    let settled = false;

    const finish = (result) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('bookmarkbase:website-preview', onPreviewReceived);
        window.clearTimeout(timeoutId);
        resolve(result);
    };

    const onPreviewReceived = (event) => {
        const detail = event?.detail || {};
        if (detail.requestId !== requestId) return;
        finish({
            imageUrl: sanitizeWebsiteAssetUrl(detail.imageUrl),
            title: typeof detail.title === 'string' ? detail.title.trim() : ''
        });
    };

    const timeoutId = window.setTimeout(() => finish({ imageUrl: '', title: '' }), WEBSITE_PREVIEW_TIMEOUT_MS);
    window.addEventListener('bookmarkbase:website-preview', onPreviewReceived);
    window.dispatchEvent(new CustomEvent('bookmarkbase:fetch-website-preview', {
        detail: { requestId, url }
    }));
});

const resolveWebsitePreviewData = async (url) => {
    const cleanUrl = sanitizeUrl(url);
    if (!cleanUrl || cleanUrl === '#') return { imageUrl: '', title: '' };
    if (websitePreviewResultCache.has(cleanUrl)) return websitePreviewResultCache.get(cleanUrl);
    if (websitePreviewRequestCache.has(cleanUrl)) return websitePreviewRequestCache.get(cleanUrl);

    const request = (async () => {
        try {
            const extensionId = localStorage.getItem('bookmark_extension_id');
            if (!extensionId) return { imageUrl: '', title: '' };

            const result = await requestWebsitePreviewFromExtension(cleanUrl);
            websitePreviewResultCache.set(cleanUrl, result);
            return result;
        } catch {}
        return { imageUrl: '', title: '' };
    })();

    websitePreviewRequestCache.set(cleanUrl, request);
    return request.finally(() => {
        websitePreviewRequestCache.delete(cleanUrl);
    });
};

const WebsiteFavicon = ({ site, className = '', iconSize = 18 }) => {
    const [hasError, setHasError] = useState(false);
    const faviconUrl = sanitizeWebsiteAssetUrl(site?.faviconUrl) || getWebsiteFaviconUrl(site?.domain || site?.url);

    return (
        <div className={`flex items-center justify-center overflow-hidden ${className}`}>
            {!hasError && faviconUrl ? (
                <img
                    src={faviconUrl}
                    alt={`${site?.domain || site?.title || 'Website'} icon`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setHasError(true)}
                />
            ) : (
                <LucideIcon name="globe" size={iconSize} className="text-slate-400" />
            )}
        </div>
    );
};

const WebsiteListRow = React.memo(({
    site,
    isInTrash,
    isCompact,
    folderColorByName,
    tagColorByName,
    onSelect,
    onStartDrag,
    onEndDrag,
    onFilter,
    onRestore,
    onDelete,
    onMoveToTrash,
    showFolder = true,
    isSelected = false,
    showSelectionControls = false,
    onToggleSelection
}) => {
    const folderName = normalizeFolder(site.folder);
    const visibleTags = (site.tags || []).slice(0, 2);
    const hiddenTagCount = Math.max(0, (site.tags || []).length - visibleTags.length);
    const hasNote = Boolean(site.description?.trim());
    const noteLabel = hasNote && site.description.trim().length <= 30 ? site.description.trim() : 'Note';

    return (
        <div
            onClick={() => { if (!isInTrash) onSelect(site); }}
            draggable={!isInTrash}
            onDragStart={(event) => onStartDrag(event, site.id)}
            onDragEnd={onEndDrag}
            className={`group relative flex min-w-0 items-center rounded-lg border bg-white shadow-sm transition-colors hover:bg-slate-50 ${isSelected ? 'border-blue-300 ring-1 ring-blue-100' : 'border-slate-200/90 hover:border-slate-300'} ${isCompact ? 'gap-2 px-2.5 py-1.5' : 'gap-3 px-3 py-2'} ${isInTrash ? 'opacity-75' : 'cursor-pointer'}`}
        >
            {!isInTrash && (
                <button
                    onClick={(event) => { event.preventDefault(); event.stopPropagation(); onToggleSelection?.(site.id); }}
                    className={`absolute -left-9 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border transition-all ${isSelected ? 'border-blue-500 bg-blue-500 text-white opacity-100 shadow-sm' : 'border-slate-300 bg-white text-transparent'} ${showSelectionControls ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover:opacity-60 hover:opacity-100'}`}
                    aria-label={isSelected ? 'Deselect bookmark' : 'Select bookmark'}
                >
                    <LucideIcon name="check" size={13} />
                </button>
            )}
            <a href={site.url} onClick={(event) => event.preventDefault()} className={`flex min-w-0 basis-1/2 shrink-0 items-center ${isCompact ? 'gap-2' : 'gap-3'}`}>
                <div className={`flex shrink-0 items-center justify-center ${isCompact ? 'h-7 w-7' : 'h-9 w-9'}`}>
                    <WebsiteFavicon site={site} className={isCompact ? 'h-6 w-6' : 'h-8 w-8'} iconSize={isCompact ? 20 : 26} />
                </div>
                <div className={`relative min-w-0 flex-1 ${isCompact ? 'h-8' : 'h-10'}`}>
                    <h3 className={`pointer-events-none absolute left-0 top-1/2 z-10 w-full -translate-y-1/2 truncate font-semibold leading-tight text-slate-900 transition-[top,transform] duration-200 ease-out group-hover:top-0 group-hover:translate-y-0 ${isCompact ? 'text-[13px]' : 'text-sm'}`}>{site.title}</h3>
                    <p className={`pointer-events-none absolute left-0 z-0 w-full translate-y-1 truncate text-slate-400 opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-80 ${isCompact ? 'top-4 text-[10px]' : 'top-5 text-xs'}`}>{site.url}</p>
                </div>
            </a>
            <div className="relative flex min-w-0 flex-1 items-center justify-end">
                {isInTrash ? (
                    <div className="flex items-center gap-1">
                        <button onClick={(event) => onRestore(event, site.id)} className="flex h-8 w-8 items-center justify-center rounded-md text-green-500 transition-all hover:bg-green-50 hover:text-green-600"><LucideIcon name="rotate-ccw" size={15} /></button>
                        <button onClick={(event) => onDelete(event, site.id)} className="flex h-8 w-8 items-center justify-center rounded-md text-red-500 transition-all hover:bg-red-50 hover:text-red-700"><LucideIcon name="trash-2" size={15} /></button>
                    </div>
                ) : (
                    <>
                        <div className="flex min-w-0 items-center gap-1.5">
                            <div className="flex min-w-0 items-center gap-1.5" title={[showFolder ? folderName : '', ...(site.tags || [])].filter(Boolean).join(' · ')}>
                                {hasNote && <span className="max-w-[120px] truncate rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500">{noteLabel}</span>}
                                {showFolder && folderName && <button onClick={(event) => { event.preventDefault(); event.stopPropagation(); onFilter(folderName); }} className="flex max-w-[96px] items-center gap-1 rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200"><LucideIcon name="folder" size={11} style={{ color: folderColorByName[folderName] || '#94a3b8' }} /><span className="truncate">{folderName}</span></button>}
                                {visibleTags.map(tag => <button key={tag} onClick={(event) => { event.preventDefault(); event.stopPropagation(); onFilter(`tag:${tag}`); }} className="flex max-w-[76px] items-center gap-0.5 rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200"><span style={{ color: tagColorByName[tag] || '#94a3b8' }}>#</span><span className="truncate">{tag}</span></button>)}
                                {hiddenTagCount > 0 && <span className="text-[10px] font-medium text-slate-400">+{hiddenTagCount}</span>}
                            </div>
                            <a href={site.url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-slate-900" title="Open website"><LucideIcon name="external-link" size={15} /></a>
                        </div>
                    </>
                )}
            </div>
            {!isInTrash && <button onClick={(event) => onMoveToTrash(event, site.id)} className="absolute -right-10 top-1/2 flex h-8 w-8 -translate-y-1/2 translate-x-1 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-300 opacity-0 shadow-sm transition-all duration-200 hover:border-red-100 hover:bg-red-50 hover:text-red-600 group-hover:translate-x-0 group-hover:opacity-100" title="Move to trash"><LucideIcon name="trash-2" size={15} /></button>}
        </div>
    );
});

const WebsiteThumbnail = ({ site, className = '', onResolved }) => {
    const persistedThumbnail = site?.thumbnailUrl && !isFallbackWebsiteThumbnail(site.thumbnailUrl)
        ? sanitizeWebsiteAssetUrl(site.thumbnailUrl)
        : '';
    const needsTitleResolution = isGeneratedWebsiteTitle(site?.title, site?.url, site?.domain);
    const [hasError, setHasError] = useState(false);
    const [resolvedThumbnail, setResolvedThumbnail] = useState(persistedThumbnail);
    const [isResolving, setIsResolving] = useState(!persistedThumbnail || needsTitleResolution);
    const fallbackThumbnailUrl = '';
    const thumbnailUrl = resolvedThumbnail || fallbackThumbnailUrl;
    const onResolvedRef = useRef(onResolved);

    useEffect(() => {
        onResolvedRef.current = onResolved;
    }, [onResolved]);

    useEffect(() => {
        let isMounted = true;
        setHasError(false);
        setResolvedThumbnail(persistedThumbnail);
        setIsResolving(!persistedThumbnail || needsTitleResolution);

        if (persistedThumbnail && !needsTitleResolution) return undefined;

        resolveWebsitePreviewData(site?.url).then((preview) => {
            if (!isMounted) return;
            if (preview?.imageUrl) {
                setResolvedThumbnail(sanitizeWebsiteAssetUrl(preview.imageUrl));
            }
            if (typeof onResolvedRef.current === 'function') onResolvedRef.current(preview || { imageUrl: '', title: '' });
            setIsResolving(false);
        });

        return () => {
            isMounted = false;
        };
    }, [needsTitleResolution, persistedThumbnail, site?.url]);

    return (
        <div className={`relative overflow-hidden ${className}`}>
            {!hasError && thumbnailUrl ? (
                <img
                    src={thumbnailUrl}
                    alt={site?.title || site?.domain || 'Website preview'}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="lazy"
                    onError={() => setHasError(true)}
                />
            ) : isResolving ? (
                <div className="h-full w-full animate-pulse bg-slate-200" />
            ) : (
                <div className="flex h-full w-full items-end bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.35),_transparent_38%),linear-gradient(135deg,_#0f172a,_#334155_48%,_#f8fafc)] p-5">
                    <div className="max-w-[80%]">
                        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-white/60">{site?.domain || 'Website'}</p>
                        <p className="mt-2 text-lg font-black leading-tight text-white">{site?.title || 'Website preview unavailable'}</p>
                    </div>
                </div>
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-900/10 to-transparent" />
        </div>
    );
};

const extractTweetId = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.includes('reddit.com')) {
        const match = url.match(/comments\/([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
    }
    const match = url.match(/status\/(\d+)/);
    return match && /^\d+$/.test(match[1]) ? match[1] : null;
};

const extractHandle = (url) => {
    if (!url || typeof url !== 'string') return '@user';
    if (url.includes('reddit.com')) {
        const match = url.match(/r\/([a-zA-Z0-9_]+)/);
        return match ? `r/${match[1]}` : 'Reddit User';
    }
    const match = url.match(/(?:x\.com|twitter\.com)\/([a-zA-Z0-9_]+)/);
    return match ? `@${match[1]}` : '@user';
};

const getHighResUrl = (url) => {
    const candidates = getTwitterImageCandidates(url);
    return candidates[0] || '';
};

const TWITTER_MEDIA_HOSTS = new Set(['pbs.twimg.com', 'abs.twimg.com', 'video.twimg.com']);
const TWITTER_MEDIA_EXTENSION_TIMEOUT_MS = 12000;
const twitterMediaRequests = new Map();

const isTwitterMediaUrl = (url) => {
    try {
        const parsedUrl = new URL(String(url || '').replace(/&amp;/gi, '&'));
        return parsedUrl.protocol === 'https:' && TWITTER_MEDIA_HOSTS.has(parsedUrl.hostname.toLowerCase());
    } catch {
        return false;
    }
};

const hasBookmarkExtension = () => {
    try {
        return !!localStorage.getItem('bookmark_extension_id');
    } catch {
        return false;
    }
};

// Spark planında sunucuya ihtiyaç duymadan, görseli host izni bulunan uzantı
// service worker'ından alırız. Sayfa yalnızca güvenli data: URL sonucunu kullanır.
const requestTwitterMediaFromExtension = (url) => {
    const cleanUrl = String(url || '').trim();
    if (!isTwitterMediaUrl(cleanUrl) || !hasBookmarkExtension()) return Promise.resolve('');
    if (twitterMediaRequests.has(cleanUrl)) return twitterMediaRequests.get(cleanUrl);

    const request = new Promise((resolve) => {
        const requestId = `twitter-image-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        let settled = false;

        const finish = (dataUrl = '') => {
            if (settled) return;
            settled = true;
            window.removeEventListener('bookmarkbase:twitter-image', onImageReceived);
            window.clearTimeout(timeoutId);
            resolve(String(dataUrl || ''));
        };

        const onImageReceived = (event) => {
            const detail = event?.detail || {};
            if (detail.requestId !== requestId) return;
            finish(detail.ok ? detail.dataUrl : '');
        };

        const timeoutId = window.setTimeout(() => finish(), TWITTER_MEDIA_EXTENSION_TIMEOUT_MS);
        window.addEventListener('bookmarkbase:twitter-image', onImageReceived);
        window.dispatchEvent(new CustomEvent('bookmarkbase:fetch-twitter-image', {
            detail: { requestId, url: cleanUrl }
        }));
    });

    twitterMediaRequests.set(cleanUrl, request);
    return request.finally(() => twitterMediaRequests.delete(cleanUrl));
};

const getTwitterImageCandidates = (url) => {
    if (!url) return [];
    const isVideoFile = String(url).match(/\.(mp4|webm|ogg|m3u8)|video\.twimg\.com|v\.redd\.it|ext_tw_video|amplify_video/i);
    if (isVideoFile) return [url];
    try {
        const parsedUrl = new URL(String(url).replace(/&amp;/gi, '&'));
        if (parsedUrl.hostname.toLowerCase() !== 'pbs.twimg.com') return [url];

        const originalUrl = parsedUrl.toString();
        if (!parsedUrl.searchParams.has('name') && !parsedUrl.searchParams.has('format')) {
            return [originalUrl];
        }

        // Eski kayıtlardaki bazı medya boyutları artık mevcut olmayabiliyor.
        // X CDN'in desteklediği boyutları sırayla deneyip çalışan sürümde kalıyoruz.
        const directCandidates = [...new Set(['large', 'medium', 'small', 'orig'].map((name) => {
            const candidateUrl = new URL(originalUrl);
            candidateUrl.searchParams.set('name', name);
            return candidateUrl.toString();
        }).concat(originalUrl))];

        return directCandidates;
    } catch {
        return [url];
    }
};

const getMediaDisplayUrl = (url) => {
    return getHighResUrl(url);
};

const TwitterMediaImage = ({ src, onError, ...props }) => {
    const candidates = useMemo(() => getTwitterImageCandidates(src), [src]);
    const [candidateIndex, setCandidateIndex] = useState(0);
    const candidateUrl = candidates[candidateIndex] || '';
    const shouldUseExtension = isTwitterMediaUrl(candidateUrl) && hasBookmarkExtension();
    const [extensionUrl, setExtensionUrl] = useState('');
    const displayUrl = shouldUseExtension ? extensionUrl : candidateUrl;

    useEffect(() => {
        setCandidateIndex(0);
    }, [src]);

    useEffect(() => {
        let isCurrent = true;
        setExtensionUrl('');
        if (!isTwitterMediaUrl(candidateUrl) || !hasBookmarkExtension()) return undefined;

        requestTwitterMediaFromExtension(candidateUrl).then((dataUrl) => {
            if (isCurrent) setExtensionUrl(dataUrl || candidateUrl);
        });

        return () => {
            isCurrent = false;
        };
    }, [candidateUrl]);

    const handleError = (event) => {
        if (candidateIndex + 1 < candidates.length) {
            setCandidateIndex(candidateIndex + 1);
            return;
        }
        onError?.(event);
    };

    if (!displayUrl) {
        return <div className={`animate-pulse bg-slate-200 ${props.className || ''}`} aria-label="Loading media" />;
    }

    return <img {...props} src={displayUrl} onError={handleError} />;
};

const handleDownload = async (url) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'bookmark_base_media';
        a.click();
    } catch (err) { window.open(url, '_blank'); }
};

const HlsVideoPlayer = ({ src, poster, className, controls, autoPlay, muted, ...rest }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        let hls;
        let hasCleanedUp = false;
        let networkRetries = 0;
        let mediaRetries = 0;
        const source = getHighResUrl(src);

        const useHlsJs = source.includes('.m3u8') && Hls.isSupported();
        const useNativeHls = video.canPlayType('application/vnd.apple.mpegurl');

        // Tarayıcı kısıtlamalarını aşmak için sesi DOM seviyesinde kapatıyoruz
        if (autoPlay) {
            video.defaultMuted = true;
            video.muted = true;
        }

        const tryPlay = async () => {
            if (!autoPlay || hasCleanedUp) return;
            try {
                // Async/Await ile oynatmanın başarılı olup olmadığını kesin olarak yakalıyoruz
                await video.play();
            } catch (err) {
                console.warn("Oynatma engellendi, sessiz başlatma deneniyor:", err);
                // Autoplay engeli yediysek, kesinlikle sessize alıp tekrar deniyoruz (Fallback)
                video.muted = true;
                video.play().catch(e => console.error("Sessiz oynatma da başarısız:", e));
            }
        };

        const loadDirect = (url) => {
            if (!url || hasCleanedUp) return;
            video.src = url;
            video.load();

            // Cache Çözümü: Meta veriler zaten yüklendiyse eventi beklemeden oynat
            if (video.readyState >= 1) { // HAVE_METADATA
                tryPlay();
            } else {
                video.addEventListener('loadedmetadata', tryPlay, { once: true });
            }
        };

        if (useHlsJs) {
            hls = new Hls({ enableWorker: true });
            hls.attachMedia(video);

            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(source);
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                tryPlay();
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            if (networkRetries < 2) {
                                networkRetries += 1;
                                hls.startLoad();
                            } else {
                                hls.destroy(); hls = null; loadDirect(source);
                            }
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            if (mediaRetries < 2) {
                                mediaRetries += 1;
                                hls.recoverMediaError();
                            } else {
                                hls.destroy(); hls = null; loadDirect(source);
                            }
                            break;
                        default:
                            hls.destroy(); hls = null; loadDirect(source);
                            break;
                    }
                }
            });
        } else if (useNativeHls && source.includes('.m3u8')) {
            loadDirect(source);
        } else {
            loadDirect(source);
        }

        return () => {
            hasCleanedUp = true;
            if (hls) hls.destroy();
            // Component unmount olduğunda video tagini güvenli bir şekilde temizle
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.removeAttribute('src');
                videoRef.current.load();
            }
        };
    }, [src, autoPlay]);

    return (
        <video
            ref={videoRef}
            className={className}
            controls={controls}
            poster={poster}
            playsInline
            preload="auto"
            // muted={...} React prop kilidini kaldırdık, işlemi yukarıda DOM üzerinden yapıyoruz
            {...rest}
        />
    );
};

const TweetEmbed = ({ tweetId }) => {
    const containerRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(!!window.twttr);

    useEffect(() => {
        if (window.twttr) {
            setIsLoaded(true);
            return;
        }
        const interval = setInterval(() => {
            if (window.twttr) {
                setIsLoaded(true);
                clearInterval(interval);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (isLoaded && window.twttr && tweetId && containerRef.current) {
            containerRef.current.innerHTML = '';
            window.twttr.widgets.createTweet(tweetId, containerRef.current, { theme: 'light', align: 'center', dnt: true });
        }
    }, [isLoaded, tweetId]);

    return <div ref={containerRef} className="w-full min-h-[150px] flex items-center justify-center bg-white rounded-xl overflow-hidden" />;
};

const RedditEmbed = React.memo(({ url }) => {
    let embedUrl = '';
    try {
        const path = new URL(url).pathname;
        embedUrl = `https://www.redditmedia.com${path}?ref_source=embed&ref=share&embed=true`;
    } catch (e) {
        embedUrl = url;
    }
    return (
        <div className="w-full bg-slate-50 flex justify-center rounded-xl overflow-hidden" style={{ minHeight: '300px' }}>
            <iframe
                src={embedUrl}
                sandbox="allow-scripts allow-same-origin allow-popups"
                style={{ border: 'none', width: '100%', height: '400px' }}
                scrolling="yes"
            />
        </div>
    );
});

const renderFormattedText = (text) => {
    if (!text) return '';
    const regex = /(\[[^\]]+\]\(https?:\/\/[^\s\)]+\)|https?:\/\/[^\s]+|#\w+|@\w+)/g;
    const lines = String(text).split('\n');
    return lines.map((line, i, arr) => {
        const parts = line.split(regex);
        const lineContent = parts.map((part, j) => {
            if (!part) return part;
            const mdMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)$/);
            if (mdMatch) {
                return <a key={j} href={mdMatch[2]} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all" onClick={(e) => e.stopPropagation()}>{mdMatch[1]}</a>;
            } else if (/^https?:\/\//.test(part)) {
                return <a key={j} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all" onClick={(e) => e.stopPropagation()}>{part.replace(/^https?:\/\/(www\.)?/, '')}</a>;
            } else if (part.startsWith('#')) {
                return <a key={j} href={`https://x.com/hashtag/${part.substring(1)}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" onClick={(e) => e.stopPropagation()}>{part}</a>;
            } else if (part.startsWith('@')) {
                return <a key={j} href={`https://x.com/${part.substring(1)}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" onClick={(e) => e.stopPropagation()}>{part}</a>;
            }
            return part;
        });
        return <React.Fragment key={i}>{lineContent}{i !== arr.length - 1 && <br />}</React.Fragment>;
    });
};

const isPlayableVideoUrl = (url) => {
    if (!url || typeof url !== 'string' || url.startsWith('blob:')) return false;
    return /(\.mp4|\.webm|\.ogg|\.m3u8|video\.twimg\.com|v\.redd\.it|ext_tw_video|amplify_video)/i.test(url);
};

const getVideoPriority = (url) => {
    const val = String(url || '').toLowerCase();
    if (/\.(mp4|webm|ogg)(\?|$)/i.test(val)) return 3;
    if (/amplify_video|ext_tw_video|video\.twimg\.com|v\.redd\.it/i.test(val)) return 2;
    if (/\.m3u8(\?|$)/i.test(val)) return 1;
    return 0;
};

const getBestVideoIndex = (medias) => {
    if (!Array.isArray(medias) || medias.length === 0) return -1;
    let bestIdx = -1;
    let bestPriority = -1;
    medias.forEach((url, idx) => {
        if (!isPlayableVideoUrl(url)) return;
        const p = getVideoPriority(url);
        if (p > bestPriority) {
            bestPriority = p;
            bestIdx = idx;
        }
    });
    return bestIdx;
};

const CustomTweetCard = React.memo(({ bookmark, onImageClick, showFullRedditContent = true }) => {
    const handle = bookmark.authorHandle || extractHandle(bookmark.url);
    const name = bookmark.authorName || handle;
    const avatar = bookmark.profileImg || (bookmark.url && bookmark.url.includes('reddit.com') ? 'https://www.redditstatic.com/avatars/defaults/v2/avatar_default_1.png' : 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png');

    const medias = bookmark.mediaUrls ? String(bookmark.mediaUrls).split(',').filter(Boolean) : [];
    const videoIdx = getBestVideoIndex(medias);
    const isVideo = bookmark.mediaType === 'video' || videoIdx !== -1;
    const isReddit = bookmark.url && bookmark.url.includes('reddit.com');

    const videoUrl = videoIdx !== -1 ? medias[videoIdx] : (isVideo ? medias[0] : null);
    const posterUrl = bookmark.posterUrl || (medias.find(m => !isPlayableVideoUrl(m)) || medias[0]);
    const tweetId = extractTweetId(bookmark.url);

    const rawText = String(bookmark.tweetText || '');
    const redditTitle = isReddit
        ? (rawText.split(/\n{2,}/)[0] || '').trim()
        : '';
    const displayText = isReddit
        ? (showFullRedditContent ? rawText : redditTitle)
        : rawText;

    return (
        <div className="text-left w-full">
            <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-3 overflow-hidden pr-2">
                    <TwitterMediaImage src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover border border-slate-100 shrink-0" />
                    <div className="flex flex-col overflow-hidden">
                        <span className="font-bold text-slate-900 text-[15px] leading-tight truncate">{name}</span>
                        <span className="text-slate-500 text-xs truncate">{handle}</span>
                    </div>
                </div>
            </div>
            <p className="text-slate-800 text-[17px] leading-relaxed whitespace-pre-wrap mb-3 px-1 break-words overflow-hidden">
                {renderFormattedText(displayText)}
            </p>
            {medias.length > 0 && (
                <div className={`rounded-2xl overflow-hidden border border-slate-100 bg-transparent ${medias.length > 1 && !isVideo ? 'grid grid-cols-2 gap-1 aspect-square md:aspect-video' : ''}`}>
                    {isVideo ? (
                        <a
                            href={getHighResUrl(videoUrl || medias[0])}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative w-full bg-slate-900 flex items-center justify-center aspect-video cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const idx = videoIdx !== -1 ? videoIdx : 0;
                                onImageClick(medias, idx, 'video', posterUrl, tweetId, isReddit);
                            }}
                        >
                            <TwitterMediaImage src={posterUrl} className="w-full h-full object-cover opacity-80" alt="Video Preview" />
                            <div className="absolute w-12 h-12 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center z-10 pointer-events-none border border-white/20">
                                <LucideIcon name="play" className="text-white text-xl ml-1" />
                            </div>
                        </a>
                    ) : (
                        medias.map((url, idx) => {
                            let itemClass = "w-full h-full object-cover cursor-pointer hover:opacity-95 bg-slate-100 transition-all active:scale-[0.98]";
                            let wrapperClass = "relative";
                            if (medias.length === 3 && idx === 0) wrapperClass = "row-span-2 h-full";
                            return (
                                <a
                                    key={idx}
                                    href={getHighResUrl(url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={wrapperClass}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onImageClick(medias, idx, 'image', null, tweetId, isReddit);
                                    }}
                                >
                                    <TwitterMediaImage src={url} alt="Media" className={`${itemClass} pointer-events-none`} />
                                </a>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
});

const CustomDropdown = ({ value, onChange, options, isMulti }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    let tags = [];
    let currentInput = value || '';
    if (isMulti) {
        const parts = (value || '').split(',');
        if (parts.length > 1) {
            tags = parts.slice(0, -1).map(t => t.trim()).filter(Boolean);
            currentInput = parts[parts.length - 1].trimStart();
        } else {
            tags = [];
            currentInput = value || '';
        }
    }

    const handleInputChange = (e) => {
        if (isMulti) {
            const newText = e.target.value;
            const prefix = tags.length > 0 ? tags.join(', ') + ', ' : '';
            onChange(prefix + newText);
        } else {
            onChange(e.target.value);
        }
        setIsOpen(true);
    };

    const handleRemoveTag = (e, tagToRemove) => {
        e.preventDefault();
        e.stopPropagation();
        const newTags = tags.filter(t => t !== tagToRemove);
        const prefix = newTags.length > 0 ? newTags.join(', ') + ', ' : '';
        onChange(prefix + currentInput);
    };

    const handleOptionSelect = (e, optValue) => {
        e.preventDefault();
        e.stopPropagation();
        if (isMulti) {
            const optTarget = optValue.toLowerCase();
            if (tags.includes(optTarget)) {
                const newTags = tags.filter(t => t !== optTarget);
                const prefix = newTags.length > 0 ? newTags.join(', ') + ', ' : '';
                onChange(prefix + currentInput);
            } else {
                const newTags = [...tags, optTarget];
                onChange(newTags.join(', ') + ', ');
            }
        } else {
            onChange(optValue);
            setIsOpen(false);
        }
    };

    const isOptionSelected = (optValue) => {
        if (!value) return false;
        if (!isMulti) return value.toLowerCase().trim() === optValue.toLowerCase().trim();
        return tags.includes(optValue.toLowerCase());
    };

    const searchVal = isMulti
        ? currentInput.trim().toLowerCase()
        : (value || '').trim().toLowerCase();

    // If it's a single select and the exact value is already selected, don't filter out the other options
    const exactMatch = !isMulti ? options.some(opt => opt.name.toLowerCase() === searchVal) : false;
    const filteredOptions = exactMatch ? options : options.filter(opt => opt.name.toLowerCase().includes(searchVal));

    const allOptions = options.map(opt => ({ ...opt }));

    return (
        <div className={`tm-input-wrapper ${isOpen ? 'open' : ''}`} ref={wrapperRef}>
            <div
                className={`w-full p-1.5 bg-slate-50 border border-slate-100 rounded-xl text-sm focus-within:ring-1 focus-within:ring-black transition-all flex items-center min-h-[46px] ${isMulti ? 'flex-wrap gap-1.5' : ''}`}
                onClick={() => setIsOpen(true)}
            >
                {isMulti && tags.map((tag, idx) => {
                    const tagObj = options.find(o => o.name === tag);
                    const bgColor = tagObj ? tagObj.color : '#cbd5e1';
                    return (
                        <span key={`${tag}-${idx}`} className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 shadow-sm rounded-lg text-[13px] font-bold text-slate-700">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: bgColor }}></span>
                            {tag}
                            <button onClick={(e) => handleRemoveTag(e, tag)} className="ml-0.5 text-slate-400 hover:text-red-500 font-bold focus:outline-none">
                                <LucideIcon name="x" className="text-[11px]" />
                            </button>
                        </span>
                    );
                })}
                <input
                    type="text"
                    value={isMulti ? currentInput : value}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    placeholder={isMulti ? (tags.length === 0 ? "Search tags or separate with comma..." : "") : "Search or select folder..."}
                    className="flex-1 min-w-[120px] bg-transparent outline-none py-1 px-2"
                />
            </div>
            <div className="tm-dropdown-arrow mr-2">
                <LucideIcon name="chevron-down" className="text-xs" />
            </div>
            <div className={`tm-dropdown-menu custom-scrollbar ${isOpen ? 'show' : ''}`}>
                {(isOpen ? filteredOptions : allOptions).length > 0 ? (isOpen ? filteredOptions : allOptions).map(opt => {
                    const selected = isOptionSelected(opt.name);
                    return (
                        <div
                            key={opt.id || opt.name}
                            className={`tm-dropdown-item ${selected ? 'selected' : ''}`}
                            onClick={(e) => handleOptionSelect(e, opt.name)}
                        >
                            <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color || '#cbd5e1' }}></span>
                                <span className={isMulti ? "uppercase tracking-wider font-bold" : ""}>
                                    {isMulti ? `#${opt.name}` : opt.name}
                                </span>
                            </span>
                            <div className="tm-dropdown-checkbox"></div>
                        </div>
                    );
                }) : (
                    <div className="tm-dropdown-item" style={{ color: 'var(--text-muted)', justifyContent: 'center' }}>No results found</div>
                )}
            </div>
        </div>
    );
};

/* Dexie Configuration */
const db = new Dexie('TweetmarkDB');
db.version(1).stores({
    bookmarks: 'id',
    folders: 'id',
    tags: 'id',
    trash: 'id'
});
db.version(2).stores({
    bookmarks: 'id',
    folders: 'id',
    tags: 'id',
    trash: 'id',
    websiteBookmarks: 'id',
    websiteFolders: 'id',
    websiteTags: 'id',
    websiteTrash: 'id'
});

/* Firebase Configuration */
const firebaseConfig = {
    apiKey: "AIzaSyDustCH0f1DYc8kkFG3qLMRrIooVp7s8Sw",
    authDomain: "bookmark-base.firebaseapp.com",
    projectId: "bookmark-base",
    storageBucket: "bookmark-base.firebasestorage.app",
    messagingSenderId: "760543001497",
    appId: "1:760543001497:web:e1914fb0ef9b03b52d7108"
};

// Initialize Firebase
if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
}
const auth = firebase.auth();
const fdb = firebase.firestore();


function App() {
    const [activeLibrary, setActiveLibrary] = useState(() => localStorage.getItem('tweetActiveLibrary') || 'posts');
    const [bookmarks, setBookmarks] = useState([]);
    const [customFolders, setCustomFolders] = useState([]);
    const [customTags, setCustomTags] = useState([]);
    const [trash, setTrash] = useState([]);
    const [websiteBookmarks, setWebsiteBookmarks] = useState(DEFAULT_WEBSITE_BOOKMARKS);
    const [websiteFolders, setWebsiteFolders] = useState(DEFAULT_WEBSITE_FOLDERS);
    const [websiteTags, setWebsiteTags] = useState(DEFAULT_WEBSITE_TAGS);
    const [websiteTrash, setWebsiteTrash] = useState([]);
    const [websiteActiveFilters, setWebsiteActiveFilters] = useState(['All']);
    const [websiteSearchQuery, setWebsiteSearchQuery] = useState('');
    const websiteTitleResolutionAttemptedRef = useRef(new Set());
    const [user, setUser] = useState(null);
    const [isDbLoaded, setIsDbLoaded] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [initialFocusedTweet, setInitialFocusedTweet] = useState(null);
    const [initialFocusedWebsite, setInitialFocusedWebsite] = useState(null);
    const [activeAddMenu, setActiveAddMenu] = useState(null);

    const [activeFilters, setActiveFilters] = useState(['All']);

    const toggleFilter = (filter, isMulti = false) => {
        setActiveFilters(prev => {
            if (filter === 'Trash') return ['Trash'];
            if (filter === 'All') return ['All'];
            if (filter === 'AllTags') return prev.includes('AllTags') ? ['All'] : ['AllTags'];
            if (filter === 'AllFolders') return prev.includes('AllFolders') ? ['All'] : ['AllFolders'];

            if (!isMulti) return [filter];

            let next = prev.filter(f => f !== 'Trash' && f !== 'All' && f !== 'AllTags' && f !== 'AllFolders');
            if (next.includes(filter)) {
                next = next.filter(f => f !== filter);
            } else {
                next.push(filter);
            }
            return next.length === 0 ? ['All'] : next;
        });
    };
    const toggleWebsiteFilter = (filter, isMulti = false) => {
        setWebsiteActiveFilters(prev => {
            if (filter === 'All') return ['All'];
            if (filter === 'Trash') return ['Trash'];
            if (filter === 'AllTags') return prev.includes('AllTags') ? ['All'] : ['AllTags'];
            if (filter === 'AllFolders') return prev.includes('AllFolders') ? ['All'] : ['AllFolders'];

            if (!isMulti) return [filter];

            let next = prev.filter(f => f !== 'All' && f !== 'AllTags' && f !== 'AllFolders' && f !== 'Trash');
            if (next.includes(filter)) {
                next = next.filter(f => f !== filter);
            } else {
                next.push(filter);
            }
            return next.length === 0 ? ['All'] : next;
        });
    };

    useEffect(() => {
        let isMounted = true;
        const candidates = [...websiteBookmarks, ...websiteTrash]
            .filter(site => isGeneratedWebsiteTitle(site.title, site.url, site.domain))
            .filter(site => !websiteTitleResolutionAttemptedRef.current.has(site.id))
            .slice(0, 10);

        candidates.forEach((site) => {
            websiteTitleResolutionAttemptedRef.current.add(site.id);
            resolveWebsitePreviewData(site.url).then((preview) => {
                const resolvedTitle = String(preview?.title || '').trim();
                if (!isMounted || !resolvedTitle || resolvedTitle === site.title) return;

                setWebsiteBookmarks((previous) => {
                    let changed = false;
                    const next = previous.map((item) => {
                        if (item.id !== site.id || item.title === resolvedTitle) return item;
                        changed = true;
                        return { ...item, title: resolvedTitle };
                    });
                    return changed ? next : previous;
                });
                setWebsiteTrash((previous) => {
                    let changed = false;
                    const next = previous.map((item) => {
                        if (item.id !== site.id || item.title === resolvedTitle) return item;
                        changed = true;
                        return { ...item, title: resolvedTitle };
                    });
                    return changed ? next : previous;
                });
            });
        });

        return () => { isMounted = false; };
    }, [websiteBookmarks, websiteTrash]);
    const [searchQuery, setSearchQuery] = useState('');
    const [postGridCols, setPostGridCols] = useState(() => parseInt(localStorage.getItem('tweetPostGridCols')) || 3);
    const [websiteGridCols, setWebsiteGridCols] = useState(() => {
        const saved = parseInt(localStorage.getItem('tweetWebsiteGridCols'));
        return Number.isFinite(saved) && saved >= 4 ? saved : 4;
    });
    const [websiteNotesVisible, setWebsiteNotesVisible] = useState(() => localStorage.getItem('tweetWebsiteNotesVisible') !== 'false');
    const [websiteListDensity, setWebsiteListDensity] = useState(() => localStorage.getItem('tweetWebsiteListDensity') === '2x' ? '2x' : '1x');
    const [websiteViewModes, setWebsiteViewModes] = useState(() => {
        const legacyMode = localStorage.getItem('tweetWebsiteViewMode') === 'list' ? 'list' : DEFAULT_WEBSITE_VIEW_MODE;
        try {
            const parsed = JSON.parse(localStorage.getItem('tweetWebsiteViewModes') || '{}');
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return Object.keys(parsed).length > 0 ? parsed : { All: legacyMode };
            }
        } catch {}
        return { All: legacyMode };
    });
    const currentGridCols = activeLibrary === 'websites' ? websiteGridCols : postGridCols;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isGridMenuOpen, setIsGridMenuOpen] = useState(false);
    const [focusedTweet, setFocusedTweet] = useState(null);
    const [focusedWebsite, setFocusedWebsite] = useState(null);
    const [selectedWebsiteIds, setSelectedWebsiteIds] = useState([]);
    const [previewState, setPreviewState] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState([]);
    const [hoverExpandedFolders, setHoverExpandedFolders] = useState([]);
    const folderHoverCloseTimersRef = useRef(new Map());
    const [folderDropTarget, setFolderDropTarget] = useState(null); // { id: string, position: 'before' | 'after' | 'inside' }
    const [storageInfo, setStorageInfo] = useState({ used: 0, quota: 0 });

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setSelectedWebsiteIds([]);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const currentIds = new Set(websiteBookmarks.map((site) => site.id));
        setSelectedWebsiteIds((previous) => previous.filter((id) => currentIds.has(id)));
    }, [websiteBookmarks]);

    useEffect(() => {
        // Calculate the exact payload size to reflect true archive size and cloud capacity
        try {
            const dataToMeasure = {
                bookmarks,
                customFolders,
                customTags,
                trash,
                websiteBookmarks,
                websiteFolders,
                websiteTags,
                websiteTrash
            };
            const jsonString = JSON.stringify(dataToMeasure);
            const byteSize = new Blob([jsonString]).size;

            setStorageInfo({
                used: byteSize,
                quota: 100 * 1024 * 1024 // Fixed 100MB Quota
            });
        } catch (err) {
            console.error("Failed to calculate archive size:", err);
        }
    }, [bookmarks, customFolders, customTags, trash, websiteBookmarks, websiteFolders, websiteTags, websiteTrash]);

    // Handle Auth Changes
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(u => {
            setUser(u);
            // We don't call loadFromFirestore here directly anymore.
            // It is handled by the initial loadDb effect or a dedicated auth+db effect.
        });
        return () => unsubscribe();
    }, []);

    // Track the last UID we loaded to handle user switching correctly
    const lastLoadedUid = useRef(null);
    const isCloudUpdateActive = useRef(false);
    const saveQueueRef = useRef(Promise.resolve());
    const prevSyncStateRef = useRef(null);
    const requestedTwitterMediaRepairsRef = useRef(new Set());
    const pendingTwitterSyncInProgressRef = useRef(false);
    const pendingWebsiteSyncInProgressRef = useRef(false);
    const databaseWriteVersionRef = useRef(0);

    // Effect to trigger loadFromFirestore ONLY when both user and DB are ready for the first time or when user changes
    useEffect(() => {
        if (user && isDbLoaded && lastLoadedUid.current !== user.uid) {
            lastLoadedUid.current = user.uid;
            loadFromFirestore(user.uid);
        } else if (!user) {
            lastLoadedUid.current = null;
        }
    }, [user, isDbLoaded]);

    const handleLogin = async () => {
        const provider = new firebase.auth.GoogleAuthProvider();
        try {
            await auth.signInWithPopup(provider);
            showToast("Logged in successfully!", "success");
        } catch (error) {
            console.error("Login Error:", error);
            showToast("Login failed. Check browser popup settings.", "error");
        }
    };

    const handleLogout = () => {
        auth.signOut();
        showToast("Logged out.", "info");
    };


    const isItemEqual = (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        for (let key of keysA) {
            if (a[key] !== b[key]) {
                if (Array.isArray(a[key]) && Array.isArray(b[key])) {
                    if (a[key].length !== b[key].length) return false;
                    for (let i = 0; i < a[key].length; i++) {
                        if (a[key][i] !== b[key][i]) return false;
                    }
                } else {
                    return false;
                }
            }
        }
        return true;
    };

    const loadFromFirestore = async (uid) => {
        if (!isDbLoaded) return;
        setIsSyncing(true);
        try {
            const metaDoc = await fdb.collection('users').doc(uid).collection('meta').doc('state').get();
            let cloudTime = 0;
            let isMigrated = false;
            let forceMigration = false;
            let oldData = null;

            if (metaDoc.exists) {
                const mData = metaDoc.data();
                cloudTime = typeof mData.lastUpdated === 'number' ? mData.lastUpdated : (mData.lastUpdated?.toMillis?.() || 0);
                isMigrated = mData.schemaVersion >= 2;
            } else {
                // Determine if we need migration from old doc
                const oldDoc = await fdb.collection('users').doc(uid).get();
                if (oldDoc.exists) {
                    oldData = oldDoc.data();
                    cloudTime = typeof oldData.lastUpdated === 'number' ? oldData.lastUpdated : (oldData.lastUpdated?.toMillis?.() || 0);
                    forceMigration = true;
                }
            }

            const localTime = parseInt(localStorage.getItem('tweetLastLocalUpdate')) || 0;

            if (forceMigration || cloudTime > localTime + 2000 || (bookmarks.length === 0 && customFolders.length === 0 && customTags.length === 0 && websiteBookmarks.length === 0 && websiteFolders.length === 0 && websiteTags.length === 0)) {
                isCloudUpdateActive.current = true;

                let data = {
                    bookmarks: [],
                    folders: [],
                    tags: [],
                    trash: [],
                    websiteBookmarks: [],
                    websiteFolders: [],
                    websiteTags: [],
                    websiteTrash: []
                };

                if (forceMigration) {
                    // Execute forced migration inline with deterministic state
                    showToast("Upgrading data sync. Do not close...", "info");

                    data.bookmarks = oldData.bookmarks || [];
                    data.folders = oldData.folders || [];
                    data.tags = oldData.tags || [];
                    data.trash = oldData.trash || [];

                    prevSyncStateRef.current = null;
                    await saveToFirestore(uid, true, data);
                    showToast("Upgraded to new Cloud Sync Architecture.", "success");
                } else if (isMigrated) {
                    const [bSnap, fSnap, tSnap, trSnap, wbSnap, wfSnap, wtSnap, wtrSnap] = await Promise.all([
                        fdb.collection('users').doc(uid).collection('bookmarks').get(),
                        fdb.collection('users').doc(uid).collection('folders').get(),
                        fdb.collection('users').doc(uid).collection('tags').get(),
                        fdb.collection('users').doc(uid).collection('trash').get(),
                        fdb.collection('users').doc(uid).collection('websiteBookmarks').get(),
                        fdb.collection('users').doc(uid).collection('websiteFolders').get(),
                        fdb.collection('users').doc(uid).collection('websiteTags').get(),
                        fdb.collection('users').doc(uid).collection('websiteTrash').get()
                    ]);

                    data.bookmarks = bSnap.empty ? [] : bSnap.docs.map(d => d.data());
                    data.folders = fSnap.empty ? [] : fSnap.docs.map(d => d.data());
                    data.tags = tSnap.empty ? [] : tSnap.docs.map(d => d.data());
                    data.trash = trSnap.empty ? [] : trSnap.docs.map(d => d.data());
                    data.websiteBookmarks = wbSnap.empty ? [] : wbSnap.docs.map(d => d.data());
                    data.websiteFolders = wfSnap.empty ? [] : wfSnap.docs.map(d => d.data());
                    data.websiteTags = wtSnap.empty ? [] : wtSnap.docs.map(d => d.data());
                    data.websiteTrash = wtrSnap.empty ? [] : wtrSnap.docs.map(d => d.data());
                }

                setBookmarks(data.bookmarks);
                setCustomFolders(data.folders);
                setCustomTags(data.tags);
                setTrash(data.trash);
                setWebsiteBookmarks(data.websiteBookmarks);
                setWebsiteFolders(data.websiteFolders);
                setWebsiteTags(data.websiteTags);
                setWebsiteTrash(data.websiteTrash);

                prevSyncStateRef.current = data;
                localStorage.setItem('tweetLastLocalUpdate', cloudTime.toString());

                showToast("Cloud sync complete!", "success");

                // Deterministic guard reset using requestAnimationFrame to ensure React has fully rendered the state
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        isCloudUpdateActive.current = false;
                    });
                });

            } else if (localTime > cloudTime + 2000) {
                // Local is newer or untouched after cloud missing, push everything
                prevSyncStateRef.current = null;
                await saveToFirestore(uid, true);
            } else {
                // Safe and strictly in sync
                prevSyncStateRef.current = {
                    bookmarks,
                    folders: customFolders,
                    tags: customTags,
                    trash,
                    websiteBookmarks,
                    websiteFolders,
                    websiteTags,
                    websiteTrash
                };
            }
        } catch (err) {
            console.error("Cloud Load Error:", err);
            showToast("Cloud load failed. Please check your connection.", "error");
        } finally {
            setIsSyncing(false);
        }
    };

    const saveToFirestore = async (uid, force = false, explicitData = null) => {
        if (!uid || !isDbLoaded) return;

        const currentBookmarks = explicitData ? explicitData.bookmarks : bookmarks;
        const currentFolders = explicitData ? explicitData.folders : customFolders;
        const currentTags = explicitData ? explicitData.tags : customTags;
        const currentTrash = explicitData ? explicitData.trash : trash;
        const currentWebsiteBookmarks = explicitData ? explicitData.websiteBookmarks : websiteBookmarks;
        const currentWebsiteFolders = explicitData ? explicitData.websiteFolders : websiteFolders;
        const currentWebsiteTags = explicitData ? explicitData.websiteTags : websiteTags;
        const currentWebsiteTrash = explicitData ? explicitData.websiteTrash : websiteTrash;

        const prevState = prevSyncStateRef.current;
        const hasAnyData =
            currentBookmarks.length > 0 || currentFolders.length > 0 || currentTags.length > 0 || currentTrash.length > 0 ||
            currentWebsiteBookmarks.length > 0 || currentWebsiteFolders.length > 0 || currentWebsiteTags.length > 0 || currentWebsiteTrash.length > 0;
        const hadAnyData = prevState && (
            prevState.bookmarks?.length > 0 || prevState.folders?.length > 0 || prevState.tags?.length > 0 || prevState.trash?.length > 0 ||
            prevState.websiteBookmarks?.length > 0 || prevState.websiteFolders?.length > 0 || prevState.websiteTags?.length > 0 || prevState.websiteTrash?.length > 0
        );

        if (!force && !hasAnyData && !hadAnyData) return;

	        const saveTask = async () => {
	            try {
	                const prevState = prevSyncStateRef.current;
	                let hasCloudChanges = false;

	                let batch = fdb.batch();
                let opsCount = 0;
                const commitBatch = async () => {
                    if (opsCount > 0) {
                        try {
                            await batch.commit();
                        } catch (commitErr) {
                            console.error('Batch commit failed:', commitErr);
                            throw commitErr; // Critical fix: throw on partial batch fail
                        }
                        batch = fdb.batch();
                        opsCount = 0;
                    }
                };

                const applyDiff = async (collectionName, currentItems, prevItems) => {
                    // Helper to remove undefined values which Firebase doesn't allow
                    const cleanItem = (obj) => {
                        try {
                            return JSON.parse(JSON.stringify(obj));
                        } catch (e) {
                            console.error("CleanItem error:", e);
                            return obj;
                        }
                    };

                    if (!prevItems) {
                        for (const item of currentItems) {
                            const itemId = item.id || item.name;
                            if (itemId == null) continue;
	                            const id = String(itemId);
	                            const ref = fdb.collection('users').doc(uid).collection(collectionName).doc(id);
	                            batch.set(ref, cleanItem(item));
	                            hasCloudChanges = true;
	                            opsCount++;
	                            if (opsCount >= 490) await commitBatch();
                        }
                        return;
                    }

                    const prevMap = new Map((prevItems || []).map(i => [String(i.id || i.name), i]));
                    const currMap = new Map((currentItems || []).map(i => [String(i.id || i.name), i]));

                    for (const [id, item] of currMap.entries()) {
                        if (id === "undefined" || id === "null") continue;
                        const prevItem = prevMap.get(id);
	                        if (!prevItem || !isItemEqual(prevItem, item)) {
	                            const ref = fdb.collection('users').doc(uid).collection(collectionName).doc(id);
	                            batch.set(ref, cleanItem(item));
	                            hasCloudChanges = true;
	                            opsCount++;
	                            if (opsCount >= 490) await commitBatch();
                        }
                    }

                    for (const id of prevMap.keys()) {
	                        if (!currMap.has(id)) {
	                            const ref = fdb.collection('users').doc(uid).collection(collectionName).doc(id);
	                            batch.delete(ref);
	                            hasCloudChanges = true;
	                            opsCount++;
	                            if (opsCount >= 490) await commitBatch();
                        }
                    }
                };

                await applyDiff('bookmarks', currentBookmarks, prevState?.bookmarks);
                await applyDiff('folders', currentFolders, prevState?.folders);
                await applyDiff('tags', currentTags, prevState?.tags);
                await applyDiff('trash', currentTrash, prevState?.trash);
                await applyDiff('websiteBookmarks', currentWebsiteBookmarks, prevState?.websiteBookmarks);
                await applyDiff('websiteFolders', currentWebsiteFolders, prevState?.websiteFolders);
                await applyDiff('websiteTags', currentWebsiteTags, prevState?.websiteTags);
                await applyDiff('websiteTrash', currentWebsiteTrash, prevState?.websiteTrash);

                await commitBatch();

	                if (hasCloudChanges || force || !prevState) {
	                    await fdb.collection('users').doc(uid).collection('meta').doc('state').set({
	                        lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
	                        schemaVersion: 3
	                    });
	                }

                // Clear old root document fields selectively to clean up payload without breaking collections or rules
                if (force && !prevState) {
                    try {
                        const rootDoc = await fdb.collection('users').doc(uid).get();
                        if (rootDoc.exists && rootDoc.data().bookmarks) {
                            await fdb.collection('users').doc(uid).update({
                                bookmarks: firebase.firestore.FieldValue.delete(),
                                folders: firebase.firestore.FieldValue.delete(),
                                tags: firebase.firestore.FieldValue.delete(),
                                trash: firebase.firestore.FieldValue.delete()
                            });
                        }
                    } catch (e) { console.warn("Failed to clean up old root doc fields.", e); }
                }

                localStorage.setItem('tweetLastLocalUpdate', Date.now().toString());
                prevSyncStateRef.current = {
                    bookmarks: currentBookmarks,
                    folders: currentFolders,
                    tags: currentTags,
                    trash: currentTrash,
                    websiteBookmarks: currentWebsiteBookmarks,
                    websiteFolders: currentWebsiteFolders,
                    websiteTags: currentWebsiteTags,
                    websiteTrash: currentWebsiteTrash
                };
            } catch (err) {
                console.error('Cloud Sync Error:', err);
                if (force) showToast('Failed to save to cloud. Will retry later.', 'error');
                throw err;
            }
        };

        const executeTask = saveQueueRef.current.then(saveTask);
        saveQueueRef.current = executeTask.catch(e => {
            console.error("Queue boundary recovered from error", e);
        });

        return executeTask;
    };

    const [dragOverFolderId, setDragOverFolderId] = useState(null);
    const [dragOverTagId, setDragOverTagId] = useState(null);
    const [isWebsiteDropActive, setIsWebsiteDropActive] = useState(false);
    const dragItemRef = useRef(null);
    const folderDropRef = useRef(null); // Sürükleme konumu için logic ref

    const clearDragState = React.useCallback(() => {
        setDragOverFolderId(null);
        setDragOverTagId(null);
        setFolderDropTarget(null);
        folderDropRef.current = null;
        dragItemRef.current = null;
    }, []);

    const handleTagDrop = (e, tagName) => {
        e.preventDefault();
        e.stopPropagation();
        const dragData = dragItemRef.current;
        if (!dragData || dragData.type !== 'tweet') {
            clearDragState();
            return;
        }

        setBookmarks(prev => prev.map(b => {
            if (!dragData.ids.includes(b.id)) return b;
            const currentTags = Array.isArray(b.tags) ? b.tags : [];
            if (currentTags.includes(tagName)) return b;
            return { ...b, tags: [...currentTags, tagName] };
        }));
        clearDragState();
    };

    const handleWebsiteTagDrop = (e, tagName) => {
        e.preventDefault();
        e.stopPropagation();
        const dragData = dragItemRef.current;
        if (!dragData || dragData.type !== 'website') {
            clearDragState();
            return;
        }

        setWebsiteBookmarks((previous) => previous.map((site) => {
            if (!dragData.ids.includes(site.id) || (site.tags || []).includes(tagName)) return site;
            return { ...site, tags: [...(site.tags || []), tagName] };
        }));
        clearDragState();
        setSelectedWebsiteIds([]);
    };

    // Debounce Search 
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    // Infinite Scroll
    const [visibleCount, setVisibleCount] = useState(20);
    const observerTarget = useRef(null);

    useEffect(() => {
        const handleGlobalDragEnd = () => clearDragState();
        window.addEventListener('dragend', handleGlobalDragEnd);
        return () => window.removeEventListener('dragend', handleGlobalDragEnd);
    }, [clearDragState]);

    // Callbacks
    const handleImageClick = React.useCallback((medias, idx, type, poster, tweetId, isReddit = false) => {
        if (type === 'video') {
            const playableMedias = (medias || []).filter(isPlayableVideoUrl);
            const nextMedias = playableMedias.length > 0 ? playableMedias : (medias || []);
            const selected = (medias || [])[idx];
            const normalizedIdx = Math.max(0, nextMedias.indexOf(selected));
            setPreviewState({ medias: nextMedias, currentIndex: normalizedIdx, mediaType: type, poster, tweetId, isReddit });
            return;
        }
        setPreviewState({ medias, currentIndex: idx, mediaType: type, poster, tweetId, isReddit });
    }, []);

    const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null);
    const [folderNameInput, setFolderNameInput] = useState('');
    const [folderColorInput, setFolderColorInput] = useState('#3b82f6');

    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [tagNameInput, setTagNameInput] = useState('');
    const [tagColorInput, setTagColorInput] = useState('#64748b');
    const [isTagsExpanded, setIsTagsExpanded] = useState(true);

    const [isNoteEditing, setIsNoteEditing] = useState(false);
    const [isWebsiteNoteEditing, setIsWebsiteNoteEditing] = useState(false);

    const [newUrl, setNewUrl] = useState('');
    const [newFolder, setNewFolder] = useState('');
    const [newTags, setNewTags] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newWebsiteUrl, setNewWebsiteUrl] = useState('');
    const [newWebsiteTitle, setNewWebsiteTitle] = useState('');
    const [newWebsiteFolder, setNewWebsiteFolder] = useState('');
    const [newWebsiteTags, setNewWebsiteTags] = useState('');
    const [newWebsiteDesc, setNewWebsiteDesc] = useState('');

    const [isWipeModalOpen, setIsWipeModalOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isEditingColors, setIsEditingColors] = useState(false);
    const [colorEditingIndex, setColorEditingIndex] = useState(null);
    const [accentColor, setAccentColor] = useState(() => localStorage.getItem('tweetAccentColor') || '#000000');

    const [customAccentColors, setCustomAccentColors] = useState(() => {
        const saved = localStorage.getItem('customAccentColors');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0].id && parsed[0].color) return parsed;
            } catch (e) { }
        }
        return [
            { id: 1, color: '#000000' },
            { id: 2, color: '#3b82f6' },
            { id: 3, color: '#10b981' },
            { id: 4, color: '#f59e0b' },
            { id: 5, color: '#ef4444' },
            { id: 6, color: '#8b5cf6' },
            { id: 7, color: '#ec4899' },
            { id: 8, color: '#64748b' }
        ];
    });
    const [theme, setTheme] = useState(() => localStorage.getItem('tweetTheme') || 'light');

    useEffect(() => {
        localStorage.setItem('customAccentColors', JSON.stringify(customAccentColors));
    }, [customAccentColors]);
    const [autoBackup, setAutoBackup] = useState(() => localStorage.getItem('tweetAutoBackup') === 'true');
    const [lastBackup, setLastBackup] = useState(() => parseInt(localStorage.getItem('tweetLastBackup')) || 0);
    const [showBrandLines, setShowBrandLines] = useState(() => localStorage.getItem('tweetShowBrandLines') !== 'false');
    const [brandLineStyle, setBrandLineStyle] = useState(() => localStorage.getItem('tweetBrandLineStyle') || 'bar');
    const usePillTags = true;

    useEffect(() => {
        localStorage.setItem('tweetShowBrandLines', showBrandLines);
    }, [showBrandLines]);

    useEffect(() => {
        localStorage.setItem('tweetBrandLineStyle', brandLineStyle);
    }, [brandLineStyle]);

    useEffect(() => {
        localStorage.setItem('tweetTheme', theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem('tweetAutoBackup', autoBackup);
    }, [autoBackup]);

    // Auto backup logic
    useEffect(() => {
        if (!autoBackup || !isDbLoaded) return;

        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;

        if (now - lastBackup > oneDay) {
            // Delay auto-backup slightly after load to not interfere with startup
            const timer = setTimeout(() => {
                handleExportJSON(true); // true means silent/auto mode
                const timestamp = Date.now();
                setLastBackup(timestamp);
                localStorage.setItem('tweetLastBackup', timestamp);
                showToast('Automatic backup completed', 'success');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [autoBackup, isDbLoaded, bookmarks.length]);

    // Toast notifications
    const [toasts, setToasts] = useState([]);
    const toastIdRef = useRef(0);
    const showToast = useCallback((message, type = 'info', undoAction = null, duration = 4000) => {
        const id = ++toastIdRef.current;
        setToasts(prev => [...prev, { id, message, type, undoAction }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
        return id;
    }, []);
    const dismissToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

    // --- EFFECTS ---
    useEffect(() => {
        const loadDb = async () => {
            try {
                const bCount = await db.bookmarks.count();
                const fCount = await db.folders.count();
                const tCount = await db.tags.count();
                const wbCount = await db.websiteBookmarks.count();
                const wfCount = await db.websiteFolders.count();
                const wtCount = await db.websiteTags.count();
                const wtrCount = await db.websiteTrash.count();

                if (bCount === 0 && fCount === 0 && tCount === 0 && wbCount === 0 && wfCount === 0 && wtCount === 0 && wtrCount === 0) {
                    // Migrate from localStorage
                    const safeParse = (key) => { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : []; } catch (e) { return []; } };
                    const oldB = safeParse('tweetBookmarks_v1');
                    const oldF = safeParse('tweetFolders_v2');
                    const oldT = safeParse('tweetTags_v1');
                    const oldTr = safeParse('tweetTrash_v1');
                    const oldWB = safeParse('websiteBookmarks_v1');
                    const oldWF = safeParse('websiteFolders_v1');
                    const oldWT = safeParse('websiteTags_v1');
                    const oldWTr = safeParse('websiteTrash_v1');

                    setBookmarks(oldB);
                    setCustomFolders(oldF);
                    setCustomTags(oldT);
                    setTrash(oldTr);
                    setWebsiteBookmarks(oldWB);
                    setWebsiteFolders(oldWF);
                    setWebsiteTags(oldWT);
                    setWebsiteTrash(oldWTr);
                } else {
                    const b = await db.bookmarks.toArray();
                    const f = await db.folders.toArray();
                    const t = await db.tags.toArray();
                    const tr = await db.trash.toArray();
                    const wb = await db.websiteBookmarks.toArray();
                    const wf = await db.websiteFolders.toArray();
                    const wt = await db.websiteTags.toArray();
                    const wtr = await db.websiteTrash.toArray();
                    setBookmarks(b);
                    setCustomFolders(f);
                    setCustomTags(t);
                    setTrash(tr);
                    setWebsiteBookmarks(wb);
                    setWebsiteFolders(wf);
                    setWebsiteTags(wt);
                    setWebsiteTrash(wtr);
                }
            } catch (err) {
                console.error("Dexie Load Error", err);
            } finally {
                setIsDbLoaded(true);
            }
        };
        loadDb();
    }, []);

    useEffect(() => {
        const handleExternalMessage = (request, sender, sendResponse) => {
            const dynamicExtId = localStorage.getItem('bookmark_extension_id');
            const ALLOWED_EXTENSION_IDS = dynamicExtId ? [dynamicExtId] : [];
            if (!sender.id || !ALLOWED_EXTENSION_IDS.includes(sender.id)) {
                console.warn('Unauthorized external message from:', sender.id);
                return;
            }
            if (request.type === 'GET_APP_DATA') {
                sendResponse({
                    folders: customFolders,
                    tags: customTags
                });
            }
        };

        if (window.chrome && chrome.runtime && chrome.runtime.onMessageExternal) {
            chrome.runtime.onMessageExternal.addListener(handleExternalMessage);
        }

        return () => {
            if (window.chrome && chrome.runtime && chrome.runtime.onMessageExternal) {
                chrome.runtime.onMessageExternal.removeListener(handleExternalMessage);
            }
        };
    }, [customFolders, customTags]);

    // --- EXTENSION SYNC EFFECT ---
    useEffect(() => {
        const checkPendingSync = async () => {
            if (!isDbLoaded || pendingTwitterSyncInProgressRef.current) return;
            pendingTwitterSyncInProgressRef.current = true;
            try {
                // YENİ KAYITLAR İÇİN
                const pendingStr = localStorage.getItem('pending_twitter_sync');
                if (pendingStr) {
                    const pendingBookmarks = JSON.parse(pendingStr);
                    if (pendingBookmarks && pendingBookmarks.length > 0) {
                        const persistedBookmarks = await db.bookmarks.toArray();
                        const persistedTweetIds = new Set(persistedBookmarks.map((bookmark) => String(bookmark.tweetId || '')));
                        const seenPendingTweetIds = new Set();
                        const uniquePending = pendingBookmarks
                            .filter((bookmark) => {
                                const tweetId = String(bookmark.tweetId || '');
                                if (!tweetId || persistedTweetIds.has(tweetId) || seenPendingTweetIds.has(tweetId)) return false;
                                seenPendingTweetIds.add(tweetId);
                                return true;
                            })
                            .map((bookmark) => ({
                                ...bookmark,
                                folder: normalizeFolder(bookmark.folder),
                                timestamp: bookmark.timestamp || Date.now()
                            }));

                        // Eklenti kuyruğunu ancak IndexedDB'ye başarılı şekilde yazdıktan sonra
                        // temizliyoruz. Böylece sayfa aniden yenilense bile Tweet kaybolmaz.
                        if (uniquePending.length > 0) {
                            await db.bookmarks.bulkPut(uniquePending);
                            localStorage.setItem('tweetLastLocalUpdate', Date.now().toString());
                        }

                        setBookmarks(prev => {
                            const existingTweetIds = new Set(prev.map(b => b.tweetId));
                            const pendingForState = uniquePending.filter((bookmark) => !existingTweetIds.has(String(bookmark.tweetId)));
                            return pendingForState.length > 0 ? [...pendingForState, ...prev] : prev;
                        });
                        localStorage.removeItem('pending_twitter_sync');
                    }
                }

                // GÜNCELLEMELER İÇİN (Eklenti popup'ından gelen klasör/not/tag vb.)
                const pendingUpdatesStr = localStorage.getItem('pending_twitter_updates');
                if (pendingUpdatesStr) {
                    const updates = JSON.parse(pendingUpdatesStr);
                    if (updates && updates.length > 0) {
                        setBookmarks(prev => {
                            let newPrev = [...prev];
                            updates.forEach(upd => {
                                const idx = newPrev.findIndex(b => b.tweetId === String(upd.tweetId));
                                if (idx !== -1) {
                                    newPrev[idx] = { ...newPrev[idx], folder: normalizeFolder(upd.folder), tags: upd.tags, description: upd.note };
                                }
                            });
                            return newPrev;
                        });

                        const tagsFromUpdates = updates.flatMap(u => (u.tags || []));
                        if (tagsFromUpdates.length > 0) {
                            setCustomTags(prevTags => {
                                const newTagsList = [...prevTags];
                                let tagsChanged = false;
                                tagsFromUpdates.forEach(tag => {
                                    if (tag && !newTagsList.some(t => t.name === tag)) {
                                        newTagsList.push({ id: 't_' + Math.random().toString(36).substr(2, 9), name: tag, color: getRandomColor() });
                                        tagsChanged = true;
                                    }
                                });
                                return tagsChanged ? newTagsList : prevTags;
                            });
                        }
                        localStorage.removeItem('pending_twitter_updates');
                    }
                }
            } catch (err) { console.error("Sync error:", err); }
            finally { pendingTwitterSyncInProgressRef.current = false; }
        };

        checkPendingSync();
        window.addEventListener('twitter-bookmarks-synced', checkPendingSync);
        return () => window.removeEventListener('twitter-bookmarks-synced', checkPendingSync);
    }, [isDbLoaded]);

    // X zaman zaman sayfa içinde geçici blob: görsel URL'leri kullanıyor. Bu URL'ler
    // uygulamaya taşındığında geçersiz olur. Eklenti, resmi syndication verisinden
    // kalıcı CDN URL'lerini getirir ve eski kayıtları da otomatik olarak onarır.
    useEffect(() => {
        const handleTwitterMediaResolved = (event) => {
            const results = Array.isArray(event?.detail?.results) ? event.detail.results : [];
            if (!event?.detail?.ok || results.length === 0) return;

            const byTweetId = new Map(results
                .filter((result) => result?.tweetId)
                .map((result) => [String(result.tweetId), result]));

            setBookmarks((previousBookmarks) => {
                let changed = false;
                const resolvedAt = Date.now();
                const nextBookmarks = previousBookmarks.map((bookmark) => {
                    const result = byTweetId.get(String(bookmark.tweetId || ''));
                    if (!result) return bookmark;

                    const resolvedMediaUrls = Array.isArray(result.mediaUrls)
                        ? result.mediaUrls.map((url) => String(url || '').trim()).filter((url) => url && !url.startsWith('blob:') && !url.startsWith('data:'))
                        : [];
                    const resolvedProfileImg = String(result.profileImg || '').trim();
                    const resolvedPosterUrl = String(result.posterUrl || '').trim();
                    const hasResolvedMedia = resolvedMediaUrls.length > 0 || resolvedProfileImg || resolvedPosterUrl;
                    if (!hasResolvedMedia) return bookmark;

                    changed = true;
                    return {
                        ...bookmark,
                        ...(resolvedMediaUrls.length > 0 ? { mediaUrls: resolvedMediaUrls.join(','), mediaType: result.mediaType || bookmark.mediaType || 'image' } : {}),
                        ...(resolvedProfileImg ? { profileImg: resolvedProfileImg } : {}),
                        ...(resolvedPosterUrl ? { posterUrl: resolvedPosterUrl } : {}),
                        twitterMediaResolvedAt: resolvedAt
                    };
                });

                return changed ? nextBookmarks : previousBookmarks;
            });
        };

        window.addEventListener('bookmarkbase:twitter-media-resolved', handleTwitterMediaResolved);
        return () => window.removeEventListener('bookmarkbase:twitter-media-resolved', handleTwitterMediaResolved);
    }, []);

    // Önceki denemeden kalan sürüm işaretini veri modelinde tutmaya gerek yok.
    // Kalıcı medya URL'si ve çözüm zamanı, tekrar çözüm gerekip gerekmediğini belirlemek için yeterli.
    useEffect(() => {
        if (!isDbLoaded) return;
        setBookmarks((previousBookmarks) => {
            if (!previousBookmarks.some((bookmark) => Object.prototype.hasOwnProperty.call(bookmark, 'twitterMediaResolverVersion'))) {
                return previousBookmarks;
            }
            return previousBookmarks.map(({ twitterMediaResolverVersion, ...bookmark }) => bookmark);
        });
    }, [isDbLoaded]);

    useEffect(() => {
        if (!isDbLoaded) return;

        const tweetIds = bookmarks
            .filter((bookmark) => {
                const tweetId = String(bookmark.tweetId || extractTweetId(bookmark.url) || '').trim();
                const isTwitterBookmark = /(?:x\.com|twitter\.com)\//i.test(String(bookmark.url || ''));
                return isTwitterBookmark && /^\d+$/.test(tweetId) && !bookmark.twitterMediaResolvedAt && !requestedTwitterMediaRepairsRef.current.has(tweetId);
            })
            .slice(0, 25)
            .map((bookmark) => String(bookmark.tweetId || extractTweetId(bookmark.url)));

        if (tweetIds.length === 0) return;
        tweetIds.forEach((tweetId) => {
            requestedTwitterMediaRepairsRef.current.add(tweetId);
        });

        window.dispatchEvent(new CustomEvent('bookmarkbase:resolve-twitter-media', {
            detail: {
                requestId: `twitter-media-${Date.now()}`,
                tweetIds
            }
        }));
    }, [bookmarks, isDbLoaded]);

    useEffect(() => {
        if (!isDbLoaded) return;
        const scheduledDatabaseWriteVersion = databaseWriteVersionRef.current;
        const handler = setTimeout(() => {
            const syncDB = async () => {
                try {
                    // Eklentiden gelen kayıtlar IndexedDB'ye doğrudan yazılırken,
                    // eski bir debounce görevinin bu kaydı stale state'e göre silmesini önle.
                    if (scheduledDatabaseWriteVersion !== databaseWriteVersionRef.current) return;

                    await db.transaction('rw', db.bookmarks, db.folders, db.tags, db.trash, db.websiteBookmarks, db.websiteFolders, db.websiteTags, db.websiteTrash, async () => {
                        const doIncrementalSync = async (table, items) => {
                            const existingIds = new Set(await table.toCollection().primaryKeys());
                            const currentIds = new Set(items.map(item => item.id));

                            const toDelete = [...existingIds].filter(id => !currentIds.has(id));
                            if (toDelete.length) await table.bulkDelete(toDelete);
                            if (items.length) await table.bulkPut(items);
                        };

                        await doIncrementalSync(db.bookmarks, bookmarks);
                        await doIncrementalSync(db.folders, customFolders);
                        await doIncrementalSync(db.tags, customTags);
                        await doIncrementalSync(db.trash, trash);
                        await doIncrementalSync(db.websiteBookmarks, websiteBookmarks);
                        await doIncrementalSync(db.websiteFolders, websiteFolders);
                        await doIncrementalSync(db.websiteTags, websiteTags);
                        await doIncrementalSync(db.websiteTrash, websiteTrash);
                    });

                    // Offline sync logic
                    // Only push to cloud if this wasn't triggered BY a cloud load
                    if (user && isDbLoaded && !isCloudUpdateActive.current) {
                        // Enqueue the offline sync
                        saveToFirestore(user.uid);
                    }

                    window.dispatchEvent(new CustomEvent('tweetmark-data-changed'));
                } catch (err) { console.error("Dexie sync error:", err); }
            };
            syncDB();
        }, 3000); // 3 sn debounce - Reduced write amplification

        return () => clearTimeout(handler);
    }, [bookmarks, customFolders, customTags, trash, websiteBookmarks, websiteFolders, websiteTags, websiteTrash, isDbLoaded, user]);

    useEffect(() => {
        localStorage.setItem('tweetPostGridCols', postGridCols.toString());
        localStorage.setItem('tweetWebsiteGridCols', websiteGridCols.toString());
        localStorage.setItem('tweetWebsiteNotesVisible', websiteNotesVisible ? 'true' : 'false');
        localStorage.setItem('tweetWebsiteListDensity', websiteListDensity);
        localStorage.setItem('tweetWebsiteViewModes', JSON.stringify(websiteViewModes));
        localStorage.setItem('tweetWebsiteViewMode', websiteViewModes.All || DEFAULT_WEBSITE_VIEW_MODE);
        localStorage.setItem('tweetAccentColor', accentColor);
    }, [postGridCols, websiteGridCols, websiteNotesVisible, websiteListDensity, websiteViewModes, accentColor]);

    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedSearchQuery(searchQuery); }, 300);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        setActiveAddMenu(null);
        setFocusedTweet(null);
        setFocusedWebsite(null);
        setIsModalOpen(false);
        setIsWebsiteModalOpen(false);
        setIsSidebarOpen(false);
        setIsGridMenuOpen(false);
    }, [activeLibrary]);

    useEffect(() => {
        localStorage.setItem('tweetActiveLibrary', activeLibrary);
    }, [activeLibrary]);

    useEffect(() => {
        setVisibleCount(20);
    }, [activeFilters, debouncedSearchQuery, currentGridCols, activeLibrary, websiteActiveFilters, websiteSearchQuery]);

    useEffect(() => {
        if (!window.twttr) {
            const script = document.createElement("script");
            script.src = "https://platform.twitter.com/widgets.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    // Preload all images when preview opens
    useEffect(() => {
        if (previewState && previewState.medias && previewState.mediaType !== 'video') {
            previewState.medias.forEach(url => {
                const img = new Image();
                img.src = getMediaDisplayUrl(url);
            });
        }
    }, [previewState?.medias]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.dropdown-container')) {
                setActiveAddMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- HANDLERS ---
    const handleExportJSON = (isAuto = false) => {
        try {
            const payload = {
                bookmarks,
                customFolders,
                customTags,
                trash,
                websiteBookmarks,
                websiteFolders,
                websiteTags,
                websiteTrash,
                exportDate: new Date().toISOString(),
                version: '3.0'
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const dateStr = new Date().toLocaleDateString().replace(/\//g, '-');
            a.download = isAuto ? `bookmark_base_auto_backup_${dateStr}.json` : `bookmark_base_manual_backup_${dateStr}.json`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (err) {
            console.error("Backup failed:", err);
            showToast('Backup failed. Check console for details.', 'error');
        }
    };

    const handleImportJSON = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Perform confirmation before overwriting everything
        if (!window.confirm("Bu işlem mevcut tüm verilerinizi silecek ve yedek dosyasındaki verileri yükleyecektir. Emin misiniz?")) {
            event.target.value = null;
            return;
        }

        // Prevent importing if it exceeds 100MB Limit
        if (file.size + storageInfo.used >= 100 * 1024 * 1024) {
            showToast('Archive Limit (100 MB) reached. Please delete some items first.', 'error');
            event.target.value = null;
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (!data || typeof data !== 'object') {
                    showToast('Invalid or corrupted JSON file. Operation cancelled.', 'error');
                    event.target.value = null;
                    return;
                }

                const rawBookmarks = Array.isArray(data.bookmarks) ? data.bookmarks : [];
                const rawFolders = Array.isArray(data.customFolders) ? data.customFolders : (Array.isArray(data.folders) ? data.folders : []);
                const rawTags = Array.isArray(data.customTags) ? data.customTags : (Array.isArray(data.tags) ? data.tags : []);
                const rawTrash = Array.isArray(data.trash) ? data.trash : [];
                const rawWebsiteBookmarks = Array.isArray(data.websiteBookmarks) ? data.websiteBookmarks : [];
                const rawWebsiteFolders = Array.isArray(data.websiteFolders) ? data.websiteFolders : [];
                const rawWebsiteTags = Array.isArray(data.websiteTags) ? data.websiteTags : [];
                const rawWebsiteTrash = Array.isArray(data.websiteTrash) ? data.websiteTrash : [];

                const normalizeTagList = (value) => {
                    if (!Array.isArray(value)) return [];
                    return [...new Set(value
                        .map(t => String(t || '').trim().toLowerCase())
                        .filter(Boolean))];
                };

                const normalizeMediaUrls = (value) => {
                    const arr = Array.isArray(value)
                        ? value
                        : (typeof value === 'string' ? value.split(',') : []);
                    return [...new Set(arr.map(u => String(u || '').trim()).filter(Boolean))];
                };

                const normalizeBookmark = (item, idx, isTrashItem = false) => {
                    if (!item || typeof item !== 'object') return null;
                    const url = String(item.url || '').trim();
                    const id = item.id != null ? String(item.id) : `imp_${Date.now()}_${idx}`;
                    const derivedTweetId = item.tweetId != null ? String(item.tweetId) : (extractTweetId(url) || id);
                    if (!derivedTweetId) return null;

                    const medias = normalizeMediaUrls(item.mediaUrls);
                    const hasPlayableVideo = medias.some(isPlayableVideoUrl);

                    return {
                        ...item,
                        id,
                        tweetId: derivedTweetId,
                        url,
                        folder: normalizeFolder(item.folder),
                        tags: normalizeTagList(item.tags),
                        description: typeof item.description === 'string' ? item.description : '',
                        mediaUrls: medias.join(','),
                        mediaType: item.mediaType === 'video' ? 'video' : 'image',
                        hasPlayableVideo: hasPlayableVideo,
                        posterUrl: typeof item.posterUrl === 'string' ? item.posterUrl : '',
                        ...(isTrashItem ? { deletedAt: (Number(item.deletedAt) || Date.now()) } : (item.deletedAt ? { deletedAt: item.deletedAt } : {}))
                    };
                };

	                const normalizeWebsiteBookmark = (item, idx, isTrashItem = false) => {
	                    if (!item || typeof item !== 'object') return null;
	                    const url = sanitizeUrl(item.url || '');
	                    if (!url || url === '#') return null;
	                    const normalizedTags = normalizeTagList(item.tags);
	                    const savedAt = typeof item.savedAt === 'string' ? item.savedAt : new Date().toISOString();
	                    const sanitizedFaviconUrl = sanitizeWebsiteAssetUrl(item.faviconUrl);
	                    const sanitizedThumbnailUrl = sanitizeWebsiteAssetUrl(item.thumbnailUrl);
	                    return {
	                        ...item,
	                        id: item.id != null ? String(item.id) : `w_imp_${Date.now()}_${idx}`,
	                        url,
	                        title: String(item.title || item.name || getWebsiteTitleFromUrl(url) || getDomainFromUrl(url)).trim(),
	                        domain: String(item.domain || getDomainFromUrl(url)).trim(),
	                        faviconUrl: sanitizedFaviconUrl || undefined,
	                        thumbnailUrl: sanitizedThumbnailUrl || undefined,
	                        folder: normalizeFolder(item.folder),
	                        tags: normalizedTags,
	                        description: typeof item.description === 'string' ? item.description : '',
	                        savedAt,
	                        ...(isTrashItem ? { deletedAt: (Number(item.deletedAt) || Date.now()) } : (item.deletedAt ? { deletedAt: item.deletedAt } : {}))
                    };
                };

                const normalizedBookmarks = rawBookmarks
                    .map((b, idx) => normalizeBookmark(b, idx, false))
                    .filter(Boolean);

                const normalizedTrash = rawTrash
                    .map((t, idx) => normalizeBookmark(t, idx, true))
                    .filter(Boolean);
                const normalizedWebsiteBookmarks = rawWebsiteBookmarks
                    .map((item, idx) => normalizeWebsiteBookmark(item, idx, false))
                    .filter(Boolean);
                const normalizedWebsiteTrash = rawWebsiteTrash
                    .map((item, idx) => normalizeWebsiteBookmark(item, idx, true))
                    .filter(Boolean);

                const seenFolderNames = new Set();
                const normalizedFolders = rawFolders
                    .filter(f => f && typeof f === 'object')
                    .map((f, idx) => ({
                        id: f.id != null ? String(f.id) : `f_imp_${Date.now()}_${idx}`,
                        name: String(f.name || '').trim(),
                        color: typeof f.color === 'string' && f.color ? f.color : '#3b82f6',
                        parentId: f.parentId != null ? String(f.parentId) : null,
                        isPinned: !!f.isPinned,
                        order: f.order != null ? Number(f.order) : idx
                    }))
                    .filter(f => {
                        if (!f.name) return false;
                        const key = f.name.toLowerCase();
                        if (seenFolderNames.has(key)) return false;
                        seenFolderNames.add(key);
                        return true;
                    });

                const seenTagNames = new Set();
                const normalizedTags = rawTags
                    .filter(t => t && typeof t === 'object')
                    .map((t, idx) => ({
                        id: t.id != null ? String(t.id) : `t_imp_${Date.now()}_${idx}`,
                        name: String(t.name || '').trim().toLowerCase(),
                        color: typeof t.color === 'string' && t.color ? t.color : '#64748b',
                        isPinned: !!t.isPinned
                    }))
                    .filter(t => {
                        if (!t.name) return false;
                        if (seenTagNames.has(t.name)) return false;
                        seenTagNames.add(t.name);
                        return true;
                    });
	                const seenWebsiteFolderNames = new Set();
	                const normalizedWebsiteFolders = rawWebsiteFolders
	                    .filter(f => f && typeof f === 'object')
	                    .map((f, idx) => ({
	                        id: f.id != null ? String(f.id) : `wf_imp_${Date.now()}_${idx}`,
	                        name: String(f.name || '').trim(),
	                        color: typeof f.color === 'string' && f.color ? f.color : '#3b82f6',
	                        isPinned: !!f.isPinned,
	                        order: f.order != null ? Number(f.order) : idx
	                    }))
	                    .filter(f => {
	                        if (!f.name) return false;
	                        const key = f.name.toLowerCase();
	                        if (seenWebsiteFolderNames.has(key)) return false;
	                        seenWebsiteFolderNames.add(key);
	                        return true;
	                    });
	                const seenWebsiteTagNames = new Set();
	                const normalizedWebsiteTags = rawWebsiteTags
	                    .filter(t => t && typeof t === 'object')
	                    .map((t, idx) => ({
	                        id: t.id != null ? String(t.id) : `wt_imp_${Date.now()}_${idx}`,
	                        name: String(t.name || '').trim().toLowerCase(),
	                        color: typeof t.color === 'string' && t.color ? t.color : '#64748b',
	                        isPinned: !!t.isPinned
	                    }))
	                    .filter(t => {
	                        if (!t.name) return false;
	                        if (seenWebsiteTagNames.has(t.name)) return false;
	                        seenWebsiteTagNames.add(t.name);
	                        return true;
	                    });

                setBookmarks(normalizedBookmarks);
                setCustomFolders(normalizedFolders);
                setCustomTags(normalizedTags);
                setTrash(normalizedTrash);
                setWebsiteBookmarks(normalizedWebsiteBookmarks);
                setWebsiteFolders(normalizedWebsiteFolders);
                setWebsiteTags(normalizedWebsiteTags);
                setWebsiteTrash(normalizedWebsiteTrash);

                showToast('Backup loaded successfully!', 'success');
                event.target.value = null;
            } catch (err) {
                showToast('JSON parse error! Make sure the file is not corrupted.', 'error');
                console.error("Import JSON Error:", err);
                event.target.value = null;
            }
        };
        reader.readAsText(file);
    };

    const handleAddBookmark = (e) => {
        e.preventDefault();

        // 100 MB Safety Limit
        if (storageInfo.used >= 100 * 1024 * 1024) {
            return showToast('Archive Limit Reached (100 MB). Please delete items to free up space.', 'error');
        }

        const tweetId = extractTweetId(newUrl);
        if (!tweetId) return showToast('Please enter a valid Twitter or Reddit link.', 'error');

        const tagsArray = newTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        const newTagsList = [...customTags];
        let tagsChanged = false;
        tagsArray.forEach(tag => {
            if (!newTagsList.some(t => t.name === tag)) {
                newTagsList.push({ id: 't_' + Math.random().toString(36).substr(2, 9), name: tag, color: getRandomColor() });
                tagsChanged = true;
            }
        });
        if (tagsChanged) setCustomTags(newTagsList);

        const now = new Date();
        const dateStr = now.toLocaleDateString('tr-TR');
        const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

        const newBtn = {
            id: Date.now().toString(),
            tweetId,
            url: sanitizeUrl(newUrl),
            folder: normalizeFolder(newFolder),
            tags: tagsArray,
            description: newDesc.trim(),
            date: `${dateStr} ${timeStr}`,
            timestamp: now.getTime()
        };

        setBookmarks([newBtn, ...bookmarks]);
        setIsModalOpen(false);
        setNewUrl(''); setNewFolder(''); setNewTags(''); setNewDesc('');
    };

    const handleAddWebsiteBookmark = (e) => {
        e.preventDefault();

        if (storageInfo.used >= 100 * 1024 * 1024) {
            return showToast('Archive Limit Reached (100 MB). Please delete items to free up space.', 'error');
        }

        const cleanUrl = sanitizeUrl(newWebsiteUrl);
        const domain = getDomainFromUrl(cleanUrl);
        if (!domain) return showToast('Please enter a valid website URL.', 'error');
        if (websiteBookmarks.some((site) => site.url === cleanUrl)) {
            showToast('This link is already saved as a bookmark.', 'info');
            return;
        }

        const tagsArray = newWebsiteTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        const nextWebsiteTags = [...websiteTags];
        let tagsChanged = false;
        tagsArray.forEach(tag => {
            if (!nextWebsiteTags.some(t => t.name === tag)) {
                nextWebsiteTags.push({ id: 'wt_' + Math.random().toString(36).substr(2, 9), name: tag, color: getRandomColor(), isPinned: false });
                tagsChanged = true;
            }
        });
        if (tagsChanged) setWebsiteTags(nextWebsiteTags);

        const now = new Date();
	        const websiteBookmark = {
	            id: 'wb_' + Date.now(),
	            url: cleanUrl,
	            title: newWebsiteTitle.trim() || getWebsiteTitleFromUrl(cleanUrl) || domain,
	            domain,
	            source: 'web',
	            folder: normalizeFolder(newWebsiteFolder),
	            tags: tagsArray,
	            description: newWebsiteDesc.trim(),
	            savedAt: now.toISOString()
	        };

        setWebsiteBookmarks(prev => [websiteBookmark, ...prev]);
        setIsWebsiteModalOpen(false);
        setNewWebsiteUrl('');
        setNewWebsiteTitle('');
        setNewWebsiteFolder('');
        setNewWebsiteTags('');
        setNewWebsiteDesc('');
        showToast('Website bookmark saved.', 'success');
    };

    const createImportedWebsiteBookmark = (item) => {
        const url = sanitizeUrl(String(item?.url || ''));
        const domain = getDomainFromUrl(url);
        if (!domain) return null;
        const importedFolder = String(item?.folderName ?? item?.folder ?? '').trim();

        return {
            id: String(item?.id || `wb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
            url,
            title: String(item?.title || '').trim() || getWebsiteTitleFromUrl(url) || domain,
            domain,
            faviconUrl: sanitizeWebsiteAssetUrl(item?.faviconUrl),
            source: item?.source === 'extension' ? 'extension' : 'web',
            // Eklenti hem okunabilir klasör adını hem de eski `folder` alanını gönderir.
            // Yeni alan önceliklidir; böylece seçilen klasör senkron sırasında kaybolmaz.
            folder: normalizeFolder(importedFolder),
            tags: Array.isArray(item?.tags)
                ? item.tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean)
                : [],
            description: String(item?.description || item?.note || '').trim(),
            savedAt: item?.savedAt || new Date().toISOString()
        };
    };

    const addImportedWebsiteBookmark = (item, successMessage = 'Website bookmark saved.') => {
        if (storageInfo.used >= 100 * 1024 * 1024) {
            showToast('Archive Limit Reached (100 MB). Please delete items to free up space.', 'error');
            return false;
        }

        const bookmark = createImportedWebsiteBookmark(item);
        if (!bookmark) return false;
        if (websiteBookmarks.some((site) => site.url === bookmark.url)) {
            showToast('This link is already saved as a bookmark.', 'info');
            return false;
        }

        setWebsiteBookmarks((previous) => {
            if (previous.some((site) => site.url === bookmark.url)) return previous;
            return [bookmark, ...previous];
        });
        setActiveLibrary('websites');
        showToast(successMessage, 'success');
        return true;
    };

    useEffect(() => {
        if (!isDbLoaded) return undefined;

        const getQueuedWebsiteBookmarks = () => {
            try {
                const rawQueued = localStorage.getItem('pending_website_sync');
                const queued = rawQueued ? JSON.parse(rawQueued) : [];
                return Array.isArray(queued) ? queued : [];
            } catch (error) {
                console.error('Website bookmark queue could not be read:', error);
                return [];
            }
        };

        const getQueueKey = (item) => {
            const id = String(item?.id || '').trim();
            if (id) return `id:${id}`;
            const url = String(item?.url || '').trim();
            return url ? `url:${url}` : '';
        };

        const importQueuedWebsiteBookmarks = async () => {
            if (pendingWebsiteSyncInProgressRef.current) return;

            let queued = [];
            queued = getQueuedWebsiteBookmarks();
            if (queued.length === 0) return;

            if (storageInfo.used >= 100 * 1024 * 1024) {
                showToast('Archive Limit Reached (100 MB). Please delete items to free up space.', 'error');
                return;
            }

            pendingWebsiteSyncInProgressRef.current = true;
            try {
                const queuedByUrl = new Map();
                queued.forEach((item) => {
                    const bookmark = createImportedWebsiteBookmark(item);
                    if (bookmark) queuedByUrl.set(bookmark.url, { item, bookmark });
                });

                const queuedBookmarks = Array.from(queuedByUrl.values());
                if (queuedBookmarks.length === 0) return;

                const persistedBookmarks = await db.websiteBookmarks.toArray();
                const persistedByUrl = new Map(persistedBookmarks.map((bookmark) => [bookmark.url, bookmark]));
                const bookmarksToPersist = queuedBookmarks
                    .map(({ bookmark }) => {
                        const existing = persistedByUrl.get(bookmark.url);
                        if (!existing) return bookmark;

                        // Eklentiden gelen kayıt yeniden kaydedildiğinde, ilk kayıttaki
                        // Unsorted değeri yerine son seçilen klasör/not/etiket kullanılır.
                        // Web uygulamasında elle oluşturulmuş kayıtlar değişmeden kalır.
                        return existing.source === 'extension'
                            ? { ...bookmark, id: existing.id }
                            : null;
                    })
                    .filter(Boolean);

                // Kuyruğu yalnızca IndexedDB yazımı başarıyla tamamlandıktan sonra onaylıyoruz.
                if (bookmarksToPersist.length > 0) {
                    // Daha önce planlanan state->DB senkronu, bu yeni kalıcı kaydı
                    // eski state snapshot'ıyla silmemeli.
                    databaseWriteVersionRef.current += 1;
                    await db.websiteBookmarks.bulkPut(bookmarksToPersist);
                }

                const processedKeys = new Set(
                    queuedBookmarks
                        .map(({ item }) => getQueueKey(item))
                        .filter(Boolean)
                );

                setWebsiteBookmarks((previous) => {
                    const importedByUrl = new Map(bookmarksToPersist.map((bookmark) => [bookmark.url, bookmark]));
                    const updated = previous.map((bookmark) => {
                        const imported = importedByUrl.get(bookmark.url);
                        return imported && bookmark.source === 'extension' ? imported : bookmark;
                    });
                    const existingUrls = new Set(previous.map((bookmark) => bookmark.url));
                    const additions = bookmarksToPersist.filter((bookmark) => !existingUrls.has(bookmark.url));
                    return additions.length > 0 ? [...additions, ...updated] : updated;
                });

                setWebsiteTags((previous) => {
                    const existingNames = new Set(previous.map((tag) => tag.name));
                    const additions = bookmarksToPersist
                        .flatMap((bookmark) => bookmark.tags || [])
                        .filter((name) => name && !existingNames.has(name))
                        .filter((name, index, names) => names.indexOf(name) === index)
                        .map((name) => ({ id: `wt_${Math.random().toString(36).slice(2, 11)}`, name, color: getRandomColor(), isPinned: false }));
                    return additions.length > 0 ? [...previous, ...additions] : previous;
                });

                setWebsiteFolders((previous) => {
                    const existingNames = new Set(previous.map((folder) => folder.name));
                    const additions = bookmarksToPersist
                        .map((bookmark) => normalizeFolder(bookmark.folder))
                        .filter((name) => name && !existingNames.has(name))
                        .filter((name, index, names) => names.indexOf(name) === index)
                        .map((name) => ({ id: `wf_${Math.random().toString(36).slice(2, 11)}`, name, color: getRandomColor() }));
                    return additions.length > 0 ? [...previous, ...additions] : previous;
                });

                // Yazım sürerken kuyruğa yeni kayıt gelmiş olabilir. Bu nedenle son kuyruğu
                // tekrar okuyup yalnızca başarıyla işlediğimiz kayıtları kaldırıyoruz.
                const latestQueue = getQueuedWebsiteBookmarks();
                const remainingQueue = latestQueue.filter((item) => !processedKeys.has(getQueueKey(item)));
                if (remainingQueue.length > 0) {
                    localStorage.setItem('pending_website_sync', JSON.stringify(remainingQueue));
                } else {
                    localStorage.removeItem('pending_website_sync');
                }

                if (bookmarksToPersist.length > 0) {
                    setActiveLibrary('websites');
                    showToast(
                        bookmarksToPersist.length === 1
                            ? 'Current website added to your library.'
                            : `${bookmarksToPersist.length} websites added to your library.`,
                        'success'
                    );
                }
            } catch (error) {
                console.error('Website bookmark import failed:', error);
                showToast('Website bookmark could not be saved. It will be retried.', 'error');
            } finally {
                pendingWebsiteSyncInProgressRef.current = false;
            }
        };

        window.addEventListener('bookmarkbase:website-bookmarks-synced', importQueuedWebsiteBookmarks);
        importQueuedWebsiteBookmarks();
        return () => window.removeEventListener('bookmarkbase:website-bookmarks-synced', importQueuedWebsiteBookmarks);
    }, [isDbLoaded, storageInfo.used]);

    const handleMoveToTrash = (e, id) => {
        if (e) e.stopPropagation();
        const item = bookmarks.find(b => b.id === id);
        if (item) {
            setTrash(prev => [{ ...item, deletedAt: Date.now() }, ...prev]);
            setBookmarks(prev => prev.filter(b => b.id !== id));
            if (focusedTweet && focusedTweet.id === id) setFocusedTweet(null);
            showToast('Moved to trash', 'info', () => {
                // Undo: restore the item
                setTrash(prev => prev.filter(t => t.id !== id));
                setBookmarks(prev => [item, ...prev]);
            });
        }
    };

    const handleRestoreFromTrash = (e, id) => {
        e.stopPropagation();
        const item = trash.find(t => t.id === id);
        if (item) {
            const { deletedAt, ...rest } = item;
            setBookmarks(prev => [rest, ...prev]);
            setTrash(prev => prev.filter(t => t.id !== id));
        }
    };

    const handlePermanentDelete = (e, id) => {
        e.stopPropagation();
        const item = trash.find(t => t.id === id);
        setTrash(prev => prev.filter(t => t.id !== id));
        showToast('Permanently deleted', 'info', () => {
            if (item) setTrash(prev => [item, ...prev]);
        });
    };

    const handleClearTrash = () => {
        if (trash.length === 0) return;
        const oldTrash = [...trash];
        setTrash([]);
        showToast(`${oldTrash.length} item(s) permanently deleted`, 'info', () => {
            setTrash(oldTrash);
        });
    };

    const handleMoveWebsiteToTrash = (e, id) => {
        if (e) e.stopPropagation();
        const item = websiteBookmarks.find(site => site.id === id);
        if (!item) return;
        setWebsiteTrash(prev => [{ ...item, deletedAt: Date.now() }, ...prev]);
        setWebsiteBookmarks(prev => prev.filter(site => site.id !== id));
        if (focusedWebsite?.id === id) setFocusedWebsite(null);
        showToast('Website moved to trash', 'info', () => {
            setWebsiteTrash(prev => prev.filter(site => site.id !== id));
            setWebsiteBookmarks(prev => [item, ...prev]);
        });
    };

    const handleRestoreWebsiteFromTrash = (e, id) => {
        if (e) e.stopPropagation();
        const item = websiteTrash.find(site => site.id === id);
        if (!item) return;
        const { deletedAt, ...rest } = item;
        setWebsiteBookmarks(prev => [rest, ...prev]);
        setWebsiteTrash(prev => prev.filter(site => site.id !== id));
    };

    const handlePermanentDeleteWebsite = (e, id) => {
        if (e) e.stopPropagation();
        const item = websiteTrash.find(site => site.id === id);
        setWebsiteTrash(prev => prev.filter(site => site.id !== id));
        showToast('Website permanently deleted', 'info', () => {
            if (item) setWebsiteTrash(prev => [item, ...prev]);
        });
    };

    const handleClearWebsiteTrash = () => {
        if (websiteTrash.length === 0) return;
        const oldTrash = [...websiteTrash];
        setWebsiteTrash([]);
        showToast(`${oldTrash.length} website item(s) permanently deleted`, 'info', () => {
            setWebsiteTrash(oldTrash);
        });
    };

    // Auto-cleanup: remove trash items older than 30 days
    useEffect(() => {
        if (trash.length === 0) return;
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const expired = trash.filter(t => t.deletedAt && t.deletedAt < thirtyDaysAgo);
        if (expired.length > 0) {
            setTrash(prev => prev.filter(t => !t.deletedAt || t.deletedAt >= thirtyDaysAgo));
            console.log(`Auto-cleaned ${expired.length} expired trash item(s)`);
        }
    }, [trash.length]);

    useEffect(() => {
        if (websiteTrash.length === 0) return;
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const expired = websiteTrash.filter(item => item.deletedAt && item.deletedAt < thirtyDaysAgo);
        if (expired.length > 0) {
            setWebsiteTrash(prev => prev.filter(item => !item.deletedAt || item.deletedAt >= thirtyDaysAgo));
        }
    }, [websiteTrash.length]);

    const handleSaveFolder = (e) => {
        e.preventDefault();
        const name = folderNameInput.trim();
        if (!name) return;
        const targetFolders = activeLibrary === 'websites' ? websiteFolders : customFolders;
        const setTargetFolders = activeLibrary === 'websites' ? setWebsiteFolders : setCustomFolders;
        const targetBookmarks = activeLibrary === 'websites' ? websiteBookmarks : bookmarks;
        const setTargetBookmarks = activeLibrary === 'websites' ? setWebsiteBookmarks : setBookmarks;

        if (!editingFolder && targetFolders.some(f => f.name.toLowerCase() === name.toLowerCase())) {
            showToast('Folder already exists!', 'error');
            return;
        }
        if (editingFolder) {
            setTargetFolders(targetFolders.map(f => f.id === editingFolder.id ? { ...f, name, color: folderColorInput } : f));
            setTargetBookmarks(targetBookmarks.map(b => b.folder === editingFolder.name ? { ...b, folder: name } : b));
        } else {
            setTargetFolders([...targetFolders, {
                id: 'f_' + Date.now(),
                name,
                color: folderColorInput,
                parentId: null,
                isPinned: false,
                order: targetFolders.length
            }]);
        }
        setIsFolderModalOpen(false);
        setEditingFolder(null);
        setFolderNameInput('');
    };

    const handleSaveTag = (e) => {
        e.preventDefault();
        const name = tagNameInput.trim().toLowerCase();
        if (!name) return;
        const targetTags = activeLibrary === 'websites' ? websiteTags : customTags;
        const setTargetTags = activeLibrary === 'websites' ? setWebsiteTags : setCustomTags;
        const targetBookmarks = activeLibrary === 'websites' ? websiteBookmarks : bookmarks;
        const setTargetBookmarks = activeLibrary === 'websites' ? setWebsiteBookmarks : setBookmarks;

        if (!editingTag && targetTags.some(t => t.name.toLowerCase() === name.toLowerCase())) {
            showToast('Tag already exists!', 'error');
            return;
        }
        if (editingTag) {
            setTargetTags(targetTags.map(t => t.id === editingTag.id ? { ...t, name, color: tagColorInput } : t));
            setTargetBookmarks(targetBookmarks.map(b => ({
                ...b,
                tags: (b.tags || []).map(tag => tag === editingTag.name ? name : tag)
            })));
        } else {
            setTargetTags([...targetTags, { id: 't_' + Date.now(), name: name, color: tagColorInput, isPinned: false }]);
        }
        setIsTagModalOpen(false);
        setEditingTag(null);
        setTagNameInput('');
    };



    // --- RENDER HELPERS ---
    const pinnedFolders = useMemo(() => {
        return customFolders
            .filter(f => f.isPinned)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [customFolders]);


    const topLevelFolders = useMemo(() => {
        return customFolders
            .filter(f => !f.parentId && !f.isPinned)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [customFolders]);

    const folderCounts = useMemo(() => {
        const directCounts = {};
        bookmarks.forEach(b => {
            const fName = normalizeFolder(b.folder) || 'Unsorted';
            directCounts[fName] = (directCounts[fName] || 0) + 1;
        });

        const counts = {};
        customFolders.forEach(f => {
            counts[f.id] = directCounts[f.name] || 0;
        });
        return counts;
    }, [bookmarks, customFolders]);

    const tagCounts = useMemo(() => {
        const ObjectCounts = {};
        bookmarks.forEach(b => {
            (b.tags || []).forEach(t => {
                ObjectCounts[t] = (ObjectCounts[t] || 0) + 1;
            });
        });
        return ObjectCounts;
    }, [bookmarks]);

    const pinnedTags = useMemo(() => {
        return customTags.filter(t => t.isPinned).sort((a, b) => (tagCounts[b.name] || 0) - (tagCounts[a.name] || 0));
    }, [customTags, tagCounts]);

    const getFolderCount = (fId) => folderCounts[fId] || 0;
    const unsortedCount = useMemo(() => bookmarks.filter(b => isUnsortedFolder(b.folder)).length, [bookmarks]);
    const websitePinnedFolders = useMemo(() => websiteFolders.filter(f => f.isPinned).sort((a, b) => (a.order || 0) - (b.order || 0)), [websiteFolders]);
    const websiteTopLevelFolders = useMemo(() => websiteFolders.filter(f => !f.parentId && !f.isPinned).sort((a, b) => (a.order || 0) - (b.order || 0)), [websiteFolders]);
    const websiteTagCounts = useMemo(() => {
        const counts = {};
        websiteBookmarks.forEach(site => {
            (site.tags || []).forEach(tag => {
                counts[tag] = (counts[tag] || 0) + 1;
            });
        });
        return counts;
    }, [websiteBookmarks]);
    const websitePinnedTags = useMemo(() => websiteTags.filter(t => t.isPinned).sort((a, b) => (websiteTagCounts[b.name] || 0) - (websiteTagCounts[a.name] || 0)), [websiteTags, websiteTagCounts]);
    const websiteUnsortedCount = useMemo(() => websiteBookmarks.filter(site => isUnsortedFolder(site.folder)).length, [websiteBookmarks]);
    const websiteFolderDescendantNames = useMemo(() => {
        const childrenByParentId = new Map();
        websiteFolders.forEach((folder) => {
            const parentId = folder.parentId || null;
            const siblings = childrenByParentId.get(parentId) || [];
            siblings.push(folder);
            childrenByParentId.set(parentId, siblings);
        });

        return new Map(websiteFolders.map((folder) => {
            const names = new Set([folder.name]);
            const pending = [folder.id];
            while (pending.length > 0) {
                const parentId = pending.pop();
                (childrenByParentId.get(parentId) || []).forEach((child) => {
                    names.add(child.name);
                    pending.push(child.id);
                });
            }
            return [folder.name, names];
        }));
    }, [websiteFolders]);
    const websiteFolderCounts = useMemo(() => {
        const counts = {};
        const countsByFolderName = {};
        websiteBookmarks.forEach(site => {
            const folderName = normalizeFolder(site.folder);
            if (!folderName) return;
            countsByFolderName[folderName] = (countsByFolderName[folderName] || 0) + 1;
        });
        websiteFolders.forEach(folder => {
            const includedFolderNames = websiteFolderDescendantNames.get(folder.name) || new Set([folder.name]);
            counts[folder.id] = [...includedFolderNames].reduce((total, folderName) => total + (countsByFolderName[folderName] || 0), 0);
        });
        return counts;
    }, [websiteBookmarks, websiteFolders, websiteFolderDescendantNames]);

    const filteredBookmarks = useMemo(() => {
        const source = activeFilters.includes('Trash') ? trash : bookmarks;
        const tagFilters = activeFilters.filter(f => f.startsWith('tag:'));
        const folderFilters = activeFilters.filter(f => !f.startsWith('tag:'));

        const filtered = source.filter(b => {
            // Tag logic: MUST match ALL selected tags (Intersection)
            const matchTags = (activeFilters.includes('AllTags') ? (b.tags && b.tags.length > 0) : true) && tagFilters.every(filter => {
                const tagName = filter.split(':')[1];
                return (b.tags || []).includes(tagName);
            });

            // Folder logic: MUST match ANY selected folder or special view (Union)
            const matchFolders = folderFilters.length === 0 || folderFilters.some(filter => {
                if (filter === 'All' || filter === 'Trash') return true;
                if (filter === 'AllFolders') return !isUnsortedFolder(b.folder);
                if (filter === 'AllTags') return true; // Handled in matchTags
                if (filter === 'Unsorted') return isUnsortedFolder(b.folder);
                const normalized = normalizeFolder(b.folder);
                return normalized === filter;
            });

            const mF = matchTags && matchFolders;
            const s = debouncedSearchQuery.toLowerCase();
            return mF && (!s || (b.tags || []).some(t => t.includes(s)) || (b.description || '').toLowerCase().includes(s) || (b.tweetText || '').toLowerCase().includes(s) || (b.authorName || '').toLowerCase().includes(s));
        });
        // Sort newest first by timestamp or id (normalized Snowflake support)
        return filtered.sort((a, b) => {
            const getVal = (x) => {
                const ts = x.timestamp || (typeof x.date === 'number' ? x.date : null);
                if (ts) return ts;
                const idNum = parseInt(x.id);
                // Eğer ID bir Twitter Snowflake ise (10^15'ten büyükse), kabaca milisaniyeye çevir
                if (idNum > 1000000000000000) {
                    return Math.floor(idNum / 4194304) + 1288834974657;
                }
                return idNum || 0;
            };
            return getVal(b) - getVal(a);
        });
    }, [bookmarks, trash, activeFilters, debouncedSearchQuery, customFolders]);

    const filteredWebsiteBookmarks = useMemo(() => {
        const source = websiteActiveFilters.includes('Trash') ? websiteTrash : websiteBookmarks;
        const tagFilters = websiteActiveFilters.filter(f => f.startsWith('tag:'));
        const folderFilters = websiteActiveFilters.filter(f => !f.startsWith('tag:'));
        const search = websiteSearchQuery.trim().toLowerCase();

        return source.filter(site => {
            const matchesTags = (websiteActiveFilters.includes('AllTags') ? (site.tags && site.tags.length > 0) : true) && tagFilters.every(filter => {
                const tagName = filter.split(':')[1];
                return (site.tags || []).includes(tagName);
            });

            const matchesFolders = folderFilters.length === 0 || folderFilters.some(filter => {
                if (filter === 'All') return true;
                if (filter === 'Trash') return true;
                if (filter === 'AllFolders') return !isUnsortedFolder(site.folder);
                if (filter === 'AllTags') return true;
                if (filter === 'Unsorted') return isUnsortedFolder(site.folder);
                const includedFolderNames = websiteFolderDescendantNames.get(filter);
                return includedFolderNames ? includedFolderNames.has(normalizeFolder(site.folder)) : normalizeFolder(site.folder) === filter;
            });

            const matchesSearch = !search ||
                (site.title || '').toLowerCase().includes(search) ||
                (site.domain || '').toLowerCase().includes(search) ||
                (site.description || '').toLowerCase().includes(search) ||
                (site.tags || []).some(tag => tag.includes(search));

            return matchesTags && matchesFolders && matchesSearch;
        }).sort((a, b) => new Date(b.savedAt || 0).getTime() - new Date(a.savedAt || 0).getTime());
    }, [websiteBookmarks, websiteTrash, websiteActiveFilters, websiteSearchQuery, websiteFolderDescendantNames]);
    const visibleWebsiteBookmarks = useMemo(() => filteredWebsiteBookmarks.slice(0, visibleCount), [filteredWebsiteBookmarks, visibleCount]);
    const activeWebsiteFolderFilter = useMemo(() => websiteActiveFilters.find((filter) => (
        !filter.startsWith('tag:') && !['All', 'Trash', 'AllTags', 'AllFolders', 'Unsorted'].includes(filter)
    )) || null, [websiteActiveFilters]);
    const shouldGroupWebsiteRows = Boolean(activeWebsiteFolderFilter || websiteActiveFilters.includes('All'));
    const visibleWebsiteFolderGroups = useMemo(() => {
        if (!shouldGroupWebsiteRows) return [];

        const rootFolder = activeWebsiteFolderFilter
            ? websiteFolders.find((folder) => folder.name === activeWebsiteFolderFilter)
            : null;
        if (activeWebsiteFolderFilter && !rootFolder) return [];

        const childrenByParentId = new Map();
        websiteFolders.forEach((folder) => {
            const parentId = folder.parentId || null;
            const children = childrenByParentId.get(parentId) || [];
            children.push(folder);
            childrenByParentId.set(parentId, children);
        });

        const orderedFolders = [];
        const addFolderAndChildren = (folder) => {
            orderedFolders.push(folder);
            (childrenByParentId.get(folder.id) || [])
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .forEach(addFolderAndChildren);
        };
        if (rootFolder) {
            addFolderAndChildren(rootFolder);
        } else {
            websiteFolders
                .filter((folder) => !folder.parentId)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .forEach(addFolderAndChildren);
        }

        const sitesByFolder = new Map();
        filteredWebsiteBookmarks.forEach((site) => {
            const folderName = normalizeFolder(site.folder);
            if (!folderName) return;
            const sites = sitesByFolder.get(folderName) || [];
            sites.push(site);
            sitesByFolder.set(folderName, sites);
        });

        const orderedGroups = [];
        if (!rootFolder) {
            const unsortedSites = filteredWebsiteBookmarks.filter((site) => isUnsortedFolder(site.folder));
            if (unsortedSites.length > 0) {
                orderedGroups.push({ folder: { id: 'unsorted', name: 'Unsorted', color: '#94a3b8' }, sites: unsortedSites });
            }
        }
        orderedFolders.forEach((folder) => {
            const sites = sitesByFolder.get(folder.name) || [];
            if (sites.length > 0) orderedGroups.push({ folder, sites });
        });

        let remaining = visibleCount;
        return orderedGroups.reduce((groups, group) => {
            if (remaining <= 0) return groups;
            const visibleSites = group.sites.slice(0, remaining);
            remaining -= visibleSites.length;
            groups.push({ ...group, sites: visibleSites });
            return groups;
        }, []);
    }, [activeWebsiteFolderFilter, filteredWebsiteBookmarks, shouldGroupWebsiteRows, visibleCount, websiteFolders]);
    const currentVirtualListLength = activeLibrary === 'websites' ? filteredWebsiteBookmarks.length : filteredBookmarks.length;

    useEffect(() => {
        const target = observerTarget.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    setVisibleCount(prev => prev + 20);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [visibleCount, currentVirtualListLength]);


    // Helper: check if targetId is a descendant of folderId
    const isDescendantOf = (targetId, folderId, folders) => {
        if (!folderId || !targetId) return false;
        const children = (folders || customFolders).filter(f => f.parentId === folderId);
        for (const child of children) {
            if (child.id === targetId) return true;
            if (isDescendantOf(targetId, child.id, folders || customFolders)) return true;
        }
        return false;
    };

    const FolderItem = ({ folder, depth = 0 }) => {
        const isWebsiteFolder = activeLibrary === 'websites';
        const libraryFolders = isWebsiteFolder ? websiteFolders : customFolders;
        const libraryFolderCounts = isWebsiteFolder ? websiteFolderCounts : folderCounts;
        const libraryActiveFilters = isWebsiteFolder ? websiteActiveFilters : activeFilters;
        const toggleLibraryFilter = isWebsiteFolder ? toggleWebsiteFilter : toggleFilter;
        const setLibraryFolders = isWebsiteFolder ? setWebsiteFolders : setCustomFolders;
        const children = useMemo(() => {
            return libraryFolders
                .filter(f => f.parentId === folder.id)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        }, [libraryFolders, folder.id]);

        const isPinnedOpen = expandedFolders.includes(folder.id);
        const isHoverOpen = hoverExpandedFolders.includes(folder.id);
        const hasActiveDescendant = libraryActiveFilters.some((filter) => {
            if (filter.startsWith('tag:')) return false;
            const activeFolder = libraryFolders.find((item) => item.name === filter);
            return activeFolder && activeFolder.id !== folder.id && isDescendantOf(activeFolder.id, folder.id, libraryFolders);
        });
        const isExpanded = isPinnedOpen || isHoverOpen || hasActiveDescendant;
        const visibleChildren = !isPinnedOpen && !isHoverOpen && hasActiveDescendant
            ? children.filter((child) => libraryActiveFilters.some((filter) => {
                if (filter.startsWith('tag:')) return false;
                const activeFolder = libraryFolders.find((item) => item.name === filter);
                return activeFolder && (activeFolder.id === child.id || isDescendantOf(activeFolder.id, child.id, libraryFolders));
            }))
            : children;
        const isActive = libraryActiveFilters.includes(folder.name);
        const isDragOver = dragOverFolderId === folder.id;
        const currentDropPos = folderDropTarget?.id === folder.id ? folderDropTarget.position : null;

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const dragData = dragItemRef.current;
            if (!dragData) return;

            if (children.length > 0) {
                const closeTimer = folderHoverCloseTimersRef.current.get(folder.id);
                if (closeTimer) {
                    clearTimeout(closeTimer);
                    folderHoverCloseTimersRef.current.delete(folder.id);
                }
                setHoverExpandedFolders((previous) => previous.includes(folder.id) ? previous : [...previous, folder.id]);
            }

            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            let pos = 'inside';

            // Only allow reordering for folders
            if (dragData.type === 'folder' && dragData.id !== folder.id) {
                if (y < rect.height * 0.25) pos = 'before';
                else if (y > rect.height * 0.75) pos = 'after';
            }

            if (dragOverFolderId !== folder.id || folderDropTarget?.position !== pos) {
                setDragOverFolderId(folder.id);
                setFolderDropTarget({ id: folder.id, position: pos });
                folderDropRef.current = { id: folder.id, position: pos };
            }
        };

        const handleDragEnd = (e) => {
            if (e) e.stopPropagation();
            clearDragState();
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const dragData = dragItemRef.current;
            const dropData = folderDropRef.current;

            handleDragEnd();

            if (!dragData || !dropData) return;

            if (dragData.type === 'folder') {
                const movedId = dragData.id;
                const targetId = dropData.id;
                const position = dropData.position || 'inside';

                if (movedId === targetId) return;

                setLibraryFolders(prev => {
                    // Safety check: Don't drop a folder into its own child
                    if (isDescendantOf(targetId, movedId, prev)) return prev;

                    let items = [...prev];
                    const movedIdx = items.findIndex(f => f.id === movedId);
                    if (movedIdx === -1) return prev;

                    const movedItem = { ...items[movedIdx] };
                    items.splice(movedIdx, 1);

                    if (position === 'inside') {
                        movedItem.parentId = targetId;
                        items.push(movedItem);
                    } else {
                        const targetFolder = prev.find(f => f.id === targetId);
                        movedItem.parentId = targetFolder.parentId;
                        const targetIndex = items.findIndex(f => f.id === targetId);
                        if (position === 'before') {
                            items.splice(targetIndex, 0, movedItem);
                        } else {
                            items.splice(targetIndex + 1, 0, movedItem);
                        }
                    }

                    // Re-assign global order based on the new array sequence
                    return items.map((f, idx) => ({ ...f, order: idx }));
                });
            } else if (dragData.type === 'tweet') {
                setBookmarks(prev => prev.map(b => dragData.ids.includes(b.id) ? { ...b, folder: folder.name } : b));
            } else if (dragData.type === 'website') {
                setWebsiteBookmarks(prev => prev.map(site => dragData.ids.includes(site.id) ? { ...site, folder: folder.name } : site));
                setSelectedWebsiteIds([]);
            }
        };

        return (
            <div
                className="w-full relative"
                onMouseEnter={() => {
                    if (children.length === 0) return;
                    const closeTimer = folderHoverCloseTimersRef.current.get(folder.id);
                    if (closeTimer) {
                        clearTimeout(closeTimer);
                        folderHoverCloseTimersRef.current.delete(folder.id);
                    }
                    setHoverExpandedFolders((previous) => previous.includes(folder.id) ? previous : [...previous, folder.id]);
                }}
                onMouseLeave={() => {
                    if (children.length === 0) return;
                    const existingTimer = folderHoverCloseTimersRef.current.get(folder.id);
                    if (existingTimer) clearTimeout(existingTimer);
                    const closeTimer = setTimeout(() => {
                        setHoverExpandedFolders((previous) => previous.includes(folder.id) ? previous.filter((id) => id !== folder.id) : previous);
                        folderHoverCloseTimersRef.current.delete(folder.id);
                    }, 90);
                    folderHoverCloseTimersRef.current.set(folder.id, closeTimer);
                }}
            >
                {/* Visual Line - Top */}
                {isDragOver && currentDropPos === 'before' && (
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-blue-500 z-50 rounded-full" style={{ marginLeft: `${depth * 1.25 + 1.25}rem` }} />
                )}

                <div
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); dragItemRef.current = { type: 'folder', id: folder.id }; }}
                    onDragEnter={(e) => e.preventDefault()}
                    onDragOver={handleDragOver}
                    onDragLeave={(e) => e.stopPropagation()}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                    className={`group flex items-center rounded-xl transition-all cursor-pointer relative ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${isDragOver && currentDropPos === 'inside' ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`}
                    style={{ paddingLeft: `${depth * 1.25 + 0.25}rem`, paddingRight: '1rem', paddingTop: '0.05rem', paddingBottom: '0.05rem', ...(isActive && (!isDragOver || currentDropPos !== 'inside') ? { backgroundColor: accentColor } : {}) }}
                >
                    <button onClick={(e) => { e.stopPropagation(); setExpandedFolders(prev => prev.includes(folder.id) ? prev.filter(x => x !== folder.id) : [...prev, folder.id]); }} className={`w-4 h-5 flex items-center justify-center shrink-0 ${children.length === 0 ? 'invisible' : ''}`}><LucideIcon name="chevron-right" className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} size={10} /></button>
                    <button onClick={() => toggleLibraryFilter(folder.name)} className="flex-1 flex items-center gap-2 text-[15px] font-bold truncate py-1 text-left"><LucideIcon name="folder" className="text-[14px]" style={{ color: isActive ? '#fff' : folder.color }} /> <span>{folder.name}</span></button>
                    {/* Pin icon - left of count area */}
                    {folder.isPinned && <span className="opacity-60 group-hover:hidden w-5 h-5 inline-flex items-center justify-center shrink-0"><LucideIcon name="pin" size={11} /></span>}
                    <button onClick={(e) => { e.stopPropagation(); setLibraryFolders(prev => prev.map(f => f.id === folder.id ? { ...f, isPinned: !f.isPinned } : f)); }} className={`hidden group-hover:inline-flex w-5 h-5 items-center justify-center transition-colors shrink-0 ${folder.isPinned ? 'text-blue-500 hover:text-slate-400' : 'text-slate-400 hover:text-blue-500'}`} title={folder.isPinned ? 'Unpin folder' : 'Pin folder'}><LucideIcon name="pin" size={11} /></button>
                    <div className="relative flex items-center justify-center w-8 h-5 shrink-0"><span className="text-[11px] font-black opacity-60 group-hover:opacity-0">{libraryFolderCounts[folder.id] || 0}</span><button onClick={(e) => { e.stopPropagation(); setEditingFolder(folder); setFolderNameInput(folder.name); setFolderColorInput(folder.color); setIsFolderModalOpen(true); }} className="absolute inset-0 hidden group-hover:inline-flex items-center justify-center text-slate-400 hover:text-blue-500"><LucideIcon name="pen" size={11} /></button></div>
                </div>

                {/* Visual Line - Bottom */}
                {isDragOver && currentDropPos === 'after' && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-500 z-50 rounded-full" style={{ marginLeft: `${depth * 1.25 + 1.25}rem` }} />
                )}

                {children.length > 0 && (
                    <div className={`grid transition-[grid-template-rows,opacity] duration-150 ease-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                        <div className="min-h-0 overflow-hidden">
                            {visibleChildren.map(c => <FolderItem key={c.id} folder={c} depth={depth + 1} />)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const WebsiteTagItem = ({ tag }) => {
        const isActive = websiteActiveFilters.includes(`tag:${tag.name}`);
        const count = websiteTagCounts[tag.name] || 0;
        const isPillLayout = usePillTags;

        if (isPillLayout) {
            return (
                <div
                    onClick={() => toggleWebsiteFilter(`tag:${tag.name}`)}
                    onDragEnter={(event) => event.preventDefault()}
                    onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (dragItemRef.current?.type === 'website') setDragOverTagId(tag.id);
                    }}
                    onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                    onDrop={(event) => handleWebsiteTagDrop(event, tag.name)}
                    className={`group h-8 flex items-center rounded-lg transition-colors cursor-pointer px-3 py-0.5 border ${isActive ? 'text-white shadow-sm border-transparent' : 'text-slate-600 bg-slate-50 border-slate-100 hover:border-slate-200 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 bg-blue-50/70' : ''}`}
                    style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                >
                    <span className="font-black text-[12px] mr-1.5 shrink-0 flex items-center" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                    <span className="text-[11px] font-bold uppercase tracking-wider truncate max-w-[140px] flex items-center">{tag.name}</span>
                    <div className="relative flex items-center justify-center ml-2 min-w-[28px] h-full">
                        <span className={`text-[11px] font-black flex items-center pt-[1px] group-hover:opacity-0 ${isActive ? 'opacity-70' : 'opacity-30'}`}>{count}</span>
                        <div className="absolute inset-0 flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100">
                            <button onClick={(e) => { e.stopPropagation(); setWebsiteTags(prev => prev.map(item => item.id === tag.id ? { ...item, isPinned: !item.isPinned } : item)); }} className={`w-4 h-4 inline-flex items-center justify-center hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`} title={tag.isPinned ? 'Unpin tag' : 'Pin tag'}><LucideIcon name="pin" size={10} /></button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`w-4 h-4 inline-flex items-center justify-center hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={10} /></button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div onClick={() => toggleWebsiteFilter(`tag:${tag.name}`)} onDragEnter={(event) => event.preventDefault()} onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); if (dragItemRef.current?.type === 'website') setDragOverTagId(tag.id); }} onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }} onDrop={(event) => handleWebsiteTagDrop(event, tag.name)} className={`px-4 flex items-center rounded-xl transition-colors cursor-pointer group ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`} style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}>
                <button className="flex-1 flex items-center gap-2 py-0 text-[15px] font-bold text-left"><span className="font-black text-[14px] w-5 flex justify-center shrink-0" style={{ color: isActive ? '#fff' : tag.color }}>#</span><span className="truncate uppercase tracking-wider text-[11px]">{tag.name}</span></button>
                <button onClick={(e) => { e.stopPropagation(); setWebsiteTags(prev => prev.map(item => item.id === tag.id ? { ...item, isPinned: !item.isPinned } : item)); }} className={`w-5 h-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} title={tag.isPinned ? 'Unpin tag' : 'Pin tag'}><LucideIcon name="pin" size={11} /></button>
                <div className="relative flex items-center justify-center w-8 h-5 shrink-0"><span className={`text-[11px] font-black group-hover:opacity-0 ${isActive ? 'text-white/70' : 'opacity-60'}`}>{count}</span><button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={11} /></button></div>
            </div>
        );
    };

    const [windowWidthState, setWindowWidthState] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidthState(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => () => {
        folderHoverCloseTimersRef.current.forEach((timer) => clearTimeout(timer));
        folderHoverCloseTimersRef.current.clear();
    }, []);

    const effectiveCols = useMemo(() => {
        const w = windowWidthState || window.innerWidth;
        if (w < 640) return 1;
        if (w < 1024) return Math.min(2, currentGridCols || 1);
        if (w < 1280) return Math.min(3, currentGridCols || 1);
        if (w < 1536) return Math.min(4, currentGridCols || 1);
        return currentGridCols || 1;
    }, [windowWidthState, currentGridCols]);
    const websiteResponsiveCols = useMemo(() => {
        const w = windowWidthState || window.innerWidth;
        const horizontalPadding = w >= 768 ? 64 : (w >= 640 ? 32 : 24);
        const gap = 16;
        const minCardWidth = 238;
        const availableWidth = Math.max(w - horizontalPadding, minCardWidth);
        const maxColsByWidth = Math.max(1, Math.floor((availableWidth + gap) / (minCardWidth + gap)));
        return Math.max(1, Math.min(websiteGridCols || 1, maxColsByWidth));
    }, [windowWidthState, websiteGridCols]);
    const websiteGridStyle = useMemo(() => ({
        gridTemplateColumns: `repeat(${websiteResponsiveCols}, minmax(0, 1fr))`
    }), [websiteResponsiveCols]);
    const websiteLayoutWidth = useMemo(() => {
        const cardWidth = 280;
        const gap = 16;
        return `${(websiteResponsiveCols * cardWidth) + (Math.max(websiteResponsiveCols - 1, 0) * gap)}px`;
    }, [websiteResponsiveCols]);

    const bookmarkColumns = useMemo(() => {
        const cols = Array.from({ length: effectiveCols }, () => []);
        if (filteredBookmarks && Array.isArray(filteredBookmarks)) {
            filteredBookmarks.slice(0, visibleCount).forEach((b, i) => {
                cols[i % effectiveCols].push(b);
            });
        }
        return cols;
    }, [filteredBookmarks, visibleCount, effectiveCols]);

    const gridConfig = {
        1: { padding: 'max-w-3xl' },
        2: { padding: 'max-w-5xl' },
        3: { padding: 'max-w-6xl' },
        4: { padding: 'max-w-[90rem]' },
        5: { padding: 'max-w-[120rem]' }
    }[currentGridCols] || { padding: 'max-w-6xl' };

    const LibrarySwitch = ({ mobile = false }) => (
        <div className={`grid grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1 ${mobile ? '' : 'mt-4'}`}>
            {[
                { id: 'posts', label: 'Posts', icon: 'bookmark' },
                { id: 'websites', label: 'Websites', icon: 'globe' }
            ].map(item => {
                const isActive = activeLibrary === item.id;
                return (
                    <button
                        key={item.id}
                        onClick={() => setActiveLibrary(item.id)}
                        className={`flex items-center justify-center gap-2 rounded-[0.9rem] px-3 py-2 text-[12px] font-black uppercase tracking-wider transition-all ${isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <LucideIcon name={item.icon} size={14} />
                        <span>{item.label}</span>
                    </button>
                );
            })}
        </div>
    );

    const WebsiteCard = ({ site }) => {
        const isInTrash = websiteActiveFilters.includes('Trash');
        const hasDescription = Boolean(site.description?.trim());
        const showDescription = hasDescription && websiteNotesVisible;
        const handleThumbnailResolved = (preview) => {
            const imageUrl = preview?.imageUrl || '';
            const resolvedTitle = preview?.title || '';
            const nextPatch = {};

            if (imageUrl) nextPatch.thumbnailUrl = imageUrl;
            if (resolvedTitle && isGeneratedWebsiteTitle(site.title, site.url, site.domain)) nextPatch.title = resolvedTitle;
            if (Object.keys(nextPatch).length === 0) return;

            setWebsiteBookmarks(prev => prev.map(item => item.id === site.id ? { ...item, ...nextPatch } : item));
            setWebsiteTrash(prev => prev.map(item => item.id === site.id ? { ...item, ...nextPatch } : item));
            if (focusedWebsite?.id === site.id) {
                setFocusedWebsite(prev => prev ? { ...prev, ...nextPatch } : prev);
            }
        };
        return (
            <div onClick={() => { if (!isInTrash) { setFocusedWebsite(site); setInitialFocusedWebsite(site); setIsWebsiteNoteEditing(false); } }} className={`group flex h-full w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-slate-300 hover:shadow-lg ${isInTrash ? 'opacity-75' : 'cursor-pointer'}`}>
                <div className="relative">
                    <WebsiteThumbnail site={site} className="aspect-[280/158] w-full bg-slate-100" onResolved={handleThumbnailResolved} />
                    <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-white/92 text-slate-400 opacity-0 shadow-sm ring-1 ring-slate-200/80 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:text-slate-900"
                    >
                        <LucideIcon name="external-link" size={14} />
                    </a>
                </div>
                <div className={`flex flex-1 flex-col p-3 pt-2.5 ${showDescription ? 'min-h-[148px]' : 'min-h-[118px]'}`}>
                    <div className="min-w-0 flex-1">
                        <h3 className="line-clamp-2 text-[14px] font-semibold leading-[1.25] text-slate-950">{site.title}</h3>
                    </div>
                    {showDescription && (
                        <div className="mt-3 rounded-xl border border-slate-200/80 bg-slate-100/80 px-3 py-2">
                            <p className="line-clamp-2 text-[12px] leading-relaxed text-slate-600">{site.description}</p>
                        </div>
                    )}
                    <div className="mt-auto flex items-center justify-between gap-3 pt-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <WebsiteFavicon site={site} className="h-5 w-5 shrink-0" iconSize={12} />
                            <p className="truncate text-[11px] font-medium text-slate-500">{site.domain}</p>
                        </div>
	                        {isInTrash ? (
	                            <div className="flex gap-2">
	                                <button onClick={(e) => handleRestoreWebsiteFromTrash(e, site.id)} className="text-green-500 hover:text-green-600"><LucideIcon name="rotate-ccw" size={16} /></button>
	                                <button onClick={(e) => handlePermanentDeleteWebsite(e, site.id)} className="text-red-500 hover:text-red-700"><LucideIcon name="trash-2" size={16} /></button>
	                            </div>
	                        ) : (
	                            <button onClick={(e) => handleMoveWebsiteToTrash(e, site.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600 transition-all p-1"><LucideIcon name="trash-2" size={16} /></button>
	                        )}
	                    </div>
	                </div>
            </div>
        );
    };

    const LegacyWebsiteListRow = ({ site }) => {
        const isInTrash = websiteActiveFilters.includes('Trash');
        const isCompact = websiteListDensity === '2x';
        const folderName = normalizeFolder(site.folder);
        const visibleTags = (site.tags || []).slice(0, 2);
        const hiddenTagCount = Math.max(0, (site.tags || []).length - visibleTags.length);
        const hasNote = Boolean(site.description?.trim());

        useEffect(() => {
            if (!isGeneratedWebsiteTitle(site.title, site.url, site.domain)) return undefined;
            let isMounted = true;

            resolveWebsitePreviewData(site.url).then((preview) => {
                const resolvedTitle = String(preview?.title || '').trim();
                if (!isMounted || !resolvedTitle) return;

                setWebsiteBookmarks((previous) => previous.map((item) => (
                    item.id === site.id ? { ...item, title: resolvedTitle } : item
                )));
                setWebsiteTrash((previous) => previous.map((item) => (
                    item.id === site.id ? { ...item, title: resolvedTitle } : item
                )));
                if (focusedWebsite?.id === site.id) {
                    setFocusedWebsite((previous) => previous ? { ...previous, title: resolvedTitle } : previous);
                }
            });

            return () => { isMounted = false; };
        }, [site.id, site.url, site.title, site.domain]);

        return (
            <div
                onClick={() => { if (!isInTrash) { setFocusedWebsite(site); setInitialFocusedWebsite(site); setIsWebsiteNoteEditing(false); } }}
                draggable={!isInTrash}
                onDragStart={(event) => { event.stopPropagation(); dragItemRef.current = { type: 'website', ids: [site.id] }; }}
                onDragEnd={() => clearDragState()}
                className={`group relative flex min-w-0 items-center rounded-lg border border-slate-200/90 bg-white shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 ${isCompact ? 'gap-2 px-2.5 py-1.5' : 'gap-3 px-3 py-2'} ${isInTrash ? 'opacity-75' : 'cursor-pointer'}`}
            >
                <a
                    href={site.url}
                    onClick={(event) => event.preventDefault()}
                    className={`flex min-w-0 basis-1/2 shrink-0 items-center ${isCompact ? 'gap-2' : 'gap-3'}`}
                >
                    <div className={`flex shrink-0 items-center justify-center ${isCompact ? 'h-7 w-7' : 'h-9 w-9'}`}>
                        <WebsiteFavicon site={site} className={isCompact ? 'h-6 w-6' : 'h-8 w-8'} iconSize={isCompact ? 20 : 26} />
                    </div>
                    <div className={`relative min-w-0 flex-1 ${isCompact ? 'h-8' : 'h-10'}`}>
                        <h3 className={`absolute left-0 top-1/2 w-full -translate-y-1/2 truncate font-semibold leading-tight text-slate-900 transition-all duration-200 ease-out group-hover:top-0 group-hover:translate-y-0 ${isCompact ? 'text-[13px]' : 'text-sm'}`}>{site.title}</h3>
                        <p className={`absolute left-0 w-full truncate text-slate-400 opacity-0 translate-y-1 transition-all duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-70 ${isCompact ? 'top-4 text-[10px]' : 'top-5 text-xs'}`}>{site.url}</p>
                    </div>
                </a>
                <div className="relative flex min-w-0 flex-1 items-center justify-end">
                    {isInTrash ? (
                        <div className="flex items-center gap-1">
                            <button onClick={(e) => handleRestoreWebsiteFromTrash(e, site.id)} className="flex h-8 w-8 items-center justify-center rounded-md text-green-500 transition-all hover:bg-green-50 hover:text-green-600"><LucideIcon name="rotate-ccw" size={15} /></button>
                            <button onClick={(e) => handlePermanentDeleteWebsite(e, site.id)} className="flex h-8 w-8 items-center justify-center rounded-md text-red-500 transition-all hover:bg-red-50 hover:text-red-700"><LucideIcon name="trash-2" size={15} /></button>
                        </div>
                    ) : (
                        <>
                            <div className="flex min-w-0 items-center gap-1.5">
                            <div className="flex min-w-0 items-center gap-1.5" title={[folderName, ...(site.tags || [])].filter(Boolean).join(' · ')}>
                                {hasNote && <span className="rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500">Note</span>}
                                {folderName && (
                                    <button onClick={(event) => { event.preventDefault(); event.stopPropagation(); toggleWebsiteFilter(folderName); }} className="flex max-w-[96px] items-center gap-1 rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200">
                                        <LucideIcon name="folder" size={11} style={{ color: websiteFolders.find(folder => folder.name === folderName)?.color || '#94a3b8' }} />
                                        <span className="truncate">{folderName}</span>
                                    </button>
                                )}
                                {visibleTags.map(tag => (
                                    <button key={tag} onClick={(event) => { event.preventDefault(); event.stopPropagation(); toggleWebsiteFilter(`tag:${tag}`); }} className="flex max-w-[76px] items-center gap-0.5 rounded bg-slate-100 px-1.5 py-1 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200">
                                        <span style={{ color: websiteTags.find(item => item.name === tag)?.color || '#94a3b8' }}>#</span>
                                        <span className="truncate">{tag}</span>
                                    </button>
                                ))}
                                {hiddenTagCount > 0 && <span className="text-[10px] font-medium text-slate-400">+{hiddenTagCount}</span>}
                            </div>
                            <a
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-slate-900"
                                title="Open website"
                            >
                                <LucideIcon name="external-link" size={15} />
                            </a>
                            </div>
                        </>
                    )}
                </div>
                {!isInTrash && <button onClick={(event) => handleMoveWebsiteToTrash(event, site.id)} className="absolute -right-10 top-1/2 flex h-8 w-8 -translate-y-1/2 translate-x-1 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-300 opacity-0 shadow-sm transition-all duration-200 hover:border-red-100 hover:bg-red-50 hover:text-red-600 group-hover:translate-x-0 group-hover:opacity-100" title="Move to trash"><LucideIcon name="trash-2" size={15} /></button>}
            </div>
        );
    };

    const websiteFolderColorByName = useMemo(() => Object.fromEntries(websiteFolders.map(folder => [folder.name, folder.color])), [websiteFolders]);
    const websiteFolderShowsOwnLabelByName = useMemo(() => {
        const parentFolderIds = new Set(websiteFolders.map((folder) => folder.parentId).filter(Boolean));
        return Object.fromEntries(websiteFolders.map((folder) => [folder.name, !folder.parentId && parentFolderIds.has(folder.id)]));
    }, [websiteFolders]);
    const websiteTagColorByName = useMemo(() => Object.fromEntries(websiteTags.map(tag => [tag.name, tag.color])), [websiteTags]);
    const handleWebsiteRowSelect = useCallback((site) => {
        setFocusedWebsite(site);
        setInitialFocusedWebsite(site);
        setIsWebsiteNoteEditing(false);
    }, []);
    const toggleWebsiteSelection = useCallback((id) => {
        setSelectedWebsiteIds((previous) => previous.includes(id)
            ? previous.filter((selectedId) => selectedId !== id)
            : [...previous, id]);
    }, []);
    const handleWebsiteRowDragStart = useCallback((event, id) => {
        event.stopPropagation();
        const ids = selectedWebsiteIds.includes(id) ? selectedWebsiteIds : [id];
        dragItemRef.current = { type: 'website', ids };
    }, [selectedWebsiteIds]);
    const isWebsiteLibrary = activeLibrary === 'websites';
    const currentWebsiteViewScope = useMemo(() => getWebsiteViewScopeKey(websiteActiveFilters), [websiteActiveFilters]);
    const isWebsiteListMode = true;
    const websiteUsesListWidth = !websiteActiveFilters.includes('AllFolders') && !websiteActiveFilters.includes('AllTags');
    const currentSearchQuery = isWebsiteLibrary ? websiteSearchQuery : searchQuery;
    const currentPrimaryCount = isWebsiteLibrary ? websiteBookmarks.length : bookmarks.length;
    const currentUnsortedCount = isWebsiteLibrary ? websiteUnsortedCount : unsortedCount;
    const currentActiveFilters = isWebsiteLibrary ? websiteActiveFilters : activeFilters;

    const getDroppedWebsiteUrl = (dataTransfer) => {
        const uriList = String(dataTransfer?.getData('text/uri-list') || '')
            .split(/\r?\n/)
            .find((entry) => entry && !entry.startsWith('#'));
        const plainText = String(dataTransfer?.getData('text/plain') || '').trim();
        const candidate = uriList || plainText;
        return /^https?:\/\//i.test(candidate) ? candidate : '';
    };

    const handleWebsiteDragOver = (event) => {
        if (dragItemRef.current) return;
        const types = Array.from(event.dataTransfer?.types || []);
        if (!types.includes('text/uri-list') && !types.includes('text/plain')) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsWebsiteDropActive(true);
    };

    const handleWebsiteDrop = (event) => {
        if (dragItemRef.current) return;
        const url = getDroppedWebsiteUrl(event.dataTransfer);
        setIsWebsiteDropActive(false);
        if (!url) return;
        event.preventDefault();
        addImportedWebsiteBookmark({ url }, 'Website link added to your library.');
    };

    if (!isDbLoaded) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-slate-50 flex-col gap-4">
                <LucideIcon name="loader" className="fa-spin text-4xl text-slate-300" />
                <p className="font-bold text-slate-500 uppercase tracking-widest text-sm">Loading Database...</p>
            </div>
        );
    }

    return (
        <div
            className={`flex h-screen w-full bg-slate-50 text-slate-800 font-sans overflow-hidden theme-${theme}`}
            onDragOver={handleWebsiteDragOver}
            onDragLeave={(event) => {
                if (event.currentTarget === event.target) setIsWebsiteDropActive(false);
            }}
            onDrop={handleWebsiteDrop}
        >
            {isWebsiteDropActive && (
                <div className="pointer-events-none fixed inset-4 z-[300] flex items-center justify-center rounded-3xl border-2 border-dashed border-blue-500 bg-blue-500/10 backdrop-blur-sm">
                    <div className="rounded-2xl bg-white px-6 py-4 text-center shadow-xl">
                        <LucideIcon name="globe" size={24} className="mx-auto text-blue-600" />
                        <p className="mt-2 text-sm font-black text-slate-900">Drop link to save website</p>
                    </div>
                </div>
            )}

            {/* MOBILE SIDEBAR OVERLAY */}
            {isSidebarOpen && (
                <div className="fixed inset-0 z-[80] sm:hidden">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)}></div>
                    <aside className="relative w-72 h-full bg-white shadow-2xl flex flex-col animate-slide-in-left">
                        <div className="p-5 flex items-center justify-between border-b border-slate-50">
                            <div className="flex items-center gap-[5px]">
                                <LucideIcon name="bookmark" className="shrink-0 -translate-y-[1px]" style={{ color: theme !== 'light' ? '#fff' : '#000' }} size={45} strokeWidth={2.5} />
                                <div className="flex flex-col items-start">
                                    <h1 className="text-[28px] tracking-wide leading-[0.85] mb-1 whitespace-nowrap" style={{ fontFamily: '"Londrina Solid", sans-serif', fontWeight: 900, letterSpacing: '0.5px' }}>Bookmark Base</h1>
                                    <span className="text-[15px] text-slate-500 leading-none" style={{ fontFamily: '"Londrina Solid", sans-serif', fontWeight: 400, letterSpacing: '0.2px' }}>Save Your Feed</span>
                                </div>
                            </div>
                            <button onClick={() => setIsSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full"><LucideIcon name="x" className="text-slate-400" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto py-6 px-5 space-y-6 custom-scrollbar" onDragOver={(e) => { if (e.target === e.currentTarget) { setDragOverFolderId(null); setDragOverTagId(null); setFolderDropTarget(null); } }}>
                            <LibrarySwitch mobile />
                            {isWebsiteLibrary && (
                                <>
                                    <div className="space-y-0.5">
                                        <div onClick={() => { toggleWebsiteFilter('All'); setIsSidebarOpen(false); }} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${websiteActiveFilters.includes('All') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={websiteActiveFilters.includes('All') ? { backgroundColor: accentColor } : {}}>
                                            <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="globe" size={18} /> All Websites</button>
                                            <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{websiteBookmarks.length}</span></div>
                                        </div>
                                        <div onClick={() => { toggleWebsiteFilter('Unsorted'); setIsSidebarOpen(false); }} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${websiteActiveFilters.includes('Unsorted') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={websiteActiveFilters.includes('Unsorted') ? { backgroundColor: accentColor } : {}}>
                                            <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="inbox" size={18} /> Unsorted</button>
                                            <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{websiteUnsortedCount}</span></div>
                                        </div>
                                        {websitePinnedFolders.map(folder => <FolderItem key={folder.id} folder={folder} />)}
                                        {websitePinnedTags.map(tag => <WebsiteTagItem key={tag.id} tag={tag} />)}
                                    </div>
                                    <div>
                                        <div className="mb-2 flex items-center justify-between px-4">
                                            <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Folders</h2>
                                            <div className="flex items-center gap-1.5">
                                                <button onClick={() => { setEditingFolder(null); setFolderNameInput(''); setFolderColorInput('#3b82f6'); setIsFolderModalOpen(true); setIsSidebarOpen(false); }} className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:text-black"><LucideIcon name="plus" size={12} /></button>
                                                <button onClick={() => { toggleWebsiteFilter('AllFolders'); setIsSidebarOpen(false); }} className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${websiteActiveFilters.includes('AllFolders') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}><LucideIcon name="eye" size={12} /></button>
                                            </div>
                                        </div>
                                        <div className="space-y-0">{websiteTopLevelFolders.map(folder => <FolderItem key={folder.id} folder={folder} />)}</div>
                                    </div>
                                    <div>
                                        <div className="mb-2 flex items-center justify-between px-4">
                                            <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Tags</h2>
                                            <div className="flex items-center gap-1.5">
                                                <button onClick={() => { setEditingTag(null); setTagNameInput(''); setTagColorInput('#64748b'); setIsTagModalOpen(true); setIsSidebarOpen(false); }} className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:text-black"><LucideIcon name="plus" size={12} /></button>
                                                <button onClick={() => { toggleWebsiteFilter('AllTags'); setIsSidebarOpen(false); }} className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${websiteActiveFilters.includes('AllTags') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}><LucideIcon name="eye" size={12} /></button>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 px-2 mt-1">{websiteTags.filter(tag => !tag.isPinned).map(tag => <WebsiteTagItem key={tag.id} tag={tag} />)}</div>
                                    </div>
                                </>
                            )}
                            {!isWebsiteLibrary && (
                            <>
                            <div className="space-y-0.5">
                                <div onClick={() => { toggleFilter('All'); setIsSidebarOpen(false); }} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${activeFilters.includes('All') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={activeFilters.includes('All') ? { backgroundColor: accentColor } : {}}>
                                    <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="layers" size={18} /> All Bookmarks</button>
                                    <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{bookmarks.length}</span></div>
                                </div>
                                <div onClick={() => { toggleFilter('Unsorted'); setIsSidebarOpen(false); }} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${activeFilters.includes('Unsorted') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={activeFilters.includes('Unsorted') ? { backgroundColor: accentColor } : {}}>
                                    <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="inbox" size={18} /> Unsorted</button>
                                    <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{unsortedCount}</span></div>
                                </div>
                                {pinnedFolders.length > 0 && (
                                    <div className="space-y-0 mt-1">
                                        {pinnedFolders.map(f => <FolderItem key={f.id} folder={f} />)}
                                    </div>
                                )}
                                {pinnedTags.length > 0 && (
                                    <div className="space-y-0">
                                        {pinnedTags.map(tag => {
                                            const isActive = activeFilters.includes(`tag:${tag.name}`);
                                            const count = tagCounts[tag.name] || 0;
                                            return (
                                                <div
                                                    key={tag.id}
                                                    onClick={() => { toggleFilter(`tag:${tag.name}`); setIsSidebarOpen(false); }}
                                                    onDragEnter={(e) => e.preventDefault()}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                                    }}
                                                    onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                                    onDrop={(e) => handleTagDrop(e, tag.name)}
                                                    className={`px-4 flex items-center rounded-xl transition-colors cursor-pointer group ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`}
                                                    style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                                >
                                                    <button className="flex-1 flex items-center gap-2 py-0 text-[15px] font-bold text-left">
                                                        <span className="font-black text-[14px] w-5 flex justify-center shrink-0" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                        <span className="truncate uppercase tracking-wider text-[11px]">{tag.name}</span>
                                                    </button>
                                                    <button onClick={(e) => { e.stopPropagation(); setCustomTags(prev => prev.map(t => t.id === tag.id ? { ...t, isPinned: !t.isPinned } : t)); }} className={`w-5 h-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-colors text-blue-500 hover:text-slate-400 shrink-0`} title="Unpin tag"><LucideIcon name="pin" size={11} /></button>
                                                    <div className="relative flex items-center justify-center w-8 h-5 shrink-0">
                                                        <span className={`text-[11px] font-black group-hover:opacity-0 ${isActive ? 'text-white/70' : 'opacity-60'}`}>{count}</span>
                                                        <button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={11} /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div
                                    className={`flex justify-between items-center mb-1 px-4 rounded-lg transition-all ${dragOverFolderId === 'root' ? 'bg-blue-50 ring-2 ring-blue-400 ring-dashed py-1' : ''}`}
                                    onDragEnter={(e) => e.preventDefault()}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('root'); }}
                                    onDragLeave={() => { if (dragOverFolderId === 'root') clearDragState(); }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const data = dragItemRef.current;
                                        if (!data) return;
                                        if (data.type === 'folder') {
                                            setCustomFolders(prev => prev.map(f => f.id === data.id ? { ...f, parentId: null } : f));
                                        }
                                        clearDragState();
                                    }}
                                >
                                    <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Folders</h2>
                                    <div className="flex items-center gap-1.5 h-6">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); toggleFilter('AllFolders'); setIsSidebarOpen(false); }}
                                            className={`order-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${activeFilters.includes('AllFolders') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}
                                            title="All Folders"
                                        >
                                            <LucideIcon name="eye" size={12} />
                                        </button>
                                        <div className="order-1 flex items-center w-10 justify-center shrink-0 h-full">
                                            <button onClick={(e) => { e.stopPropagation(); setEditingFolder(null); setFolderNameInput(''); setFolderColorInput('#3b82f6'); setIsFolderModalOpen(true); }} className="w-6 h-6 bg-slate-100 rounded-lg flex items-center justify-center hover:text-black transition-colors"><LucideIcon name="plus" size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-0">
                                    {customFolders.filter(f => !f.parentId && !f.isPinned).sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => <FolderItem key={f.id} folder={f} />)}
                                </div>
                            </div>
                            <div>
                                <div className="group flex justify-between items-center mb-2 px-4 cursor-pointer tag-header transition-all py-1" onClick={() => setIsTagsExpanded(!isTagsExpanded)}>
                                    <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                        Tags
                                        <LucideIcon name={isTagsExpanded ? "chevron-down" : "chevron-right"} size={10} className="opacity-50" />
                                    </h2>
                                    <div className="flex items-center gap-1.5 h-6">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); toggleFilter('AllTags'); setIsSidebarOpen(false); }}
                                            className={`order-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${activeFilters.includes('AllTags') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}
                                            title="All Tags"
                                        >
                                            <LucideIcon name="eye" size={12} />
                                        </button>
                                        <div className="order-1 flex items-center w-10 justify-center shrink-0 h-full">
                                            <button onClick={(e) => { e.stopPropagation(); setEditingTag(null); setTagNameInput(''); setTagColorInput('#64748b'); setIsTagModalOpen(true); }} className="w-6 h-6 bg-slate-100 rounded-lg flex items-center justify-center hover:text-black transition-colors"><LucideIcon name="plus" size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                                {isTagsExpanded && (
                                    <div className="flex flex-wrap gap-1.5 px-2 mt-1">
                                        {customTags.filter(t => !t.isPinned).length > 0 ? [...customTags].filter(t => !t.isPinned).sort((a, b) => (tagCounts[b.name] || 0) - (tagCounts[a.name] || 0)).map(tag => {
                                            const isActive = activeFilters.includes(`tag:${tag.name}`);
                                            const count = tagCounts[tag.name] || 0;

                                            if (usePillTags) {
                                                return (
                                                    <div
                                                        key={tag.id}
                                                        onClick={() => { toggleFilter(`tag:${tag.name}`); setIsSidebarOpen(false); }}
                                                        onDragEnter={(e) => e.preventDefault()}
                                                        onDragOver={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                                        }}
                                                        onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                                        onDrop={(e) => handleTagDrop(e, tag.name)}
                                                        className={`h-7 flex items-center cursor-pointer rounded-lg transition-colors px-2.5 border ${isActive ? 'text-white shadow-sm border-transparent' : 'text-slate-500 bg-slate-50 border-slate-100 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 bg-blue-50/70' : ''}`}
                                                        style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                                    >
                                                        <span className="font-black mr-1.5 text-[11px] flex items-center" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                        <span className="text-[12px] font-bold flex items-center uppercase tracking-wider">{tag.name}</span>
                                                        <span className={`ml-2 text-[10px] font-black flex items-center h-full pt-[1px] ${isActive ? 'opacity-70' : 'opacity-30'}`}>{count}</span>
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div
                                                        key={tag.id}
                                                        onClick={(e) => { e.stopPropagation(); toggleFilter(`tag:${tag.name}`); setIsSidebarOpen(false); }}
                                                        onDragEnter={(e) => e.preventDefault()}
                                                        onDragOver={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                                        }}
                                                        onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                                        onDrop={(e) => handleTagDrop(e, tag.name)}
                                                        className={`px-4 flex items-center rounded-xl transition-colors cursor-pointer group ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`}
                                                        style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                                    >
                                                        <button className="flex-1 flex items-center gap-2 py-0 text-[15px] font-bold text-left">
                                                            <span className="font-black text-[14px] w-5 flex justify-center shrink-0" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                            <span className="truncate uppercase tracking-wider text-[11px]">{tag.name}</span>
                                                        </button>
                                                        <div className="flex items-center w-10 justify-center shrink-0 h-full">
                                                            <span className={`text-[11px] font-black ${isActive ? 'text-white/70' : 'opacity-60'}`}>{count}</span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }) : <div className="px-4 py-3 text-xs text-slate-400 italic">No tags yet</div>}
                                    </div>
                                )}
                            </div>
                            </>
                            )}
                        </div>
                        <div className="p-4 border-t border-slate-100 space-y-3">
                            {/* Cloud Sync Status - Mobile */}
                            <div className={`p-3 rounded-2xl border transition-all ${user ? 'bg-blue-50/50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}>
                                {user ? (
                                    <div className="flex items-center gap-3">
                                        {user.photoURL && <img src={user.photoURL} alt="User" className="w-8 h-8 rounded-full border border-white shadow-sm" />}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13px] font-bold text-slate-800 truncate">{user.displayName || 'User'}</p>
                                            <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest leading-none">{isSyncing ? 'Syncing...' : 'Online'}</p>
                                        </div>
                                        <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors" title="Logout">
                                            <LucideIcon name="log-out" size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <button onClick={handleLogin} className="w-full flex items-center justify-center gap-2 py-2 text-[13px] font-bold text-blue-600 hover:text-blue-700 transition-colors">
                                        <LucideIcon name="cloud" size={16} /> Cloud Login
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { isWebsiteLibrary ? toggleWebsiteFilter('Trash') : toggleFilter('Trash'); setIsSidebarOpen(false); }} className={`flex-1 flex items-center gap-2 px-3 py-2 text-[13px] font-bold rounded-xl ${(isWebsiteLibrary ? websiteActiveFilters.includes('Trash') : activeFilters.includes('Trash')) ? 'bg-red-50 text-red-500' : 'text-slate-400 hover:bg-slate-50'}`}><LucideIcon name="trash-2" size={18} /> Trash</button>
                                <button onClick={() => { setIsSettingsOpen(true); setIsSidebarOpen(false); }} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-slate-100 rounded-xl"><LucideIcon name="settings" size={18} /></button>
                            </div>
                        </div>
                    </aside>
                </div>
            )}

            <aside className="w-72 bg-white border-r border-slate-200 flex flex-col shrink-0 shadow-sm hidden sm:flex">
                <div className="p-6 border-b border-slate-50">
                    <div className="flex items-center gap-[5px]">
                    <LucideIcon name="bookmark" className="shrink-0 -translate-y-[1px]" style={{ color: theme !== 'light' ? '#fff' : '#000' }} size={45} strokeWidth={2.5} />
                    <div className="flex flex-col items-start">
                        <h1 className="text-[28px] tracking-wide leading-[0.85] mb-1 whitespace-nowrap" style={{ fontFamily: '"Londrina Solid", sans-serif', fontWeight: 900, letterSpacing: '0.5px' }}>Bookmark Base</h1>
                        <span className="text-[15px] text-slate-500 leading-none" style={{ fontFamily: '"Londrina Solid", sans-serif', fontWeight: 400, letterSpacing: '0.2px' }}>Save Your Feed</span>
                    </div>
                    </div>
                    <LibrarySwitch />
                </div>
                <div className="flex-1 overflow-y-auto py-6 px-5 space-y-6 custom-scrollbar" onDragOver={(e) => { if (e.target === e.currentTarget) { setDragOverFolderId(null); setDragOverTagId(null); setFolderDropTarget(null); } }}>
                    {isWebsiteLibrary && (
                        <>
                            <div className="space-y-0.5">
                                <div onClick={() => toggleWebsiteFilter('All')} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${websiteActiveFilters.includes('All') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={websiteActiveFilters.includes('All') ? { backgroundColor: accentColor } : {}}>
                                    <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="globe" size={18} /> All Websites</button>
                                    <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{websiteBookmarks.length}</span></div>
                                </div>
                                <div onClick={() => toggleWebsiteFilter('Unsorted')} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${websiteActiveFilters.includes('Unsorted') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={websiteActiveFilters.includes('Unsorted') ? { backgroundColor: accentColor } : {}}>
                                    <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="inbox" size={18} /> Unsorted</button>
                                    <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{websiteUnsortedCount}</span></div>
                                </div>
                                {websitePinnedFolders.map(folder => (
                                    <FolderItem key={folder.id} folder={folder} />
                                ))}
                                {websitePinnedTags.length > 0 && <div className="space-y-0">{websitePinnedTags.map(tag => <WebsiteTagItem key={tag.id} tag={tag} />)}</div>}
                            </div>
                            <div>
                                <div className="mb-2 flex items-center justify-between px-4">
                                    <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Folders</h2>
                                    <div className="flex items-center gap-1.5">
                                        <button onClick={() => { setEditingFolder(null); setFolderNameInput(''); setFolderColorInput('#3b82f6'); setIsFolderModalOpen(true); }} className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:text-black"><LucideIcon name="plus" size={12} /></button>
                                        <button onClick={() => toggleWebsiteFilter('AllFolders')} className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${websiteActiveFilters.includes('AllFolders') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}><LucideIcon name="eye" size={12} /></button>
                                    </div>
                                </div>
                                <div className="space-y-0">{websiteTopLevelFolders.map(folder => <FolderItem key={folder.id} folder={folder} />)}</div>
                            </div>
                            <div>
                                <div className="mb-2 flex items-center justify-between px-4">
                                    <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Tags</h2>
                                    <div className="flex items-center gap-1.5">
                                        <button onClick={() => { setEditingTag(null); setTagNameInput(''); setTagColorInput('#64748b'); setIsTagModalOpen(true); }} className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:text-black"><LucideIcon name="plus" size={12} /></button>
                                        <button onClick={() => toggleWebsiteFilter('AllTags')} className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${websiteActiveFilters.includes('AllTags') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}><LucideIcon name="eye" size={12} /></button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 px-2 mt-1">{websiteTags.filter(tag => !tag.isPinned).map(tag => <WebsiteTagItem key={tag.id} tag={tag} />)}</div>
                            </div>
                        </>
                    )}
                    {!isWebsiteLibrary && (
                    <>
                    <div className="space-y-0.5">
                        <div onClick={() => toggleFilter('All')} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${activeFilters.includes('All') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={activeFilters.includes('All') ? { backgroundColor: accentColor } : {}}>
                            <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="layers" size={18} /> All Bookmarks</button>
                            <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{bookmarks.length}</span></div>
                        </div>
                        <div onClick={() => toggleFilter('Unsorted')} className={`px-4 flex items-center rounded-xl transition-all cursor-pointer ${activeFilters.includes('Unsorted') ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} style={activeFilters.includes('Unsorted') ? { backgroundColor: accentColor } : {}}>
                            <button className="flex-1 flex items-center gap-3 py-2 text-[15px] font-bold text-left"><LucideIcon name="inbox" size={18} /> Unsorted</button>
                            <div className="flex items-center w-10 justify-center shrink-0"><span className="text-[11px] font-black opacity-60">{unsortedCount}</span></div>
                        </div>
                        {pinnedFolders.length > 0 && (
                            <div className="space-y-0 mt-1">
                                {pinnedFolders.map(f => <FolderItem key={f.id} folder={f} />)}
                            </div>
                        )}
                        {pinnedTags.length > 0 && (
                            <div className="space-y-0">
                                {pinnedTags.map(tag => {
                                    const isActive = activeFilters.includes(`tag:${tag.name}`);
                                    const count = tagCounts[tag.name] || 0;
                                    return (
                                        <div
                                            key={tag.id}
                                            onClick={() => toggleFilter(`tag:${tag.name}`)}
                                            onDragEnter={(e) => e.preventDefault()}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                            }}
                                            onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                            onDrop={(e) => handleTagDrop(e, tag.name)}
                                            className={`px-4 flex items-center rounded-xl transition-colors cursor-pointer group ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`}
                                            style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                        >
                                            <button className="flex-1 flex items-center gap-2 py-0 text-[15px] font-bold text-left">
                                                <span className="font-black text-[14px] w-5 flex justify-center shrink-0" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                <span className="truncate uppercase tracking-wider text-[11px]">{tag.name}</span>
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); setCustomTags(prev => prev.map(t => t.id === tag.id ? { ...t, isPinned: !t.isPinned } : t)); }} className={`w-5 h-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-colors text-blue-500 hover:text-slate-400 shrink-0`} title="Unpin tag"><LucideIcon name="pin" size={11} /></button>
                                            <div className="relative flex items-center justify-center w-8 h-5 shrink-0">
                                                <span className={`text-[11px] font-black group-hover:opacity-0 ${isActive ? 'text-white/70' : 'opacity-60'}`}>{count}</span>
                                                <button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={11} /></button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div>
                        <div
                            className={`flex justify-between items-center mb-1 px-4 rounded-lg transition-all ${dragOverFolderId === 'root' ? 'bg-blue-50 ring-2 ring-blue-400 ring-dashed py-1' : ''}`}
                            onDragEnter={(e) => e.preventDefault()}
                            onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('root'); }}
                            onDragLeave={() => { if (dragOverFolderId === 'root') clearDragState(); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const data = dragItemRef.current;
                                if (!data) return;
                                if (data.type === 'folder') {
                                    setCustomFolders(prev => prev.map(f => f.id === data.id ? { ...f, parentId: null } : f));
                                }
                                clearDragState();
                            }}
                        >
                            <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Folders</h2>
                            <div className="flex items-center gap-1.5 h-6">
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleFilter('AllFolders'); }}
                                    className={`order-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${activeFilters.includes('AllFolders') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}
                                    title="All Folders"
                                >
                                    <LucideIcon name="eye" size={12} />
                                </button>
                                <div className="order-1 flex items-center w-10 justify-center shrink-0 h-full">
                                    <button onClick={() => { setEditingFolder(null); setFolderNameInput(''); setFolderColorInput('#3b82f6'); setIsFolderModalOpen(true); }} className="w-6 h-6 bg-slate-100 rounded-lg flex items-center justify-center hover:text-black transition-colors"><LucideIcon name="plus" size={12} /></button>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-0">{topLevelFolders.map(f => <FolderItem key={f.id} folder={f} />)}</div>
                    </div>
                    <div>
                        <div className="group flex justify-between items-center mb-2 px-4 cursor-pointer tag-header transition-all py-1" onClick={() => setIsTagsExpanded(!isTagsExpanded)}>
                            <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                Tags
                                <LucideIcon name={isTagsExpanded ? "chevron-down" : "chevron-right"} size={10} className="opacity-50" />
                            </h2>
                            <div className="flex items-center gap-1.5 h-6">
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleFilter('AllTags'); }}
                                    className={`order-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${activeFilters.includes('AllTags') ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-400 hover:text-black'}`}
                                    title="All Tags"
                                >
                                    <LucideIcon name="eye" size={12} />
                                </button>
                                <div className="order-1 flex items-center w-10 justify-center shrink-0 h-full">
                                    <button onClick={(e) => { e.stopPropagation(); setEditingTag(null); setTagNameInput(''); setTagColorInput('#64748b'); setIsTagModalOpen(true); }} className="w-6 h-6 bg-slate-100 rounded-lg flex items-center justify-center hover:text-black transition-colors"><LucideIcon name="plus" size={12} /></button>
                                </div>
                            </div>
                        </div>
                        {isTagsExpanded && (
                            <div className="flex flex-wrap gap-1.5 px-2 mt-1">
                                {customTags.filter(t => !t.isPinned).length > 0 ? [...customTags].filter(t => !t.isPinned).sort((a, b) => (tagCounts[b.name] || 0) - (tagCounts[a.name] || 0)).map(tag => {
                                    const isActive = activeFilters.includes(`tag:${tag.name}`);
                                    const count = tagCounts[tag.name] || 0;

                                    if (usePillTags) {
                                        return (
                                            <div
                                                key={tag.id}
                                                onClick={() => toggleFilter(`tag:${tag.name}`)}
                                                onDragEnter={(e) => e.preventDefault()}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                                }}
                                                onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                                onDrop={(e) => handleTagDrop(e, tag.name)}
                                                className={`group h-8 flex items-center rounded-lg transition-colors cursor-pointer px-3 py-0.5 border ${isActive ? 'text-white shadow-sm border-transparent' : 'text-slate-600 bg-slate-50 border-slate-100 hover:border-slate-200 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 bg-blue-50/70' : ''}`}
                                                style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                            >
                                                <span className="font-black text-[12px] mr-1.5 shrink-0 flex items-center" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                <span className="text-[11px] font-bold uppercase tracking-wider truncate max-w-[140px] flex items-center">{tag.name}</span>
                                                <div className="relative flex items-center justify-center ml-2 min-w-[28px] h-full">
                                                    <span className={`text-[11px] font-black flex items-center pt-[1px] group-hover:opacity-0 ${isActive ? 'opacity-70' : 'opacity-30'}`}>{count}</span>
                                                    <div className="absolute inset-0 flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100">
                                                        <button onClick={(e) => { e.stopPropagation(); setCustomTags(prev => prev.map(t => t.id === tag.id ? { ...t, isPinned: true } : t)); }} className={`w-4 h-4 inline-flex items-center justify-center hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`} title="Pin tag"><LucideIcon name="pin" size={10} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`w-4 h-4 inline-flex items-center justify-center hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={10} /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div
                                                key={tag.id}
                                                onClick={(e) => { e.stopPropagation(); toggleFilter(`tag:${tag.name}`); }}
                                                onDragEnter={(e) => e.preventDefault()}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (dragItemRef.current?.type === 'tweet') setDragOverTagId(tag.id);
                                                }}
                                                onDragLeave={() => { if (dragOverTagId === tag.id) setDragOverTagId(null); }}
                                                onDrop={(e) => handleTagDrop(e, tag.name)}
                                                className={`px-4 flex items-center rounded-xl transition-colors cursor-pointer group ${isActive ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${dragOverTagId === tag.id ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/70' : ''}`}
                                                style={isActive && dragOverTagId !== tag.id ? { backgroundColor: accentColor } : {}}
                                            >
                                                <button className="flex-1 flex items-center gap-2 py-0 text-[15px] font-bold text-left">
                                                    <span className="font-black text-[14px] w-5 flex justify-center shrink-0" style={{ color: isActive ? '#fff' : tag.color }}>#</span>
                                                    <span className="truncate uppercase tracking-wider text-[11px]">{tag.name}</span>
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); setCustomTags(prev => prev.map(t => t.id === tag.id ? { ...t, isPinned: true } : t)); }} className={`w-5 h-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} title="Pin tag"><LucideIcon name="pin" size={11} /></button>
                                                <div className="relative flex items-center justify-center w-8 h-5 shrink-0">
                                                    <span className={`text-[11px] font-black group-hover:opacity-0 ${isActive ? 'text-white/70' : 'opacity-60'}`}>{count}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); setEditingTag(tag); setTagNameInput(tag.name); setTagColorInput(tag.color); setIsTagModalOpen(true); }} className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-blue-500 ${isActive ? 'text-white' : 'text-slate-400'}`}><LucideIcon name="pen" size={11} /></button>
                                                </div>
                                            </div>
                                        );
                                    }
                                }) : <div className="px-3 py-2 text-[11px] text-slate-400 italic">No tags yet</div>}
                            </div>
                        )}
                    </div>
                    </>
                    )}
                </div>
                <div className="p-4 border-t border-slate-100 space-y-3">
                    {/* Cloud Sync Status - Desktop */}
                    <div className={`p-3 rounded-2xl border transition-all ${user ? 'bg-blue-50/50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}>
                        {user ? (
                            <div className="flex items-center gap-3">
                                {user.photoURL && <img src={user.photoURL} alt="User" className="w-8 h-8 rounded-full border border-white shadow-sm" />}
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] font-bold text-slate-800 truncate">{user.displayName || 'User'}</p>
                                    <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest leading-none">{isSyncing ? 'Syncing...' : 'Online'}</p>
                                </div>
                                <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors" title="Logout">
                                    <LucideIcon name="log-out" size={14} />
                                </button>
                            </div>
                        ) : (
                            <button onClick={handleLogin} className="w-full flex items-center justify-center gap-2 py-2 text-[13px] font-bold text-blue-600 hover:text-blue-700 transition-colors">
                                <LucideIcon name="cloud" size={16} /> Cloud Sync Login
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleExportJSON} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[12px] font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all">
                            <LucideIcon name="download" className="text-[14px]" /> Save
                        </button>
                        <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[12px] font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all cursor-pointer">
                            <LucideIcon name="upload" className="text-[14px]" /> Load
                            <input type="file" accept=".json" className="hidden" onChange={handleImportJSON} />
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => { isWebsiteLibrary ? toggleWebsiteFilter('Trash') : toggleFilter('Trash'); }} className={`flex-1 flex items-center gap-3 px-3 py-2 rounded-xl text-[15px] font-medium transition-all ${(isWebsiteLibrary ? websiteActiveFilters.includes('Trash') : activeFilters.includes('Trash')) ? 'bg-red-50 text-red-600' : 'text-slate-500 hover:bg-red-50'}`}><LucideIcon name="trash-2" size={18} /> Trash</button>
                        <button onClick={() => setIsSettingsOpen(true)} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all shrink-0"><LucideIcon name="settings" size={18} className="text-sm" /></button>
                    </div>
                </div>
            </aside>

            <main className="flex-1 flex flex-col h-screen min-w-0 bg-slate-50/50">
                <header className="h-16 sm:h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 z-40 shrink-0">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                        <button className="sm:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg shrink-0" onClick={() => setIsSidebarOpen(true)}><LucideIcon name="menu" /></button>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            {currentActiveFilters.includes('All') || currentActiveFilters.includes('AllTags') || currentActiveFilters.includes('Trash') || currentActiveFilters.includes('Unsorted') || currentActiveFilters.includes('AllFolders') ? (
                                <h2 className="text-base sm:text-lg font-bold text-slate-900 capitalize truncate">
                                    {currentActiveFilters.includes('AllTags') ? (isWebsiteLibrary ? 'Website Tags' : 'All Tags') :
                                        currentActiveFilters.includes('Trash') ? 'Trash' :
                                            currentActiveFilters.includes('Unsorted') ? 'Unsorted' :
                                                currentActiveFilters.includes('AllFolders') ? 'Folders' :
                                                    (isWebsiteLibrary ? 'All Websites' : 'All Bookmarks')}
                                </h2>
                            ) : (
                                currentActiveFilters.map(filter => (
                                    <div key={filter} className="group flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-sm font-bold cursor-pointer hover:bg-red-50 hover:text-red-500 transition-all border border-transparent hover:border-red-200" onClick={() => isWebsiteLibrary ? toggleWebsiteFilter(filter, true) : toggleFilter(filter, true)}>
                                        {filter.startsWith('tag:') ? (
                                            <>
                                                <span className="font-black" style={{ color: (isWebsiteLibrary ? websiteTags : customTags).find(t => t.name === filter.split(':')[1])?.color || '#64748b' }}>#</span>
                                                <span>{filter.split(':')[1]}</span>
                                            </>
                                        ) : filter === 'Unsorted' ? (
                                            <>
                                                <LucideIcon name="inbox" size={12} className="text-slate-400" />
                                                <span>Unsorted</span>
                                            </>
                                        ) : (
                                            <>
                                                <LucideIcon name="folder" size={13} style={{ color: (isWebsiteLibrary ? websiteFolders : customFolders).find(f => f.name === filter)?.color || '#94a3b8' }} />
                                                <span>{filter}</span>
                                            </>
                                        )}
                                        <LucideIcon name="x" className="ml-1 opacity-0 group-hover:opacity-100 w-3 h-3" />
                                    </div>
                                ))
                            )}

                            {!isWebsiteLibrary &&
                                !activeFilters.includes('Trash') &&
                                !activeFilters.includes('AllTags') &&
                                !activeFilters.includes('AllFolders') &&
                                !activeFilters.includes('All') &&
                                !activeFilters.includes('Unsorted') && (
                                    <div className="relative dropdown-container">
                                        <button onClick={(e) => { e.stopPropagation(); setActiveAddMenu(activeAddMenu === 'headerFilter' ? null : 'headerFilter'); }} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all shadow-sm shrink-0"><LucideIcon name="plus" size={14} /></button>
                                        {activeAddMenu === 'headerFilter' && (
                                            <div className="absolute top-full left-0 mt-3 w-[420px] bg-white rounded-2xl shadow-2xl border border-slate-100 z-[100] p-4 flex gap-6" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex-1 max-h-72 overflow-y-auto custom-scrollbar pr-1">
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-3 tracking-widest">Folders</div>
                                                    <div className="space-y-0.5">

                                                        {customFolders.filter(f => !activeFilters.includes(f.name)).map(f => (
                                                            <div key={f.name} onClick={(e) => { e.stopPropagation(); toggleFilter(f.name, true); setActiveAddMenu(null); }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer text-sm font-bold text-slate-700 transition-colors">
                                                                <LucideIcon name="folder" size={14} style={{ color: f.color }} />
                                                                {f.name}
                                                            </div>
                                                        ))}
                                                        {customFolders.filter(f => !activeFilters.includes(f.name)).length === 0 && activeFilters.includes('All') && activeFilters.includes('Unsorted') && (
                                                            <div className="px-3 py-2 text-xs text-slate-400 italic">No more folders</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="w-px bg-slate-100 self-stretch"></div>

                                                <div className="flex-1 max-h-72 overflow-y-auto custom-scrollbar pl-1">
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-3 tracking-widest">Tags</div>
                                                    <div className="space-y-0.5">
                                                        {customTags.filter(t => !activeFilters.includes(`tag:${t.name}`)).length > 0 ? (
                                                            customTags
                                                                .filter(t => !activeFilters.includes(`tag:${t.name}`))
                                                                .sort((a, b) => (tagCounts[b.name] || 0) - (tagCounts[a.name] || 0))
                                                                .map(t => (
                                                                    <div key={t.id} onClick={(e) => { e.stopPropagation(); toggleFilter(`tag:${t.name}`, true); setActiveAddMenu(null); }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer text-sm font-bold text-slate-700 transition-colors">
                                                                        <span className="font-black" style={{ color: t.color }}>#</span>
                                                                        {t.name}
                                                                    </div>
                                                                ))
                                                        ) : (
                                                            <div className="px-3 py-2 text-xs text-slate-400 italic">No more tags</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="relative hidden md:block group">
                            <LucideIcon name="search" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={14} />
                            <input
                                type="text"
                                placeholder={isWebsiteLibrary ? "Search websites..." : "Search..."}
                                value={currentSearchQuery}
                                onChange={e => isWebsiteLibrary ? setWebsiteSearchQuery(e.target.value) : setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 h-10 bg-slate-100/50 border border-transparent focus:border-blue-100 focus:bg-white rounded-xl text-sm w-44 focus:w-64 outline-none transition-all font-medium"
                            />
                        </div>

                        <div className="h-10 w-[1px] bg-slate-100 hidden md:block mx-1"></div>

                        {false && isWebsiteLibrary && (
                            <button
                                onClick={() => {
                                    setWebsiteViewModes(prev => {
                                        const currentMode = prev[currentWebsiteViewScope] || prev.All || DEFAULT_WEBSITE_VIEW_MODE;
                                        return {
                                            ...prev,
                                            [currentWebsiteViewScope]: currentMode === 'grid' ? 'list' : 'grid'
                                        };
                                    });
                                    setIsGridMenuOpen(false);
                                }}
                                className="h-10 flex items-center gap-2 bg-slate-100/50 border border-transparent hover:border-slate-200 px-3.5 rounded-xl text-xs font-bold text-slate-600 transition-all active:scale-95"
                                title={isWebsiteListMode ? 'Switch to card view' : 'Switch to list view'}
                            >
                                <LucideIcon name={isWebsiteListMode ? 'grid' : 'list'} size={14} />
                                <span className="hidden sm:inline">{isWebsiteListMode ? 'Cards' : 'List'}</span>
                            </button>
                        )}

                        {false && isWebsiteLibrary && (
                            <button
                                onClick={() => setWebsiteNotesVisible(prev => !prev)}
                                className={`h-10 flex items-center gap-2 border px-3.5 rounded-xl text-xs font-bold transition-all active:scale-95 ${websiteNotesVisible ? 'bg-slate-100/60 border-slate-200 text-slate-600 hover:border-slate-300' : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'}`}
                                title={websiteNotesVisible ? 'Hide website notes' : 'Show website notes'}
                            >
                                <LucideIcon name="eye" size={14} className={websiteNotesVisible ? '' : 'opacity-50'} />
                                <span className="hidden sm:inline">Notes</span>
                            </button>
                        )}

                        {isWebsiteLibrary && (
                            <button
                                onClick={() => setWebsiteListDensity((current) => current === '1x' ? '2x' : '1x')}
                                className="h-10 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 text-xs font-bold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-95"
                                title="Toggle website list density"
                            >
                                <LucideIcon name="list" size={14} />
                                <span>{websiteListDensity}</span>
                            </button>
                        )}

                        {!isWebsiteLibrary && (<div className="relative" tabIndex="0" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsGridMenuOpen(false); }}>
                            <button
                                onClick={() => {
                                    if (isWebsiteLibrary && isWebsiteListMode) return;
                                    setIsGridMenuOpen(!isGridMenuOpen);
                                }}
                                className={`h-10 flex items-center gap-2 bg-slate-100/50 border border-transparent px-3.5 rounded-xl text-xs font-bold text-slate-600 transition-all active:scale-95 ${(isWebsiteLibrary && isWebsiteListMode) ? 'cursor-not-allowed opacity-50' : 'hover:border-slate-200'}`}
                            >
                                <LucideIcon name="columns" size={14} />
                                <span className="hidden sm:inline">{isWebsiteLibrary ? (isWebsiteListMode ? 'Grid Size' : `${websiteResponsiveCols} View`) : `${currentGridCols} View`}</span>
                            </button>
                            {isGridMenuOpen && (!isWebsiteLibrary || !isWebsiteListMode) && (
                                <div className="absolute right-0 top-full mt-2 w-32 bg-white border border-slate-100 shadow-xl rounded-xl overflow-hidden z-[60]">
                                    {(isWebsiteLibrary ? [4, 5, 6, 7, 8] : [1, 2, 3, 4, 5]).map(n => (
                                        <button
                                            key={n}
                                            onClick={() => {
                                                if (isWebsiteLibrary) {
                                                    setWebsiteGridCols(n);
                                                } else {
                                                    setPostGridCols(n);
                                                }
                                                setIsGridMenuOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-2.5 text-xs font-bold ${currentGridCols === n ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            {n} {n === 1 ? 'Column' : 'Columns'}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>)}

                        {((!isWebsiteLibrary && activeFilters.includes('Trash')) || (isWebsiteLibrary && websiteActiveFilters.includes('Trash'))) ? (
                            <button
                                onClick={isWebsiteLibrary ? handleClearWebsiteTrash : handleClearTrash}
                                className="h-10 flex items-center gap-2 px-5 bg-red-500 text-white rounded-xl text-xs font-black shadow-sm hover:bg-red-600 hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95"
                            >
                                <LucideIcon name="trash-2" size={16} />
                                <span>CLEAR ALL</span>
                            </button>
                        ) : (
                            <button
                                onClick={() => isWebsiteLibrary ? setIsWebsiteModalOpen(true) : setIsModalOpen(true)}
                                className="w-10 h-10 flex items-center justify-center rounded-xl text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 shrink-0"
                                style={{ backgroundColor: accentColor }}
                                title={isWebsiteLibrary ? "Website prototype" : "Add New Bookmark"}
                            >
                                <LucideIcon name="plus" size={18} />
                            </button>
                        )}
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-8 custom-scrollbar">
                    {isWebsiteLibrary ? (
                        <div className="mx-auto space-y-6" style={{ maxWidth: websiteUsesListWidth ? 'min(100%, 1120px)' : `min(100%, ${websiteLayoutWidth})` }}>
                            {false && <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                                    <div className="max-w-2xl">
                                        <p className="mb-2 text-[11px] font-black uppercase tracking-[0.28em] text-amber-600">Website Library</p>
                                        <h3 className="text-2xl font-black text-slate-900">Website bookmarks run as a separate library inside the same product.</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-slate-600">Bu alan post arşivinden bağımsız çalışır. Website klasörleri, website tag’leri, arama ve trash akışı kendi namespace’i içinde kalır.</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Websites</p>
                                            <p className="mt-1 text-2xl font-black text-slate-900">{websiteBookmarks.length}</p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Folders</p>
                                            <p className="mt-1 text-2xl font-black text-slate-900">{websiteFolders.length}</p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tags</p>
                                            <p className="mt-1 text-2xl font-black text-slate-900">{websiteTags.length}</p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Trash</p>
                                            <p className="mt-1 text-2xl font-black text-slate-900">{websiteTrash.length}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>}

                            {websiteActiveFilters.includes('Trash') ? (
                                isWebsiteListMode ? (
                                    <div className="flex flex-col gap-1.5">
                                        {visibleWebsiteBookmarks.map(site => <WebsiteListRow key={site.id} site={site} isInTrash={websiteActiveFilters.includes('Trash')} isCompact={websiteListDensity === '2x'} isSelected={selectedWebsiteIds.includes(site.id)} showSelectionControls={selectedWebsiteIds.length > 0} onToggleSelection={toggleWebsiteSelection} folderColorByName={websiteFolderColorByName} tagColorByName={websiteTagColorByName} onSelect={handleWebsiteRowSelect} onStartDrag={handleWebsiteRowDragStart} onEndDrag={clearDragState} onFilter={toggleWebsiteFilter} onRestore={handleRestoreWebsiteFromTrash} onDelete={handlePermanentDeleteWebsite} onMoveToTrash={handleMoveWebsiteToTrash} />)}
                                    </div>
                                ) : (
                                    <div className="grid justify-center gap-4" style={websiteGridStyle}>
                                        {visibleWebsiteBookmarks.map(site => <WebsiteCard key={site.id} site={site} />)}
                                    </div>
                                )
                            ) : websiteActiveFilters.includes('AllFolders') ? (
                                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                                    <div className="mb-5 flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><LucideIcon name="folder" size={20} /></div>
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Website Folders</h3>
                                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Ayrı klasör sistemi</p>
                                        </div>
                                    </div>
                                    <div className="space-y-0 rounded-xl border border-slate-100 bg-slate-50/60 p-2">
                                        {websiteFolders.filter(folder => !folder.parentId).sort((a, b) => (a.order || 0) - (b.order || 0)).map(folder => <FolderItem key={folder.id} folder={folder} />)}
                                        {websiteFolders.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">No folders yet.</p>}
                                    </div>
                                </div>
                            ) : websiteActiveFilters.includes('AllTags') ? (
                                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                                    <div className="mb-5 flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-600"><LucideIcon name="tags" size={20} /></div>
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Website Tags</h3>
                                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Post taglerinden bağımsız</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2.5">
                                        {websiteTags.map(tag => (
                                            <button key={tag.id} onClick={() => toggleWebsiteFilter(`tag:${tag.name}`)} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-700 transition-all hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-md">
                                                <span className="text-[16px] font-black" style={{ color: tag.color }}>#</span>
                                                <span className="text-[12px] font-bold uppercase tracking-wider">{tag.name}</span>
                                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-400">{websiteTagCounts[tag.name] || 0}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                isWebsiteListMode ? (
                                    shouldGroupWebsiteRows ? (
                                        <div className="space-y-5">
                                            {visibleWebsiteFolderGroups.map(({ folder, sites }) => (
                                                <section key={folder.id} className="space-y-1.5">
                                                    <div className="flex items-center gap-2.5 px-1.5 py-1">
                                                        {folder.id === 'unsorted' ? <LucideIcon name="inbox" size={18} className="text-slate-400" /> : <LucideIcon name="folder" size={18} style={{ color: folder.color || '#94a3b8' }} />}
                                                        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">{folder.name}</h3>
                                                        <span className="text-xs font-bold text-slate-400">{sites.length}</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        {sites.map(site => <WebsiteListRow key={site.id} site={site} isInTrash={false} isCompact={websiteListDensity === '2x'} isSelected={selectedWebsiteIds.includes(site.id)} showSelectionControls={selectedWebsiteIds.length > 0} onToggleSelection={toggleWebsiteSelection} showFolder={websiteFolderShowsOwnLabelByName[normalizeFolder(site.folder)] === true} folderColorByName={websiteFolderColorByName} tagColorByName={websiteTagColorByName} onSelect={handleWebsiteRowSelect} onStartDrag={handleWebsiteRowDragStart} onEndDrag={clearDragState} onFilter={toggleWebsiteFilter} onRestore={handleRestoreWebsiteFromTrash} onDelete={handlePermanentDeleteWebsite} onMoveToTrash={handleMoveWebsiteToTrash} />)}
                                                    </div>
                                                </section>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-1.5">
                                            {visibleWebsiteBookmarks.map(site => <WebsiteListRow key={site.id} site={site} isInTrash={websiteActiveFilters.includes('Trash')} isCompact={websiteListDensity === '2x'} isSelected={selectedWebsiteIds.includes(site.id)} showSelectionControls={selectedWebsiteIds.length > 0} onToggleSelection={toggleWebsiteSelection} folderColorByName={websiteFolderColorByName} tagColorByName={websiteTagColorByName} onSelect={handleWebsiteRowSelect} onStartDrag={handleWebsiteRowDragStart} onEndDrag={clearDragState} onFilter={toggleWebsiteFilter} onRestore={handleRestoreWebsiteFromTrash} onDelete={handlePermanentDeleteWebsite} onMoveToTrash={handleMoveWebsiteToTrash} />)}
                                        </div>
                                    )
                                ) : (
                                    <div className="grid justify-center gap-4" style={websiteGridStyle}>
                                        {visibleWebsiteBookmarks.map(site => <WebsiteCard key={site.id} site={site} />)}
                                    </div>
                                )
                            )}

                            {filteredWebsiteBookmarks.length === 0 && !websiteActiveFilters.includes('AllFolders') && !websiteActiveFilters.includes('AllTags') && (
                                <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                    <LucideIcon name="globe" size={18} className="text-4xl mb-4" />
                                    <p className="text-sm font-bold uppercase tracking-widest">No Websites Found</p>
                                </div>
                            )}
                            {filteredWebsiteBookmarks.length > visibleCount && !websiteActiveFilters.includes('AllFolders') && !websiteActiveFilters.includes('AllTags') && (
                                <div ref={observerTarget} className="h-10 w-full" />
                            )}
            </div>
          ) : activeFilters.includes('AllFolders') ? (
                        <div className="mx-auto max-w-5xl">
                            {/* Thin Hybrid Header */}
                            <div className="mb-6 p-4 bg-white border border-slate-200 rounded-[1.25rem] shadow-sm flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                                    <LucideIcon name="folder" className="text-blue-500" size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-black text-slate-900 leading-tight">Folder Archive</h3>
                                    <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">{customFolders.length} curated categories</p>
                                </div>
                            </div>

                            {customFolders.length > 0 ? (
                                <div className="flex flex-wrap gap-2.5">
                                    {[...customFolders].sort((a, b) => (a.order || 0) - (b.order || 0)).map(folder => {
                                        const count = getFolderCount(folder.id);

                                        // Find root ancestor's color for inheritance
                                        let rootFolder = folder;
                                        let safetyCounter = 0;
                                        while (rootFolder.parentId && safetyCounter < 10) {
                                            const parent = customFolders.find(f => f.id === rootFolder.parentId);
                                            if (!parent) break;
                                            rootFolder = parent;
                                            safetyCounter++;
                                        }
                                        const displayColor = rootFolder.color || '#3b82f6';

                                        return (
                                            <button
                                                key={folder.id}
                                                onClick={() => toggleFilter(folder.name)}
                                                className="group flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all active:scale-95 border-2 bg-white text-slate-700 hover:shadow-md hover:-translate-y-0.5"
                                                style={{ borderColor: displayColor + '40' }}
                                            >
                                                <LucideIcon name="folder" size={16} style={{ color: displayColor }} />
                                                <span className="text-[14px] font-bold">{folder.name}</span>
                                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                    <LucideIcon name="folder" className="text-4xl mb-4" />
                                    <p className="text-sm font-bold uppercase tracking-widest">No Folders Created</p>
                                </div>
                            )}
                        </div>
                    ) : activeFilters.includes('AllTags') ? (
                        <div className="mx-auto max-w-5xl">
                            {/* Thin Hybrid Header */}
                            <div className="mb-6 p-4 bg-white border border-slate-200 rounded-[1.25rem] shadow-sm flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                                    <LucideIcon name="tags" className="text-slate-500" size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-black text-slate-900 leading-tight">Tag Collection</h3>
                                    <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">{customTags.length} registered tags</p>
                                </div>
                            </div>

                            {customTags.length > 0 ? (
                                <div className="flex flex-wrap gap-2.5">
                                    {customTags.sort((a, b) => (tagCounts[b.name] || 0) - (tagCounts[a.name] || 0)).map(tag => {
                                        const count = tagCounts[tag.name] || 0;
                                        return (
                                            <button
                                                key={tag.id}
                                                onClick={() => toggleFilter(`tag:${tag.name}`)}
                                                className="group flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all active:scale-95 border bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5"
                                            >
                                                <span className="font-black text-[16px]" style={{ color: tag.color }}>#</span>
                                                <span className="text-[12px] font-bold uppercase tracking-wider">{tag.name}</span>
                                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                    <LucideIcon name="tags" className="text-4xl mb-4" />
                                    <p className="text-sm font-bold uppercase tracking-widest">No Tags Found</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className={`mx-auto ${gridConfig.padding}`}>
                                <div className="flex gap-3 sm:gap-6 items-start justify-center">
                                    {bookmarkColumns.map((col, colIdx) => (
                                        <div key={colIdx} className="flex-1 flex flex-col gap-3 sm:gap-6 min-w-0">
                                            {col.map(b => (
                                                <div key={b.id} draggable onDragStart={(e) => { e.stopPropagation(); dragItemRef.current = { type: 'tweet', ids: [b.id] }; }} onClick={() => { if (!activeFilters.includes('Trash')) { setFocusedTweet(b); setInitialFocusedTweet(b); setIsNoteEditing(false); } }} className={`group bg-white rounded-[1.25rem] sm:rounded-[1.5rem] border ${showBrandLines && brandLineStyle === 'border' ? (b.url && b.url.includes('reddit.com') ? 'border-[#ff4500]' : 'border-[#1da1f2]') : 'border-slate-200'} shadow-sm overflow-hidden relative w-full transition-all duration-300 ${activeFilters.includes('Trash') ? 'opacity-70' : ''} hover:border-slate-400 p-3 sm:p-4`}>
                                                    <div className="w-full">
                                                        <CustomTweetCard
                                                            bookmark={b}
                                                            onImageClick={handleImageClick}
                                                            showFullRedditContent={!(b.url && b.url.includes('reddit.com'))}
                                                        />
                                                    </div>

                                                    <div className="mt-4 space-y-3">
                                                        {b.description && <div className="bg-slate-50/50 border border-slate-100 p-3 rounded-2xl"><p className="text-[13px] font-medium text-slate-700 leading-relaxed line-clamp-3 break-words">{b.description}</p></div>}

                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleFilter(getFolderLabel(b.folder));
                                                                    }}
                                                                    className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-bold uppercase tracking-wider whitespace-nowrap hover:bg-slate-200 transition-colors"
                                                                >
                                                                    <LucideIcon name="folder" className="mr-1" size={12} style={{ color: customFolders.find(f => f.name === normalizeFolder(b.folder))?.color || '#94a3b8' }} /> {getFolderLabel(b.folder)}
                                                                </button>
                                                                {(b.tags || []).map(tag => {
                                                                    const tO = customTags.find(t => t.name === tag);
                                                                    return (
                                                                        <button
                                                                            key={tag}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFilter(`tag:${tag}`);
                                                                            }}
                                                                            className="flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 text-slate-500 rounded-lg text-[10px] font-bold uppercase tracking-wider truncate hover:bg-slate-100 transition-colors"
                                                                        >
                                                                            <span className="font-black" style={{ color: tO?.color || '#64748b' }}>#</span>{tag}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                {activeFilters.includes('Trash') ? (
                                                                    <div className="flex gap-2"><button onClick={(e) => handleRestoreFromTrash(e, b.id)} className="text-green-500 hover:text-green-600"><LucideIcon name="rotate-ccw" /></button><button onClick={(e) => handlePermanentDelete(e, b.id)} className="text-red-500 hover:text-red-700"><LucideIcon name="trash-2" size={18} /></button></div>
                                                                ) : (
                                                                    <button onClick={(e) => handleMoveToTrash(e, b.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600 transition-all p-1"><LucideIcon name="trash-2" size={18} className="text-[13px]" /></button>
                                                                )}
                                                                <a href={b.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-7 h-7 flex items-center justify-center bg-white border border-slate-200 shadow-sm text-slate-400 hover:text-black rounded-lg transition-all"><LucideIcon name="external-link" size={12} /></a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {showBrandLines && brandLineStyle === 'bar' && (
                                                        <div className="absolute bottom-0 left-0 right-0 h-[6px]" style={{ backgroundColor: b.url && b.url.includes('reddit.com') ? '#ff4500' : '#1da1f2' }}></div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {filteredBookmarks.length > visibleCount && <div ref={observerTarget} className="h-10 w-full" />}
                            {filteredBookmarks.length === 0 && <div className="flex flex-col items-center justify-center py-20 opacity-30"><LucideIcon name="layers" size={18} className="text-4xl mb-4" /><p className="text-sm font-bold uppercase tracking-widest">No Content Found</p></div>}
                        </>
                    )}
                </div>
            </main>

            {/* EDIT (FOCUS) MODAL */}
            {
                focusedTweet && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-2 sm:p-4 md:p-8 overflow-y-auto" onClick={() => { setFocusedTweet(null); setIsNoteEditing(false); }}>
                        <div className="bg-white w-full max-w-5xl rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden modal-enter flex flex-col md:flex-row h-fit max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                            <div className="flex-1 bg-slate-100 p-4 sm:p-8 md:p-12 lg:p-16 overflow-y-auto custom-scrollbar flex items-start justify-center min-h-[200px] sm:min-h-[400px]">
                                <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-5">
                                    <CustomTweetCard
                                        bookmark={focusedTweet}
                                        onImageClick={(medias, idx, type, poster, tweetId, isReddit) => setPreviewState({ medias, currentIndex: idx, mediaType: type || focusedTweet.mediaType, poster, tweetId, isReddit })}
                                        showFullRedditContent={true}
                                    />
                                </div>
                            </div>
                            <div className="w-full md:w-[350px] p-5 sm:p-8 border-l border-slate-100 flex flex-col justify-between bg-white overflow-y-auto custom-scrollbar relative">
                                <div onClick={() => { if (activeAddMenu) setActiveAddMenu(null); }}>
                                    <div className="flex justify-between items-center mb-4 border-b border-slate-50 pb-3">
                                        <div>
                                            {focusedTweet.date && <span className="text-[11px] text-slate-400 font-medium flex items-center"><LucideIcon name="calendar" className="mr-1.5" size={12} />{formatDate(focusedTweet.date)}</span>}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {initialFocusedTweet && JSON.stringify({ ...initialFocusedTweet, date: null, timestamp: null }) !== JSON.stringify({ ...focusedTweet, date: null, timestamp: null }) && (
                                                <button onClick={() => {
                                                    setFocusedTweet(initialFocusedTweet);
                                                    setBookmarks(prev => prev.map(b => b.id === initialFocusedTweet.id ? initialFocusedTweet : b));
                                                }} className="w-8 h-8 flex items-center justify-center hover:bg-orange-50 text-orange-400 rounded-full transition-all" title="Revert Changes"><LucideIcon name="rotate-ccw" className="text-[16px]" /></button>
                                            )}
                                            <button onClick={() => { setFocusedTweet(null); setIsNoteEditing(false); }} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all text-slate-400"><LucideIcon name="x" className="text-xl" /></button>
                                        </div>
                                    </div>

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Your Note</h3>
                                    {isNoteEditing ? (
                                        <textarea
                                            autoFocus
                                            value={focusedTweet.description || ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                const updated = { ...focusedTweet, description: val };
                                                setFocusedTweet(updated);
                                                setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
                                            }}
                                            onBlur={() => setIsNoteEditing(false)}
                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none resize-none focus:ring-1 focus:ring-blue-400 mb-5"
                                            rows="4"
                                            placeholder="Empty note..."
                                        />
                                    ) : (
                                        <div onClick={(e) => { e.stopPropagation(); setIsNoteEditing(true); }} className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-5 cursor-text hover:bg-slate-100 hover:border-slate-200 transition-colors min-h-[80px]">
                                            <p className="text-slate-800 font-medium leading-relaxed break-words whitespace-pre-wrap">{focusedTweet.description || <span className="text-slate-400 italic">No note added. Click to write...</span>}</p>
                                        </div>
                                    )}

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Folder</h3>
                                    <div className="flex flex-wrap gap-2 mb-5 relative">
                                        {!isUnsortedFolder(focusedTweet.folder) ? (
                                            <div className="group flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold cursor-pointer hover:bg-red-50 hover:text-red-500 transition-all border border-transparent hover:border-red-200" onClick={() => {
                                                const updated = { ...focusedTweet, folder: null };
                                                setFocusedTweet(updated);
                                                setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
                                            }}>
                                                <LucideIcon name="folder" size={14} style={{ color: customFolders.find(f => f.name === normalizeFolder(focusedTweet.folder))?.color || '#94a3b8' }} />
                                                <span>{normalizeFolder(focusedTweet.folder)}</span>
                                                <LucideIcon name="x" className="ml-1 opacity-0 group-hover:opacity-100 w-3 h-3" />
                                            </div>
                                        ) : (
                                            <div className="relative dropdown-container">
                                                <button onClick={(e) => { e.stopPropagation(); setActiveAddMenu(activeAddMenu === 'folder' ? null : 'folder'); }} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all"><LucideIcon name="plus" size={14} /></button>
                                                {activeAddMenu === 'folder' && (
                                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-2 max-h-48 overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-1 mt-1">Select Folder</div>
                                                        {customFolders.length > 0 ? [...customFolders].sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => (
                                                            <div key={f.name} onClick={(e) => {
                                                                e.stopPropagation();
                                                                const updated = { ...focusedTweet, folder: f.name };
                                                                setFocusedTweet(updated);
                                                                setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
                                                                setActiveAddMenu(null);
                                                            }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm font-medium text-slate-700">
                                                                <LucideIcon name="folder" size={14} style={{ color: f.color }} />
                                                                {f.name}
                                                            </div>
                                                        )) : <div className="px-3 py-2 text-xs text-slate-400 italic">No custom folders.</div>}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Tags</h3>
                                    <div className="flex flex-wrap gap-2 mb-5 relative dropdown-container">
                                        {(focusedTweet.tags || []).length > 0 && (focusedTweet.tags || []).map(tag => (
                                            <div key={tag} className="group flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold cursor-pointer hover:bg-red-50 hover:text-red-500 transition-all border border-transparent hover:border-red-200" onClick={() => {
                                                const newTags = (focusedTweet.tags || []).filter(t => t !== tag);
                                                const updated = { ...focusedTweet, tags: newTags };
                                                setFocusedTweet(updated);
                                                setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
                                            }}>
                                                <span className="font-black" style={{ color: customTags.find(t => t.name === tag)?.color || '#64748b' }}>#</span>
                                                <span className="uppercase tracking-wider">{tag}</span>
                                                <LucideIcon name="x" className="ml-1 opacity-0 group-hover:opacity-100 w-3 h-3" />
                                            </div>
                                        ))}
                                        <div className="relative dropdown-container">
                                            <button onClick={(e) => { e.stopPropagation(); setActiveAddMenu(activeAddMenu === 'tag' ? null : 'tag'); }} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all"><LucideIcon name="plus" size={14} /></button>
                                            {activeAddMenu === 'tag' && (
                                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-2 max-h-48 overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-1 mt-1 flex justify-between items-center">
                                                        Select Tag
                                                    </div>
                                                    {customTags.filter(t => !(focusedTweet.tags || []).includes(t.name)).length > 0 ? customTags.filter(t => !(focusedTweet.tags || []).includes(t.name)).map(t => (
                                                        <div key={t.id} onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newTags = [...(focusedTweet.tags || []), t.name];
                                                            const updated = { ...focusedTweet, tags: newTags };
                                                            setFocusedTweet(updated);
                                                            setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
                                                            setActiveAddMenu(null);
                                                        }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm font-medium text-slate-700">
                                                            <span className="font-black" style={{ color: t.color }}>#</span>
                                                            <span className="uppercase tracking-wider">{t.name}</span>
                                                        </div>
                                                    )) : <div className="px-3 py-2 text-xs text-slate-400 italic">No more tags</div>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="pt-5 border-t border-slate-50 flex items-center gap-3 mt-auto">
                                    <button
                                        onClick={(e) => { handleMoveToTrash(e, focusedTweet.id); setFocusedTweet(null); }}
                                        className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-all active:scale-95 shrink-0"
                                        title="Move to Trash"
                                    >
                                        <LucideIcon name="trash-2" size={17} />
                                    </button>
                                    <a href={focusedTweet.url} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-xl text-xs font-bold shadow-lg hover:bg-slate-800 transition-all active:scale-95">
                                        {focusedTweet.url && focusedTweet.url.includes('reddit.com') ? 'OPEN ON REDDIT' : 'OPEN ON X'} <LucideIcon name="external-link" size={14} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                focusedWebsite && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-2 sm:p-4 md:p-8 overflow-y-auto" onClick={() => { setFocusedWebsite(null); setIsWebsiteNoteEditing(false); }}>
                        <div className="bg-white w-full max-w-4xl rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden modal-enter flex flex-col md:flex-row h-fit max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                            <div className="flex-1 bg-slate-50 p-6 sm:p-8 md:p-10 overflow-y-auto custom-scrollbar">
                                <div className="mx-auto max-w-2xl rounded-[1.6rem] border border-slate-200 bg-white p-6 shadow-sm">
                                    <div className="mb-5 flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">{focusedWebsite.domain}</p>
                                            <h3 className="mt-2 break-words text-2xl font-black text-slate-900">{focusedWebsite.title}</h3>
                                        </div>
                                        <WebsiteFavicon site={focusedWebsite} className="h-12 w-12 shrink-0 rounded-2xl border border-slate-200 bg-white shadow-sm" iconSize={20} />
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">
                                        {focusedWebsite.description || 'No note added yet.'}
                                    </div>
                                </div>
                            </div>
                            <div className="w-full md:w-[360px] p-5 sm:p-8 border-l border-slate-100 flex flex-col justify-between bg-white overflow-y-auto custom-scrollbar relative">
                                <div onClick={() => { if (activeAddMenu) setActiveAddMenu(null); }}>
                                    <div className="flex justify-between items-center mb-4 border-b border-slate-50 pb-3">
                                        <div>
                                            <span className="text-[11px] text-slate-400 font-medium flex items-center"><LucideIcon name="calendar" className="mr-1.5" size={12} />{formatDate(focusedWebsite.savedAt)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {initialFocusedWebsite && JSON.stringify(initialFocusedWebsite) !== JSON.stringify(focusedWebsite) && (
                                                <button onClick={() => {
                                                    setFocusedWebsite(initialFocusedWebsite);
                                                    setWebsiteBookmarks(prev => prev.map(site => site.id === initialFocusedWebsite.id ? initialFocusedWebsite : site));
                                                }} className="w-8 h-8 flex items-center justify-center hover:bg-orange-50 text-orange-400 rounded-full transition-all" title="Revert Changes"><LucideIcon name="rotate-ccw" size={16} /></button>
                                            )}
                                            <button onClick={() => { setFocusedWebsite(null); setIsWebsiteNoteEditing(false); }} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all text-slate-400"><LucideIcon name="x" className="text-xl" /></button>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Title</label>
                                        <input value={focusedWebsite.title || ''} onChange={e => {
                                            const updated = { ...focusedWebsite, title: e.target.value };
                                            setFocusedWebsite(updated);
                                            setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                        }} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-1 focus:ring-blue-400" />
                                    </div>

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Your Note</h3>
                                    {isWebsiteNoteEditing ? (
                                        <textarea
                                            autoFocus
                                            value={focusedWebsite.description || ''}
                                            onChange={e => {
                                                const updated = { ...focusedWebsite, description: e.target.value };
                                                setFocusedWebsite(updated);
                                                setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                            }}
                                            onBlur={() => setIsWebsiteNoteEditing(false)}
                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none resize-none focus:ring-1 focus:ring-blue-400 mb-5"
                                            rows="4"
                                            placeholder="Add a website note..."
                                        />
                                    ) : (
                                        <div onClick={() => setIsWebsiteNoteEditing(true)} className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-5 cursor-text hover:bg-slate-100 hover:border-slate-200 transition-colors min-h-[80px]">
                                            <p className="text-slate-800 font-medium leading-relaxed break-words whitespace-pre-wrap">{focusedWebsite.description || <span className="text-slate-400 italic">No note added. Click to write...</span>}</p>
                                        </div>
                                    )}

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Collection</h3>
                                    <div className="flex flex-wrap gap-2 mb-5 relative">
                                        {!isUnsortedFolder(focusedWebsite.folder) ? (
                                            <div className="group flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold cursor-pointer hover:bg-red-50 hover:text-red-500 transition-all border border-transparent hover:border-red-200" onClick={() => {
                                                const updated = { ...focusedWebsite, folder: null };
                                                setFocusedWebsite(updated);
                                                setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                            }}>
                                                <LucideIcon name="folder" size={14} style={{ color: websiteFolders.find(f => f.name === normalizeFolder(focusedWebsite.folder))?.color || '#94a3b8' }} />
                                                <span>{normalizeFolder(focusedWebsite.folder)}</span>
                                                <LucideIcon name="x" className="ml-1 opacity-0 group-hover:opacity-100 w-3 h-3" />
                                            </div>
                                        ) : (
                                            <div className="relative dropdown-container">
                                                <button onClick={(e) => { e.stopPropagation(); setActiveAddMenu(activeAddMenu === 'website-folder' ? null : 'website-folder'); }} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all"><LucideIcon name="plus" size={14} /></button>
                                                {activeAddMenu === 'website-folder' && (
                                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-2 max-h-48 overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-1 mt-1">Select Collection</div>
                                                        {websiteFolders.length > 0 ? [...websiteFolders].sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => (
                                                            <div key={f.id} onClick={() => {
                                                                const updated = { ...focusedWebsite, folder: f.name };
                                                                setFocusedWebsite(updated);
                                                                setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                                                setActiveAddMenu(null);
                                                            }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm font-medium text-slate-700">
                                                                <LucideIcon name="folder" size={14} style={{ color: f.color }} />
                                                                {f.name}
                                                            </div>
                                                        )) : <div className="px-3 py-2 text-xs text-slate-400 italic">No collections yet.</div>}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Tags</h3>
                                    <div className="flex flex-wrap gap-2 mb-5 relative dropdown-container">
                                        {(focusedWebsite.tags || []).map(tag => (
                                            <div key={tag} className="group flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold cursor-pointer hover:bg-red-50 hover:text-red-500 transition-all border border-transparent hover:border-red-200" onClick={() => {
                                                const updated = { ...focusedWebsite, tags: (focusedWebsite.tags || []).filter(t => t !== tag) };
                                                setFocusedWebsite(updated);
                                                setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                            }}>
                                                <span className="font-black" style={{ color: websiteTags.find(t => t.name === tag)?.color || '#64748b' }}>#</span>
                                                <span className="uppercase tracking-wider">{tag}</span>
                                                <LucideIcon name="x" className="ml-1 opacity-0 group-hover:opacity-100 w-3 h-3" />
                                            </div>
                                        ))}
                                        <div className="relative dropdown-container">
                                            <button onClick={(e) => { e.stopPropagation(); setActiveAddMenu(activeAddMenu === 'website-tag' ? null : 'website-tag'); }} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all"><LucideIcon name="plus" size={14} /></button>
                                            {activeAddMenu === 'website-tag' && (
                                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-2 max-h-48 overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase px-2 mb-1 mt-1">Select Tag</div>
                                                    {websiteTags.filter(t => !(focusedWebsite.tags || []).includes(t.name)).length > 0 ? websiteTags.filter(t => !(focusedWebsite.tags || []).includes(t.name)).map(t => (
                                                        <div key={t.id} onClick={() => {
                                                            const updated = { ...focusedWebsite, tags: [...(focusedWebsite.tags || []), t.name] };
                                                            setFocusedWebsite(updated);
                                                            setWebsiteBookmarks(prev => prev.map(site => site.id === updated.id ? updated : site));
                                                            setActiveAddMenu(null);
                                                        }} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm font-medium text-slate-700">
                                                            <span className="font-black" style={{ color: t.color }}>#</span>
                                                            <span className="uppercase tracking-wider">{t.name}</span>
                                                        </div>
                                                    )) : <div className="px-3 py-2 text-xs text-slate-400 italic">No more tags</div>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="pt-5 border-t border-slate-50 flex items-center gap-3 mt-auto">
                                    <button onClick={(e) => { handleMoveWebsiteToTrash(e, focusedWebsite.id); setFocusedWebsite(null); }} className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-all active:scale-95 shrink-0"><LucideIcon name="trash-2" size={17} /></button>
                                    <a href={focusedWebsite.url} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-xl text-xs font-bold shadow-lg hover:bg-slate-800 transition-all active:scale-95">
                                        OPEN WEBSITE <LucideIcon name="external-link" size={14} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* TAG MODAL */}
            {
                isTagModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4" onClick={() => setIsTagModalOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl modal-enter" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center"><h3 className="font-bold text-slate-900">{editingTag ? 'Edit Tag' : 'New Tag'}</h3><button onClick={() => setIsTagModalOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all"><LucideIcon name="x" className="text-slate-400" /></button></div>
                            <form onSubmit={handleSaveTag} className="p-6 space-y-4">
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Tag Name</label><input type="text" required value={tagNameInput} onChange={e => setTagNameInput(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none transition-all" /></div>
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Pick a Color</label><div className="flex items-center gap-3"><input type="color" value={tagColorInput} onChange={e => setTagColorInput(e.target.value)} className="w-12 h-12 p-1 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer shadow-sm" /> <span className="text-sm font-medium text-slate-600 uppercase font-mono">{tagColorInput}</span></div></div>
                                <div className="flex gap-2 pt-4"><button type="submit" className="flex-1 bg-green-600 text-white py-3.5 rounded-xl font-bold text-xs shadow-md shadow-green-600/20 hover:bg-green-700 transition-all active:scale-95">SAVE</button>{editingTag && <button type="button" onClick={() => { if (window.confirm("Delete this tag?")) { if (activeLibrary === 'websites') { setWebsiteTags(prev => prev.filter(t => t.id !== editingTag.id)); setWebsiteBookmarks(prev => prev.map(b => ({ ...b, tags: (b.tags || []).filter(tag => tag !== editingTag.name) }))); } else { setCustomTags(prev => prev.filter(t => t.id !== editingTag.id)); setBookmarks(prev => prev.map(b => ({ ...b, tags: (b.tags || []).filter(tag => tag !== editingTag.name) }))); } setIsTagModalOpen(false); setEditingTag(null); } }} className="flex-1 bg-red-50 text-red-500 py-3.5 rounded-xl font-bold text-xs hover:bg-red-100 transition-all active:scale-95">DELETE</button>}</div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ADD MODAL */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsModalOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl modal-enter" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center"><h3 className="font-bold text-slate-900">Add New Bookmark</h3><button onClick={() => setIsModalOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all"><LucideIcon name="x" className="text-slate-400" /></button></div>
                            <form onSubmit={handleAddBookmark} className="p-6 space-y-4">
                                <input type="url" required placeholder="Tweet URL (https://x.com/...)" value={newUrl} onChange={e => setNewUrl(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none transition-all" />
                                <div className="grid grid-cols-2 gap-4">
                                    <CustomDropdown value={newFolder} onChange={setNewFolder} options={[{ name: 'Unsorted', color: '#94a3b8' }, ...customFolders]} isMulti={false} />
                                    <CustomDropdown value={newTags} onChange={setNewTags} options={customTags} isMulti={true} />
                                </div>
                                <textarea placeholder="Your Note..." rows="3" value={newDesc} onChange={e => setNewDesc(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none resize-none transition-all" ></textarea>
                                <button type="submit" className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-sm shadow-md shadow-green-600/20 hover:bg-green-700 transition-all active:scale-95">Add to Folder</button>
                            </form>
                        </div>
                    </div>
                )
            }

            {
                isWebsiteModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsWebsiteModalOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl modal-enter" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center"><h3 className="font-bold text-slate-900">Add Website Bookmark</h3><button onClick={() => setIsWebsiteModalOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all"><LucideIcon name="x" className="text-slate-400" /></button></div>
                            <form onSubmit={handleAddWebsiteBookmark} className="p-6 space-y-4">
                                <input type="url" required placeholder="Website URL (https://example.com)" value={newWebsiteUrl} onChange={e => { const value = e.target.value; setNewWebsiteUrl(value); if (!newWebsiteTitle.trim()) setNewWebsiteTitle(getWebsiteTitleFromUrl(value)); }} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none transition-all" />
                                <input type="text" maxLength="160" placeholder="Title" value={newWebsiteTitle} onChange={e => setNewWebsiteTitle(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none transition-all" />
                                <div className="grid grid-cols-2 gap-4">
                                    <CustomDropdown value={newWebsiteFolder} onChange={setNewWebsiteFolder} options={[{ name: 'Unsorted', color: '#94a3b8' }, ...websiteFolders]} isMulti={false} />
                                    <CustomDropdown value={newWebsiteTags} onChange={setNewWebsiteTags} options={websiteTags} isMulti={true} />
                                </div>
                                <textarea placeholder="Why did you save this site?" rows="3" value={newWebsiteDesc} onChange={e => setNewWebsiteDesc(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none resize-none transition-all"></textarea>
                                <button type="submit" className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-sm shadow-md shadow-green-600/20 hover:bg-green-700 transition-all active:scale-95">Save Website</button>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* PREVIEW MODAL */}
            {
                previewState && (
                    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-md z-[200] flex items-center justify-center p-4 cursor-zoom-out modal-enter" onClick={() => setPreviewState(null)}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowRight' && previewState.medias.length > 1) { e.stopPropagation(); setPreviewState(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.medias.length })); }
                            if (e.key === 'ArrowLeft' && previewState.medias.length > 1) { e.stopPropagation(); setPreviewState(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.medias.length) % prev.medias.length })); }
                            if (e.key === 'Escape') setPreviewState(null);
                        }} tabIndex={0} ref={(el) => el && el.focus()}
                    >
                        {/* Left Arrow */}
                        {previewState.medias.length > 1 && (
                            <button onClick={(e) => { e.stopPropagation(); setPreviewState(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.medias.length) % prev.medias.length })); }} className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full transition-all backdrop-blur-sm z-10"><LucideIcon name="chevron-left" className="text-lg" /></button>
                        )}
                        {/* Media */}
                        {(previewState.mediaType === 'video' && isPlayableVideoUrl(previewState.medias[previewState.currentIndex])) ? (
                            <HlsVideoPlayer
                                src={previewState.medias[previewState.currentIndex]}
                                poster={previewState.poster}
                                controls
                                autoPlay
                                className="max-w-full max-h-[90vh] rounded-lg shadow-2xl outline-none"
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : (previewState.mediaType === 'video' && previewState.tweetId && !String(previewState.tweetId).includes('_')) ? (
                            <div className="w-full max-w-2xl bg-white rounded-2xl p-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                                <div className="max-h-[80vh] overflow-y-auto custom-scrollbar">
                                    {previewState.isReddit ? <RedditEmbed url={previewState.medias[0] || ""} /> : <TweetEmbed tweetId={previewState.tweetId} />}
                                </div>
                            </div>
                        ) : (
                            <TwitterMediaImage
                                src={previewState.medias[previewState.currentIndex]}
                                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                        {/* Right Arrow */}
                        {previewState.medias.length > 1 && (
                            <button onClick={(e) => { e.stopPropagation(); setPreviewState(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.medias.length })); }} className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full transition-all backdrop-blur-sm z-10"><LucideIcon name="chevron-right" className="text-lg" /></button>
                        )}
                        {/* Counter */}
                        {previewState.medias.length > 1 && (
                            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full">{previewState.currentIndex + 1} / {previewState.medias.length}</div>
                        )}
                        {/* Top bar buttons */}
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(getMediaDisplayUrl(previewState.medias[previewState.currentIndex])); }} className="absolute top-6 right-20 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/30 text-white rounded-full transition-colors"><LucideIcon name="download" /></button>
                        <button onClick={() => setPreviewState(null)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/30 text-white rounded-full transition-colors"><LucideIcon name="x" className="text-xl" /></button>
                    </div>
                )
            }

            {/* FOLDER MODAL */}
            {
                isFolderModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4" onClick={() => setIsFolderModalOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl modal-enter" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center"><h3 className="font-bold text-slate-900">{editingFolder ? 'Edit Folder' : 'New Folder'}</h3><button onClick={() => setIsFolderModalOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all"><LucideIcon name="x" className="text-slate-400" /></button></div>
                            <form onSubmit={handleSaveFolder} className="p-6 space-y-4">
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Name</label><input type="text" required value={folderNameInput} onChange={e => setFolderNameInput(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:bg-white outline-none transition-all" /></div>
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Color</label><div className="flex items-center gap-3"><input type="color" value={folderColorInput} onChange={e => setFolderColorInput(e.target.value)} className="w-12 h-12 p-1 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer shadow-sm" /> <span className="text-sm font-medium uppercase">{folderColorInput}</span></div></div>
                                <div className="flex gap-2 pt-4"><button type="submit" className="flex-1 bg-green-600 text-white py-3.5 rounded-xl font-bold text-xs shadow-md shadow-green-600/20 hover:bg-green-700 transition-all active:scale-95">SAVE</button>{editingFolder && <button type="button" onClick={() => { if (window.confirm("Delete this folder?")) { if (activeLibrary === 'websites') { setWebsiteFolders(prev => prev.filter(f => f.id !== editingFolder.id)); setWebsiteBookmarks(prev => prev.map(b => b.folder === editingFolder.name ? { ...b, folder: null } : b)); } else { setCustomFolders(prev => prev.filter(f => f.id !== editingFolder.id)); setBookmarks(prev => prev.map(b => b.folder === editingFolder.name ? { ...b, folder: null } : b)); } setIsFolderModalOpen(false); setEditingFolder(null); } }} className="flex-1 bg-red-50 text-red-500 py-3.5 rounded-xl font-bold text-xs hover:bg-red-100 transition-all active:scale-95">DELETE</button>}</div>
                            </form>
                        </div>
                    </div>
                )
            }

            {
                isSettingsOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4" onClick={() => setIsSettingsOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl modal-enter" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center"><h3 className="font-bold text-slate-900 text-lg">Settings</h3><button onClick={() => setIsSettingsOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all"><LucideIcon name="x" className="text-slate-400" /></button></div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Accent Color</label>
                                        {customAccentColors.length > 2 && (
                                            <button
                                                onClick={() => setIsEditingColors(!isEditingColors)}
                                                className={`text-[10px] font-bold uppercase transition-colors px-2 py-1 rounded-md ${isEditingColors ? 'bg-blue-100 text-blue-600' : 'text-blue-500 hover:bg-slate-50'}`}
                                            >
                                                {isEditingColors ? 'Bitti' : 'Düzenle'}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {customAccentColors.map((colorObj, i) => (
                                            <div key={colorObj.id} className="relative group/col">
                                                {isEditingColors && i > 1 ? (
                                                    <div className={`relative w-9 h-9 rounded-xl transition-all shadow-sm border-2 border-white ring-2 ring-blue-200 overflow-hidden ${colorEditingIndex === i ? 'scale-110 ring-blue-400' : 'opacity-60'}`}>
                                                        <div className="w-full h-full pointer-events-none" style={{ backgroundColor: colorObj.color }}></div>
                                                        <input
                                                            type="color"
                                                            defaultValue={colorObj.color}
                                                            className="absolute -inset-4 w-[200%] h-[200%] opacity-0 cursor-pointer color-picker-input"
                                                            onFocus={() => setColorEditingIndex(i)}
                                                            onInput={(e) => {
                                                                // Sadece görseli anlık güncelleyelim, state'e dokunmayalım
                                                                e.target.previousSibling.style.backgroundColor = e.target.value;
                                                            }}
                                                            onChange={(e) => {
                                                                // OnChange (seçim bitince) ana listeyi güncelleyelim
                                                                const newCols = [...customAccentColors];
                                                                newCols[i].color = e.target.value;
                                                                setCustomAccentColors(newCols);
                                                                localStorage.setItem('customAccentColors', JSON.stringify(newCols));
                                                            }}
                                                            onBlur={(e) => {
                                                                setAccentColor(e.target.value);
                                                                setIsEditingColors(false);
                                                                setColorEditingIndex(null);
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <button onClick={() => setAccentColor(colorObj.color)} className={`w-9 h-9 rounded-xl transition-all shadow-sm hover:scale-110 ${accentColor === colorObj.color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''}`} style={{ backgroundColor: colorObj.color }}></button>
                                                )}

                                                {isEditingColors && i > 1 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newCols = customAccentColors.filter((_, idx) => idx !== i);
                                                            setCustomAccentColors(newCols);
                                                            localStorage.setItem('customAccentColors', JSON.stringify(newCols));
                                                            if (accentColor === colorObj.color) setAccentColor(newCols[0]?.color || '#000000');
                                                            if (newCols.length <= 2) setIsEditingColors(false);
                                                        }}
                                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex justify-center items-center shadow-sm transition-all scale-100 z-10 hover:bg-red-600"
                                                    >
                                                        <LucideIcon name="x" size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {customAccentColors.length < 9 && (
                                            <button
                                                onClick={() => {
                                                    const newColorObj = {
                                                        id: Date.now(),
                                                        color: '#3b82f6'
                                                    };

                                                    setCustomAccentColors(prev => [...prev, newColorObj]);
                                                    setAccentColor(newColorObj.color);
                                                    setIsEditingColors(true);
                                                    setColorEditingIndex(customAccentColors.length);
                                                    setTimeout(() => {
                                                        const inputs = document.querySelectorAll('.color-picker-input');
                                                        if (inputs.length > 0) {
                                                            inputs[inputs.length - 1].click();
                                                        }
                                                    }, 50);
                                                }}
                                                className="relative w-9 h-9 flex items-center justify-center rounded-xl transition-all shadow-sm hover:bg-slate-200 bg-slate-100 cursor-pointer text-slate-500 overflow-hidden"
                                            >
                                                <LucideIcon name="plus" size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-slate-50">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">Display Theme</label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { id: 'light', name: 'Light Mode', bg: '#ffffff', text: '#000000', border: '#f1f5f9' },
                                            { id: 'oldschool', name: 'Old School', bg: '#15202b', text: '#ffffff', border: '#243447' },
                                            { id: 'dark', name: 'Classic Dark', bg: '#000000', text: '#ffffff', border: '#2f3336' },
                                        ].map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => setTheme(t.id)}
                                                className={`relative overflow-hidden group p-3 rounded-2xl border-2 transition-all flex flex-col gap-2 items-center ${theme === t.id ? 'border-blue-500 bg-blue-50/10' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}
                                            >
                                                <div className="w-full h-8 rounded-lg shadow-inner flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: t.bg, color: t.text, border: `1px solid ${t.border}` }}>
                                                    Aa
                                                </div>
                                                <span className={`text-[11px] font-bold ${theme === t.id ? 'text-blue-600' : 'text-slate-500'}`}>{t.name}</span>
                                                {theme === t.id && (
                                                    <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                                                        <LucideIcon name="check" className="text-[8px] text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="pt-6 border-t border-slate-50">
                                    <div className="flex justify-between items-center mb-4">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Storage Usage</label>
                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${(storageInfo.used / (1024 * 1024)) < 80 ? 'bg-green-100 text-green-600' : (storageInfo.used / (1024 * 1024)) < 90 ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'}`}>
                                            {(storageInfo.used / (1024 * 1024)) < 80 ? 'OPTIMIZED' : (storageInfo.used / (1024 * 1024)) < 90 ? 'HEAVY' : 'CRITICAL'}
                                        </span>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                                        <div className="flex justify-between items-end mb-2">
                                            <span className="text-xs font-bold text-slate-700">Archive Size</span>
                                            <span className="text-[10px] font-bold text-slate-400">
                                                {(storageInfo.used / (1024 * 1024)).toFixed(2)} MB / 100 MB
                                            </span>
                                        </div>
                                        <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-700 ${(storageInfo.used / (1024 * 1024)) < 80 ? 'bg-green-500' : (storageInfo.used / (1024 * 1024)) < 90 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                style={{ width: `${Math.min((storageInfo.used / (1024 * 1024 * 100)) * 100, 100) || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-slate-50 font-sans">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Card Brand Styling</label>
                                    <div className="flex gap-2 mb-6">
                                        <button
                                            onClick={() => { setShowBrandLines(false); }}
                                            className={`flex-1 py-2.5 px-3 rounded-xl text-[11px] font-bold transition-all border ${!showBrandLines ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100'}`}
                                        >
                                            Off
                                        </button>
                                        <button
                                            onClick={() => { setShowBrandLines(true); setBrandLineStyle('bar'); }}
                                            className={`flex-1 py-2.5 px-3 rounded-xl text-[11px] font-bold transition-all border ${showBrandLines && brandLineStyle === 'bar' ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100'}`}
                                        >
                                            Underline
                                        </button>
                                        <button
                                            onClick={() => { setShowBrandLines(true); setBrandLineStyle('border'); }}
                                            className={`flex-1 py-2.5 px-3 rounded-xl text-[11px] font-bold transition-all border ${showBrandLines && brandLineStyle === 'border' ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100'}`}
                                        >
                                            Outline
                                        </button>
                                    </div>

                                    <p className="mb-6 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-medium text-slate-500">Sidebar tags use rounded boxes in both libraries.</p>

                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Automatic Backup</label>
                                        <div
                                            onClick={() => setAutoBackup(!autoBackup)}
                                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-all duration-300 ${autoBackup ? 'bg-green-500' : 'bg-slate-200'}`}
                                        >
                                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-300 ${autoBackup ? 'left-5.5' : 'left-0.5'}`} style={{ left: autoBackup ? '22px' : '2px' }}></div>
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic pr-8 mb-6">
                                        Automatically creates a JSON backup file once a day when you open the app.
                                    </p>

                                    <div className="space-y-2 mt-4">
                                        <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 cursor-pointer group transition-all" onClick={() => handleExportJSON()}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 transition-colors">Manual Export</span>
                                                <span className="text-[10px] font-medium text-slate-400">Download a JSON backup manually.</span>
                                            </div>
                                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 group-hover:bg-blue-100 text-slate-400 group-hover:text-blue-500 transition-all">
                                                <LucideIcon name="download" size={14} />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 cursor-pointer group transition-all" onClick={() => document.getElementById('restore-input').click()}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 transition-colors">Restore Archive</span>
                                                <span className="text-[10px] font-medium text-slate-400">Load a JSON backup to restore data.</span>
                                                <input id="restore-input" type="file" accept=".json" onChange={handleImportJSON} className="hidden" />
                                            </div>
                                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 group-hover:bg-blue-100 text-slate-400 group-hover:text-blue-500 transition-all">
                                                <LucideIcon name="upload" size={14} />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between p-3 rounded-xl border border-red-50 hover:border-red-100 hover:bg-red-50 cursor-pointer group transition-all" onClick={() => {
                                            setIsSettingsOpen(false);
                                            setIsWipeModalOpen(true);
                                        }}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-red-500 group-hover:text-red-600 transition-colors">Wipe Database</span>
                                                <span className="text-[10px] font-medium text-red-400/80">Clear all archived data permanently.</span>
                                            </div>
                                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 group-hover:bg-red-200 text-red-400 group-hover:text-red-600 transition-all">
                                                <LucideIcon name="trash-2" size={14} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                isWipeModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 modal-enter" onClick={() => setIsWipeModalOpen(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl text-center" onClick={e => e.stopPropagation()}>
                            <div className="p-8">
                                <div className="w-16 h-16 bg-red-50 border-4 border-white shadow-sm text-red-500 rounded-full flex items-center justify-center mx-auto mb-5">
                                    <LucideIcon name="alert-triangle" size={28} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 mb-3 tracking-tight">Bütün Veritabanı Silinecek!</h3>
                                <p className="text-[13px] text-slate-500 mb-8 px-2 leading-relaxed">Bu işlem geri alınamaz. Arşivlenen tüm içerikleriniz kalıcı olarak gidecek. Onaylıyor musunuz?</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setIsWipeModalOpen(false)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95">İptal</button>
                                    <button onClick={() => {
                                        setIsWipeModalOpen(false);
                                        db.bookmarks.clear();
                                        db.folders.clear();
                                        db.tags.clear();
                                        db.trash.clear();
                                        db.websiteBookmarks.clear();
                                        db.websiteFolders.clear();
                                        db.websiteTags.clear();
                                        db.websiteTrash.clear();
                                        window.location.reload();
                                    }} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-red-500/20 transition-all active:scale-95">Evet, Sil</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* TOAST NOTIFICATIONS */}
            {toasts.length > 0 && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-[300] flex flex-col gap-2.5 pointer-events-none w-[calc(100%-2rem)] sm:w-auto">
                    {toasts.map(toast => (
                        <div key={toast.id} className="pointer-events-auto flex items-center gap-3 px-5 py-3.5 bg-white border border-slate-200 rounded-2xl shadow-xl text-sm font-semibold text-slate-700 animate-slide-in-right min-w-[280px] max-w-[400px]">
                            <i className={`text-xs ${toast.type === 'success' ? 'fa-solid fa-check-circle text-green-500' : toast.type === 'error' ? 'fa-solid fa-exclamation-circle text-red-500' : 'fa-solid fa-info-circle text-blue-500'}`}></i>
                            <span className="flex-1">{toast.message}</span>
                            {toast.undoAction && (
                                <button onClick={() => { toast.undoAction(); dismissToast(toast.id); }} className="text-xs font-bold text-blue-600 hover:text-blue-700 uppercase tracking-wider shrink-0 px-2 py-1 hover:bg-blue-50 rounded-lg transition-all">Undo</button>
                            )}
                            <button onClick={() => dismissToast(toast.id)} className="text-slate-300 hover:text-slate-500 transition-colors ml-1"><LucideIcon name="x" className="text-xs" /></button>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
}
export default App;
